import { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { supabase } from "./supabaseClient";
import EventCardPreview from "./components/shared/EventCardPreview";
import ManagerLineup from "./components/manager/ManagerLineup";

const getToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const isToday = (d) => d === getToday();
const isTomorrow = (d) => {
  const t = new Date();
  t.setDate(t.getDate() + 1);
  return d === `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
};
// Shared date badge styles — applied across all calendar views
const todayBadge    = { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 22, height: 22, borderRadius: "50%", background: "#1e1e1e", color: "#fff", fontWeight: 700, fontSize: 13 };
const tomorrowBadge = { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 22, height: 22, borderRadius: "50%", border: "1.5px solid rgba(249,115,22,0.55)", color: "rgba(249,115,22,0.85)", fontSize: 13 };

const CACHE_KEYS = { profile: "superdome_profile", tomorrow: "superdome_tomorrow" };
const readCache = (key) => { try { return JSON.parse(localStorage.getItem(key)); } catch { return null; } };
const writeCache = (key, val) => { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} };
const clearCache = (key) => { try { localStorage.removeItem(key); } catch {} };

const fmt = (t) => {
  if (!t) return "";
  const parts = t.split(":");
  const hr = parseInt(parts[0]);
  const m = parts[1];
  return `${hr > 12 ? hr - 12 : hr === 0 ? 12 : hr}:${m} ${hr >= 12 ? "PM" : "AM"}`;
};

const fmtDate = (d) =>
  new Date(d + "T00:00:00").toLocaleDateString("en-US", {
    weekday: "short", month: "short", day: "numeric",
  });

const timesOverlap = (s1, e1, s2, e2) => s1 < e2 && e1 > s2;

const fmtShortDate = (d) =>
  new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });

const fmtDateRange = (start, end) => {
  const s = fmtShortDate(start);
  const e = end && end !== start ? fmtShortDate(end) : null;
  return e ? `${s}–${e}` : s;
};

const fmtShort = (t) => {
  if (!t) return "";
  const parts = t.split(":");
  const hr = parseInt(parts[0]);
  const m = parts[1];
  const suffix = hr >= 12 ? "p" : "a";
  const h = hr > 12 ? hr - 12 : hr === 0 ? 12 : hr;
  return m === "00" ? `${h}${suffix}` : `${h}:${m}${suffix}`;
};


// Formats a name. Managers see full names; employees see "First L." abbreviation.
// Accepts (firstName, lastName, role) or a single full-name string as firstName.
const formatName = (firstName, lastName, role) => {
  const first = firstName ? String(firstName).trim() : "";
  const last  = lastName  ? String(lastName).trim()  : "";
  if (first && last) {
    return role === "manager" ? `${first} ${last}` : `${first} ${last.charAt(0)}.`;
  }
  if (first) {
    const parts = first.split(/\s+/);
    if (parts.length >= 2) {
      return role === "manager" ? first : `${parts[0]} ${parts[parts.length - 1].charAt(0)}.`;
    }
    return first;
  }
  return last;
};

const CHIP_COLORS = ["#6366f1","#22c55e","#a855f7","#3b82f6","#f59e0b","#ec4899","#14b8a6","#84cc16","#06b6d4","#8b5cf6","#10b981","#e879f9"];
const employeeColor = (id) => {
  if (!id) return CHIP_COLORS[0];
  let h = 0;
  for (let i = 0; i < id.length; i++) h = Math.imul(31, h) + id.charCodeAt(i) | 0;
  return CHIP_COLORS[Math.abs(h) % CHIP_COLORS.length];
};

const inputStyle = {
  width: "100%", background: "#0a0a0a", border: "1px solid #2a2a2a", borderRadius: 8,
  padding: "11px 14px", color: "#fff", fontSize: 14, marginBottom: 10,
  boxSizing: "border-box", outline: "none", display: "block",
};

const shiftCard = {
  background: "#111", border: "1px solid #1e1e1e", borderRadius: 10,
  padding: "14px 16px", marginBottom: 10, display: "flex",
  alignItems: "center", justifyContent: "space-between", gap: 12,
};

const TimePicker = ({ value, onChange }) => {
  const parse = (v) => {
    if (!v) return { h: "", m: "00", ampm: "AM" };
    const [hStr, mStr] = v.split(":");
    const h24 = parseInt(hStr, 10);
    return {
      h: String(h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24),
      m: mStr || "00",
      ampm: h24 >= 12 ? "PM" : "AM",
    };
  };
  const { h, m, ampm } = parse(value);

  const emit = (newH, newM, newAmpm) => {
    if (!newH) { onChange(""); return; }
    let h24 = parseInt(newH, 10);
    if (newAmpm === "AM") { if (h24 === 12) h24 = 0; }
    else { if (h24 !== 12) h24 += 12; }
    onChange(`${String(h24).padStart(2, "0")}:${newM}`);
  };

  const sel = {
    background: "#0a0a0a", border: "1px solid #2a2a2a", borderRadius: 8,
    padding: "11px 6px", color: h ? "#fff" : "#555", fontSize: 14,
    cursor: "pointer", outline: "none", flex: 1, minWidth: 0,
    appearance: "none", WebkitAppearance: "none", textAlign: "center",
  };

  return (
    <div style={{ display: "flex", gap: 6, flex: 1 }}>
      <select value={h} onChange={e => emit(e.target.value, m, ampm)} style={sel}>
        <option value="">hr</option>
        {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => (
          <option key={n} value={String(n)}>{n}</option>
        ))}
      </select>
      <select value={m} onChange={e => emit(h, e.target.value, ampm)} style={sel}>
        {["00","15","30","45"].map(min => (
          <option key={min} value={min}>:{min}</option>
        ))}
      </select>
      <button
        type="button"
        onClick={() => emit(h, m, ampm === "AM" ? "PM" : "AM")}
        style={{
          background: "none", border: "1px solid #2a2a2a", borderRadius: 8,
          padding: "11px 12px", fontSize: 13, fontWeight: 700, cursor: "pointer",
          flexShrink: 0, color: !h ? "#444" : ampm === "AM" ? "#f97316" : "#aaa",
        }}
      >{ampm}</button>
    </div>
  );
};

// ── My Team: role options ─────────────────────────────────────────────────────

const TEAM_ROLE_OPTIONS = [
  { label: "Lead",      badge: "L", badgeStyle: { background: "#F55C00", border: "2.5px solid #111",    color: "white"   } },
  { label: "Assistant", badge: "A", badgeStyle: { background: "white",   border: "2.5px solid #111",    color: "#111"    } },
];

function currentRoleLabel(p) {
  if (p.role === "manager") return "Manager";
  if (p.staff_type === "lead" || p.staff_type === "head") return "Lead";
  return "Assistant";
}

function StaffRow({ p, currentLabel, badgeLetter, badgeStyle, sectionLabel, teamMenuOpen, setTeamMenuOpen, setRemoveStaffTarget, onChangeRole, onEditLocations, onEditName, readOnly, isMe }) {
  const effectiveReadOnly = readOnly || isMe;
  const displayName = p.first_name && p.last_name ? `${p.first_name} ${p.last_name}` : p.name || "Unnamed";
  return (
    <div style={{ borderBottom: "1px solid #eee" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: "50%", fontWeight: 700, fontSize: 12, flexShrink: 0, ...badgeStyle }}>{badgeLetter}</span>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontWeight: 600, color: "#111", fontSize: 15 }}>{displayName}</span>
              {isMe && <span style={{ fontSize: 10, fontWeight: 700, color: "#F55C00", letterSpacing: 0.5, fontFamily: "'Oswald', sans-serif" }}>YOU</span>}
            </div>
          </div>
        </div>
        {!effectiveReadOnly && (
          <div style={{ position: "relative" }}>
            <button
              type="button"
              onClick={() => setTeamMenuOpen(teamMenuOpen === p.id ? null : p.id)}
              style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#888", padding: "4px 8px", fontFamily: "inherit", lineHeight: 1 }}
            >···</button>
            {teamMenuOpen === p.id && (
              <div
                style={{ position: "absolute", right: 0, top: "100%", background: "white", border: "1px solid #EFEFEF", borderRadius: 10, boxShadow: "0 4px 16px rgba(0,0,0,0.1)", zIndex: 100, minWidth: 160, overflow: "hidden" }}
                onMouseLeave={() => setTeamMenuOpen(null)}
              >
                {p.role !== "manager" && (
                  <>
                    <button
                      type="button"
                      onClick={() => { setTeamMenuOpen(null); onEditName(); }}
                      style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 16px", background: "none", border: "none", fontSize: 13, fontWeight: 500, color: "#111", cursor: "pointer", fontFamily: "inherit", borderBottom: "0.5px solid #EFEFEF" }}
                    >Edit Name</button>
                    <button
                      type="button"
                      onClick={() => { setTeamMenuOpen(null); onEditLocations(); }}
                      style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 16px", background: "none", border: "none", fontSize: 13, fontWeight: 500, color: "#111", cursor: "pointer", fontFamily: "inherit", borderBottom: "0.5px solid #EFEFEF" }}
                    >Edit Locations</button>
                    <button
                      type="button"
                      onClick={() => { setTeamMenuOpen(null); onChangeRole(p, "Lead"); }}
                      style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 16px", background: "none", border: "none", fontSize: 13, fontWeight: 500, color: "#111", cursor: "pointer", fontFamily: "inherit", borderBottom: "0.5px solid #EFEFEF" }}
                    >Make Lead</button>
                    <button
                      type="button"
                      onClick={() => { setTeamMenuOpen(null); onChangeRole(p, "Assistant"); }}
                      style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 16px", background: "none", border: "none", fontSize: 13, fontWeight: 500, color: "#111", cursor: "pointer", fontFamily: "inherit", borderBottom: "0.5px solid #EFEFEF" }}
                    >Make Assistant</button>
                    <button
                      type="button"
                      onClick={() => { setTeamMenuOpen(null); setRemoveStaffTarget(p); }}
                      style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 16px", background: "none", border: "none", fontSize: 13, fontWeight: 700, color: "#E53935", cursor: "pointer", fontFamily: "inherit" }}
                    >Remove</button>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export default function App() {
  const [user, setUser] = useState(() => {
    const cached = readCache(CACHE_KEYS.profile);
    return (cached && cached.active === false) ? null : cached;
  });
  const [loading, setLoading] = useState(() => !readCache(CACHE_KEYS.profile));
  const [profileConfirmed, setProfileConfirmed] = useState(() => !readCache(CACHE_KEYS.profile));
  const [dataLoaded, setDataLoaded] = useState(false);
  const [tomorrowCache, setTomorrowCache] = useState(() => readCache(CACHE_KEYS.tomorrow));
  const [shifts, setShifts] = useState([]);
  const [requests, setRequests] = useState([]);
  const [openCovers, setOpenCovers] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [allProfiles, setAllProfiles] = useState([]);
  const [pendingProfiles, setPendingProfiles] = useState([]);
  const [view, setView] = useState("today");
  const [homeDay, setHomeDay] = useState("tomorrow");
  const [homeLocFilter, setHomeLocFilter] = useState("");
  const [loginData, setLoginData] = useState({ email: "", password: "", error: "", submitting: false });
  const [inviteCode, setInviteCode] = useState({ active: false, email: "", code: "", error: "", submitting: false });
  const [orgSetup, setOrgSetup] = useState({ step: 1, email: "", code: "", businessName: "", firstName: "", lastName: "", phone: "", phoneError: "", password: "", confirmPassword: "", error: "", submitting: false, locations: [""], orgId: null });
  const [sendSetupPage, setSendSetupPage] = useState({ email: "", firstName: "", businessName: "", ownerName: "", phone: "", submitting: false, error: "", success: false, successEmail: "", successName: "" });
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showSetupPassword, setShowSetupPassword] = useState(false);
  const [showSetupConfirm, setShowSetupConfirm] = useState(false);
  const [showAnnouncementsPanel, setShowAnnouncementsPanel] = useState(false);
  const [lastSeenAnnouncementId, setLastSeenAnnouncementId] = useState(() => { try { return localStorage.getItem("superdome_last_seen_ann") || null; } catch { return null; } });
  const [showAddShift, setShowAddShift] = useState(false);
  const [lastShiftLocation, setLastShiftLocation] = useState(() => { try { return localStorage.getItem("superdome_last_location") || null; } catch { return null; } });
  const [newShift, setNewShift] = useState({ employeeId: "", date: "", start: "09:00", end: "17:00", note: "", location: "" });
  const [allAvailability, setAllAvailability] = useState([]);
  const [selectedEmpAvailability, setSelectedEmpAvailability] = useState([]);
  const [selectedDates, setSelectedDates] = useState(new Set());
  const [pickerMonth, setPickerMonth] = useState({ year: new Date().getFullYear(), month: new Date().getMonth() });
  const [locationFilter, setLocationFilter] = useState("All");
  const [swapRequest, setSwapRequest] = useState({ targetShiftId: "", myShiftId: "", reason: "" });
  const [swapSubmitting, setSwapSubmitting] = useState(false);
  const [timeOffSubmitting, setTimeOffSubmitting] = useState(false);
  const [coverConfirmError, setCoverConfirmError] = useState("");
  const [updatingStars, setUpdatingStars] = useState(new Set());
  const [teamOpenRow, setTeamOpenRow] = useState(null); // profile id of open dropdown
  const [teamPendingRole, setTeamPendingRole] = useState({}); // { [id]: "Manager"|"Lead"|"Assistant" }
  const [removeStaffTarget, setRemoveStaffTarget] = useState(null);
  const [timeOffRequest, setTimeOffRequest] = useState({ startDate: "", endDate: "", reason: "" });
  const [showSwap, setShowSwap] = useState(false);
  const [showTimeOff, setShowTimeOff] = useState(false);
  const [confirmSwap, setConfirmSwap] = useState(false);
  const [confirmTimeOff, setConfirmTimeOff] = useState(false);
  const [timeOffReasonError, setTimeOffReasonError] = useState(false);
  const [timeOffDateError, setTimeOffDateError] = useState("");
  const [cancelConfirmId, setCancelConfirmId] = useState(null);
  const [cancelConfirmRequest, setCancelConfirmRequest] = useState(null);
  const [scheduleMode, setScheduleMode] = useState("mine");
  const [toast, setToast] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [alertDialog, setAlertDialog] = useState(null);
  const [newAnnouncement, setNewAnnouncement] = useState("");
  const [pinNew, setPinNew] = useState(false);
  const [addShiftWarning, setAddShiftWarning] = useState("");
  const [editingShiftId, setEditingShiftId] = useState(null);
  const [calDate, setCalDate] = useState({ year: new Date().getFullYear(), month: new Date().getMonth() });
  const [selectedDay, setSelectedDay] = useState(null);
  const [teamCalMonth, setTeamCalMonth] = useState({ year: new Date().getFullYear(), month: new Date().getMonth() });
  const [myShiftsLocationFilter, setMyShiftsLocationFilter] = useState("All");
  const [empTeamLocationFilter, setEmpTeamLocationFilter] = useState("All");
  const [events, setEvents] = useState([]);
  const [eventStaff, setEventStaff] = useState([]);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [newEvent, setNewEvent] = useState({ type: "", typeCustom: "", title: "", date: "", start_time: "", end_time: "", location: "", notes: "", is_recurring: false, recurring_end_date: "", is_informational: false });
  const [addEventWarning, setAddEventWarning] = useState("");
  const [selectedStaffIds, setSelectedStaffIds] = useState(new Set());
  const [selectedEventDates, setSelectedEventDates] = useState(new Set());
  const [eventPickerMonth, setEventPickerMonth] = useState({ year: new Date().getFullYear(), month: new Date().getMonth() });
  const [eventTypeFilter, setEventTypeFilter] = useState("All");
  const [scheduleSubview, setScheduleSubview] = useState("week"); // "week" | "calendar"
  const [shiftSaving, setShiftSaving] = useState(null); // null | "saving" | "saved"
  const [processingId, setProcessingId] = useState(null);
  const [timeOffConflictModal, setTimeOffConflictModal] = useState(null);
  const [showApproved, setShowApproved] = useState(false);
  const [pinningId, setPinningId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [pullY, setPullY] = useState(0);
  const [passwordSetup, setPasswordSetup] = useState({ active: false, password: "", confirm: "", firstName: "", lastName: "", email: "", phone: "", phoneError: "", error: "", submitting: false });
  const [inviteSheet, setInviteSheet] = useState({ open: false, visible: false, firstName: "", lastName: "", email: "", role: "Assistant", locations: user?.locations?.length > 0 ? [user.locations[0]] : [], submitting: false, error: "", adminInvite: false });
  const [locationEditSheet, setLocationEditSheet] = useState({ open: false, visible: false, profileId: null, profileName: "", locations: [] });
  const [allLocations, setAllLocations] = useState([]);
  const defaultLoc = allLocations[0]?.name || "";
  const [editNameSheet, setEditNameSheet] = useState({ open: false, visible: false, profileId: null, firstName: "", lastName: "" });
  const [inviteToast, setInviteToast] = useState("");
  const inviteToastTimer = useRef(null);
  const [empDrawerOpen, setEmpDrawerOpen] = useState(false);
  const [empDrawerVisible, setEmpDrawerVisible] = useState(false);
  const [editPhoneState, setEditPhoneState] = useState({ editing: false, value: "", error: "", saving: false });
  // Employee action sheets
  const [empSheet, setEmpSheet] = useState({ type: null, visible: false }); // type: "availability"|"timeoff"|"cover"|"late"
  const DEFAULT_AVAIL_ROWS = [0,1,2,3,4,5,6].map(dow => ({ day_of_week: dow, is_available: false, start_time: "09:00:00", end_time: "22:00:00" }));
  const [empAvailRows, setEmpAvailRows] = useState(DEFAULT_AVAIL_ROWS);
  const [mgrExpanded, setMgrExpanded] = useState(false);
  const [empCalSheet, setEmpCalSheet] = useState(false);
  const [empAvailSaving, setEmpAvailSaving] = useState(false);
  const [empTimeOff, setEmpTimeOff] = useState({ startDate: "", endDate: "", reason: "", error: "", submitting: false });
  const [empCover, setEmpCover] = useState({ shiftId: null, reason: "", error: "", submitting: false });
  const [empLate, setEmpLate] = useState({ arrivalTime: "", note: "", error: "", submitting: false });
  const [empStripDayCount, setEmpStripDayCount] = useState(60);
  const empStripScrollRef = useRef(null);
  const [empStripTodayVisible, setEmpStripTodayVisible] = useState(true);
  const [empSelectedDate, setEmpSelectedDate] = useState(getToday());
  const [forgotPassword, setForgotPassword] = useState({ active: false, email: "", submitting: false, sent: false, error: "" });
  const [passwordReset, setPasswordReset] = useState({ active: false, password: "", confirm: "", error: "", submitting: false, success: false });
  const [resetCodeStep, setResetCodeStep] = useState({ active: false, email: "", code: "", error: "", submitting: false });
  const updatingStarsRef = useRef(updatingStars);
  updatingStarsRef.current = updatingStars;
  const passwordSetupRequired = useRef(false);
  const verifyingCode = useRef(false);
  const verifyingResetCode = useRef(false);
  const touchStartY = useRef(0);
  const pulling = useRef(false);
  const pullYRef = useRef(0);
  const [coverRequestShift, setCoverRequestShift] = useState(null); // shift object | null
  const [coverNote, setCoverNote] = useState("");
  const [coverConfirmShiftId, setCoverConfirmShiftId] = useState(null);
  const [requestedCoverIds, setRequestedCoverIds] = useState(new Set());
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date(getToday() + "T00:00:00");
    d.setDate(d.getDate() - d.getDay());
    return d.toISOString().split("T")[0];
  });
  const [empWeekAnchor, setEmpWeekAnchor] = useState(() => {
    const d = new Date(getToday() + "T00:00:00");
    d.setDate(d.getDate() - 1);
    return d.toISOString().split("T")[0];
  });
  const showToast = useCallback((msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const loadData = useCallback(async () => {
    const orgId = user?.org_id;
    console.log("[loadData] orgId for locations query:", orgId);
    const [s, r, oc, a, e, p, ev, es, av, loc] = await Promise.all([
      supabase.from("shifts").select("*").eq("org_id", orgId).order("date").order("start_time"),
      supabase.from("requests").select("*").eq("org_id", orgId).order("created_at", { ascending: false }),
      supabase.from("requests").select("*").eq("org_id", orgId).eq("type", "cover").eq("status", "pending").gte("shift_date", getToday()).order("created_at", { ascending: false }),
      supabase.from("announcements").select("*").eq("org_id", orgId).order("created_at", { ascending: false }),
      supabase.from("profiles").select("*").eq("org_id", orgId).eq("role", "employee").eq("active", true),
      supabase.from("profiles").select("*").eq("org_id", orgId).eq("active", true).order("name"),
      supabase.from("events").select("*").eq("org_id", orgId).order("date").order("start_time"),
      supabase.from("event_staff").select("*").eq("org_id", orgId),
      supabase.from("availability").select("*").eq("org_id", orgId),
      supabase.from("locations").select("*").eq("org_id", orgId).order("name"),
    ]);
    if (s.data) setShifts(s.data);
    if (r.data) setRequests(r.data);
    if (oc.data) setOpenCovers(oc.data);
    if (a.data) setAnnouncements(a.data);
    if (e.data) setEmployees(e.data);
    if (p.data) {
      const inFlight = updatingStarsRef.current;
      if (inFlight.size === 0) {
        setAllProfiles([...p.data].sort((a, b) => (b.cit_stars || 0) - (a.cit_stars || 0)));
      } else {
        setAllProfiles(prev => {
          const localStars = new Map(prev.filter(x => inFlight.has(x.id)).map(x => [x.id, x.cit_stars]));
          return [...p.data].sort((a, b) => (b.cit_stars || 0) - (a.cit_stars || 0)).map(prof => localStars.has(prof.id) ? { ...prof, cit_stars: localStars.get(prof.id) } : prof);
        });
      }
    }
    if (ev.data) setEvents(ev.data);
    if (es.data) setEventStaff(es.data);
    if (av.data) setAllAvailability(av.data);
    if (loc.data) setAllLocations(loc.data);
    setDataLoaded(true);
    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
    const { data: pendingData } = await supabase
      .from("profiles")
      .select("*")
      .eq("org_id", orgId)
      .eq("active", false)
      .eq("removed", false)
      .gte("created_at", sevenDaysAgo);
    if (pendingData) setPendingProfiles(pendingData);
  }, [user?.org_id]);

  const formatPhoneDisplay = (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 10);
    if (digits.length <= 3) return digits.length ? `(${digits}` : "";
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };
  const phoneDigitsOnly = (v) => v.replace(/\D/g, "").slice(0, 10);

  const submitPasswordSetup = async () => {
    const { password, confirm, firstName, lastName, phone } = passwordSetup;
    if (!firstName.trim() || !lastName.trim() || !password || !confirm) {
      setPasswordSetup(p => ({ ...p, error: "Please fill in all fields." }));
      return;
    }
    const phoneDigits = phone.replace(/\D/g, "");
    if (phoneDigits.length > 0 && phoneDigits.length !== 10) {
      setPasswordSetup(p => ({ ...p, phoneError: "Enter a valid 10-digit phone number" }));
      return;
    }
    if (password !== confirm) {
      setPasswordSetup(p => ({ ...p, error: "Passwords don't match." }));
      return;
    }
    if (password.length < 8) {
      setPasswordSetup(p => ({ ...p, error: "Password must be at least 8 characters." }));
      return;
    }
    setPasswordSetup(p => ({ ...p, submitting: true, error: "", phoneError: "" }));
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      setPasswordSetup(p => ({ ...p, submitting: false, error: error.message }));
      return;
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      const fName = firstName.trim();
      const lName = lastName.trim();
      const fullName = `${fName} ${lName}`;
      const profileUpdate = { first_name: fName, last_name: lName, name: fullName, active: true };
      if (phoneDigits.length === 10) profileUpdate.phone = `+1${phoneDigits}`;
      await supabase.from("profiles").update(profileUpdate).eq("id", session.user.id);
    }

    passwordSetupRequired.current = false;
    localStorage.setItem("tmrw_signup_success", "1");
    window.history.replaceState(null, "", window.location.pathname);
    await supabase.auth.signOut();
    window.location.replace("/");
  };

  const openInviteSheet = (mode) => setInviteSheet({ open: true, visible: false, firstName: "", lastName: "", email: "", role: mode === "admin" ? "Manager" : "Assistant", locations: mode === "admin" ? allLocations.map(l => l.name) : (user?.is_admin === true ? allLocations.map(l => l.name) : (user?.locations?.length > 0 ? [user.locations[0]] : [])), submitting: false, error: "", adminInvite: mode === "admin" });

  const openLocationEdit = (p) => {
    setLocationEditSheet({ open: true, visible: false, profileId: p.id, profileName: p.first_name ? `${p.first_name} ${p.last_name}` : p.name, locations: p.locations || [p.location || defaultLoc] });
    setTimeout(() => setLocationEditSheet(s => ({ ...s, visible: true })), 10);
  };
  const closeLocationEdit = () => {
    setLocationEditSheet(s => ({ ...s, visible: false }));
    setTimeout(() => setLocationEditSheet(s => ({ ...s, open: false })), 300);
  };
  const saveLocationEdit = async () => {
    const { profileId, locations } = locationEditSheet;
    if (!locations || locations.length === 0) return;
    const { error } = await supabase.from("profiles").update({
      locations: locations,
      location: locations[0],
    }).eq("id", profileId);
    if (error) {
      showToast("Error updating locations.", "error");
      return;
    }
    closeLocationEdit();
    loadData();
    showToast("Locations updated.");
  };
  const closeInviteSheet = () => {
    setInviteSheet(s => ({ ...s, visible: false }));
    setTimeout(() => setInviteSheet(s => ({ ...s, open: false, adminInvite: false })), 300);
  };
  useEffect(() => {
    if (inviteSheet.open) setTimeout(() => setInviteSheet(s => ({ ...s, visible: true })), 10);
  }, [inviteSheet.open]);

  const resendInvite = async (profile) => {
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    try {
      const res = await fetch(`${supabaseUrl}/functions/v1/invite-staff`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}` },
        body: JSON.stringify({
          email: profile.email,
          first_name: profile.first_name || profile.name?.split(" ")[0] || "Staff",
          last_name: profile.last_name || profile.name?.split(" ").slice(1).join(" ") || "",
          role: profile.role === "manager" ? "Manager" : profile.staff_type === "lead" ? "Lead" : "Assistant",
          locations: profile.locations || [profile.location],
          org_id: user.org_id,
        }),
      });
      const json = await res.json();
      if (!res.ok || json.error) {
        showToast(json.error || "Failed to resend invite.", "error");
        return;
      }
      const name = [profile.first_name, profile.last_name].filter(Boolean).join(" ") || profile.name || profile.email;
      clearTimeout(inviteToastTimer.current);
      setInviteToast(`Invite resent to ${name}.`);
      inviteToastTimer.current = setTimeout(() => setInviteToast(""), 3000);
      loadData();
    } catch (err) {
      showToast("Network error. Please try again.", "error");
    }
  };

  const changeRole = async (p, label) => {
    const newRole = label === "Manager" ? "manager" : "employee";
    const newStaffType = label === "Manager" ? "manager" : label === "Lead" ? "lead" : "assistant";
    const previousProfiles = allProfiles;
    setAllProfiles(prev => prev.map(x => x.id === p.id ? { ...x, role: newRole, staff_type: newStaffType } : x));
    const { error } = await supabase.from("profiles").update({ role: newRole, staff_type: newStaffType }).eq("id", p.id);
    if (error) {
      setAllProfiles(previousProfiles);
      clearTimeout(inviteToastTimer.current);
      setInviteToast("Failed to update role. Please try again.");
      inviteToastTimer.current = setTimeout(() => setInviteToast(""), 4000);
    }
  };

  const submitInvite = async () => {
    const { firstName, lastName, email, role, locations, adminInvite } = inviteSheet;
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !role) {
      setInviteSheet(s => ({ ...s, error: "Please fill in all fields." }));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setInviteSheet(s => ({ ...s, error: "Please enter a valid email address." }));
      return;
    }
    if (!adminInvite && (!locations || locations.length === 0)) {
      setInviteSheet(s => ({ ...s, error: "Please select at least one location." }));
      return;
    }
    setInviteSheet(s => ({ ...s, submitting: true, error: "" }));
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    try {
      const invitePayload = {
        email: email.trim(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        role: adminInvite ? "Manager" : role,
        locations: adminInvite ? allLocations.map(l => l.name) : locations,
        is_admin: inviteSheet.adminInvite === true,
        org_id: user.org_id,
      };
      console.log("[submitInvite] payload:", JSON.stringify(invitePayload));
      const res = await fetch(`${supabaseUrl}/functions/v1/invite-staff`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}` },
        body: JSON.stringify(invitePayload),
      });
      const rawText = await res.text();
      console.log("[invite-staff] status:", res.status, "body:", rawText);
      let json;
      try { json = JSON.parse(rawText); } catch { json = {}; }
      if (!res.ok || json.error) {
        setInviteSheet(s => ({ ...s, submitting: false, error: json.error || "Something went wrong." }));
        return;
      }
      closeInviteSheet();
      clearTimeout(inviteToastTimer.current);
      setInviteToast(`Invite sent to ${email.trim()}`);
      inviteToastTimer.current = setTimeout(() => setInviteToast(""), 4000);
      // Optimistically add to pending list immediately
      const effectiveRole = adminInvite ? "Manager" : role;
      const dbRole = effectiveRole === "Manager" ? "manager" : "employee";
      const dbStaffType = effectiveRole === "Manager" ? "manager" : effectiveRole === "Lead" ? "lead" : "assistant";
      const optimisticProfile = {
        id: json.userId || `pending-${Date.now()}`,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        name: `${firstName.trim()} ${lastName.trim()}`,
        email: email.trim(),
        role: dbRole,
        staff_type: dbStaffType,
        locations: adminInvite ? allLocations.map(l => l.name) : locations,
        active: false,
        removed: false,
      };
      setPendingProfiles(prev => [optimisticProfile, ...prev.filter(p => p.email !== email.trim())]);
      loadData();
    } catch (err) {
      setInviteSheet(s => ({ ...s, submitting: false, error: err.message || "Network error." }));
    }
  };

  const submitForgotPassword = async () => {
    if (!forgotPassword.email.trim()) return;
    setForgotPassword(p => ({ ...p, submitting: true, error: "" }));
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    try {
      const res = await fetch(`${supabaseUrl}/functions/v1/send-password-reset-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}` },
        body: JSON.stringify({ email: forgotPassword.email.trim().toLowerCase() }),
      });
      const json = await res.json();
      if (!res.ok || json.error) {
        setForgotPassword(p => ({ ...p, submitting: false, error: json.error || "Failed to send reset code. Please try again." }));
        return;
      }
      setResetCodeStep(p => ({ ...p, email: forgotPassword.email.trim().toLowerCase() }));
      setForgotPassword(p => ({ ...p, submitting: false, sent: true }));
    } catch (err) {
      setForgotPassword(p => ({ ...p, submitting: false, error: "Network error. Please try again." }));
    }
  };

  const submitPasswordReset = async () => {
    const { password, confirm } = passwordReset;
    if (!password || !confirm) {
      setPasswordReset(p => ({ ...p, error: "Please fill in both fields." }));
      return;
    }
    if (password !== confirm) {
      setPasswordReset(p => ({ ...p, error: "Passwords don't match." }));
      return;
    }
    if (password.length < 8) {
      setPasswordReset(p => ({ ...p, error: "Password must be at least 8 characters." }));
      return;
    }
    setPasswordReset(p => ({ ...p, submitting: true, error: "" }));
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      setPasswordReset(p => ({ ...p, submitting: false, error: error.message }));
      return;
    }
    passwordSetupRequired.current = false;
    setPasswordReset({ active: false, password: "", confirm: "", error: "", submitting: false, success: false });
    setLoginData(p => ({ ...p, error: "", successMsg: "Password updated. Please sign in." }));
    window.history.replaceState(null, "", window.location.pathname);
    await supabase.auth.signOut();
  };

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await loadData();
    } finally {
      setRefreshing(false);
    }
  }, [loadData]);

  useEffect(() => {
    let mounted = true;

    // Synchronous recovery-URL detection — must happen before any await so we read the URL
    // before the Supabase SDK has a chance to clear the hash fragment after token exchange.
    // Covers both implicit flow (#type=recovery in hash) and PKCE flow (type=recovery in search).
    const _initHash = window.location.hash;
    const _initSearch = window.location.search;
    console.log("[Auth] useEffect start — hash:", _initHash || "(empty)", "| search:", _initSearch || "(empty)");
    if ((_initHash.includes("type=recovery") || _initSearch.includes("type=recovery")) && !_initSearch.includes("invite=true")) {
      passwordSetupRequired.current = true;
      setPasswordReset(p => ({ ...p, active: true }));
      setLoading(false);
      console.log("[Auth] Synchronous recovery URL detected — passwordSetupRequired set to true");
    } else {
      console.log("[Auth] No recovery token in URL — proceeding with normal session restore");
    }

    const _resetParam = new URLSearchParams(_initSearch).get("reset");
    const _resetEmail = new URLSearchParams(_initSearch).get("email");
    if (_resetParam === "true" && _resetEmail) {
      window.history.replaceState(null, "", window.location.pathname);
      setForgotPassword(p => ({ ...p, active: true, sent: true, email: _resetEmail.toLowerCase() }));
      setResetCodeStep(p => ({ ...p, email: _resetEmail.toLowerCase() }));
      setLoading(false);
    }

    const _inviteToken = new URLSearchParams(_initSearch).get("token");
    const _inviteType = new URLSearchParams(_initSearch).get("type");
    const _isInvite = !!_inviteToken && _inviteType === "recovery" && _initSearch.includes("invite=true");

    if (_isInvite) {
      passwordSetupRequired.current = true;
      window.history.replaceState(null, "", window.location.pathname);
      setLoading(false);
      supabase.auth.verifyOtp({ token_hash: _inviteToken, type: "recovery" }).then(({ error }) => {
        if (error) {
          passwordSetupRequired.current = false;
          setLoginData(p => ({ ...p, error: "This invite link has expired or already been used. Please contact your manager." }));
        } else {
          setPasswordSetup(p => ({ ...p, active: true }));
        }
      });
    }

    // Safety net — never stay stuck on loading screen
    const timeout = setTimeout(() => {
      if (mounted) { setLoading(false); setProfileConfirmed(true); }
    }, 5000);

    const hasCachedProfile = !!readCache(CACHE_KEYS.profile);
    // Blocks onAuthStateChange from acting until init() has finished its own session check.
    // Supabase can fire SIGNED_OUT spuriously while getSession() is still resolving —
    // gating on this flag prevents that from clearing the cached user prematurely.
    let initDone = false;

    const init = async () => {
      console.log("[Auth] init() start — passwordSetupRequired.current:", passwordSetupRequired.current);
      try {
        // If a recovery URL was detected synchronously above, skip all session restoration.
        if (passwordSetupRequired.current) {
          console.log("[Auth] init() — recovery flag set, skipping getSession()");
          initDone = true;
          clearTimeout(timeout);
          return;
        }

        // Detect invite links before anything else
        if (!_isInvite) {
          const hash = window.location.hash;
          if (hash.includes("type=invite")) {
            passwordSetupRequired.current = true;
            if (mounted) { setPasswordSetup(p => ({ ...p, active: true })); setLoading(false); }
            initDone = true;
            clearTimeout(timeout);
            return;
          }
        }

        console.log("[Auth] init() — calling getSession()");
        const { data: { session } } = await supabase.auth.getSession();
        console.log("[Auth] init() — getSession() resolved, session user:", session?.user?.id ?? "none", "| passwordSetupRequired.current:", passwordSetupRequired.current);

        if (session?.user && !passwordSetupRequired.current) {
          console.log("[Auth] init() — fetching profile for", session.user.id);
          const { data: profile, error: profileError } = await supabase
            .from("profiles").select("*").eq("id", session.user.id).single();
          console.log("[Auth] init() — profile fetch done, profile:", profile?.id ?? "null", "| passwordSetupRequired.current:", passwordSetupRequired.current);
          // Re-check after await — PASSWORD_RECOVERY may have fired during the fetch
          if (profile && !profileError && mounted && !passwordSetupRequired.current) {
            if (profile.active === false) {
              if (profile.removed === true) {
                await supabase.auth.signOut();
                clearCache(CACHE_KEYS.profile);
                if (mounted) {
                  setUser(null);
                  setLoading(false);
                  setLoginData(p => ({ ...p, error: "Your account has been deactivated. Contact your manager.", submitting: false }));
                }
                return;
              }
              // Not removed — new invite pending password setup
              passwordSetupRequired.current = true;
              if (mounted) {
                setUser(null);
                setLoading(false);
                setPasswordSetup(p => ({ ...p, active: true }));
              }
              initDone = true;
              return;
            }
            console.log("[Auth] init() — calling setUser (suppression check passed)");
            setUser(profile);
            setProfileConfirmed(true);
            writeCache(CACHE_KEYS.profile, profile);
            if (updatingStarsRef.current.size === 0) {
              loadData(); // fire and forget — data loads in background
            }
          } else {
            console.log("[Auth] init() — setUser SUPPRESSED — profile:", !!profile, "| profileError:", !!profileError, "| mounted:", mounted, "| passwordSetupRequired:", passwordSetupRequired.current);
          }
        } else if (!session?.user && hasCachedProfile) {
          // Session genuinely gone — clear cache and show login
          clearCache(CACHE_KEYS.profile);
          clearCache(CACHE_KEYS.tomorrow);
          if (mounted) { setUser(null); setProfileConfirmed(true); }
        }
      } catch (e) {
        console.error("Auth init error:", e);
      }
      initDone = true;
      console.log("[Auth] init() complete — initDone = true");
      if (mounted) setProfileConfirmed(true);
      if (mounted && !hasCachedProfile) {
        clearTimeout(timeout);
        setLoading(false);
      }
    };

    init();

    // Only handles post-init auth events: real logins and real sign-outs.
    // PASSWORD_RECOVERY is always handled immediately (before initDone) so PKCE recovery
    // links are intercepted even if the code exchange completes before init() finishes.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log("[Auth] onAuthStateChange — event:", event, "| initDone:", initDone, "| passwordSetupRequired.current:", passwordSetupRequired.current, "| session user:", session?.user?.id ?? "none");
        if (event === "PASSWORD_RECOVERY") {
          // User clicked a password-reset link — show password reset form immediately,
          // clearing any user state that init() may have set before this event fired.
          passwordSetupRequired.current = true;
          console.log("[Auth] PASSWORD_RECOVERY handled — setting passwordSetupRequired=true, clearing user, showing reset form");
          if (mounted) { setUser(null); setPasswordReset(p => ({ ...p, active: true })); setLoading(false); }
          return;
        }
        if (!initDone) {
          console.log("[Auth] onAuthStateChange — ignoring event (initDone=false):", event);
          return;
        }
        if (event === "SIGNED_IN" && session?.user) {
          // Skip profile load if we're waiting for the user to set their password
          if (passwordSetupRequired.current) {
            console.log("[Auth] SIGNED_IN — SUPPRESSED (passwordSetupRequired=true)");
            return;
          }
          console.log("[Auth] SIGNED_IN — fetching profile for", session.user.id);
          try {
            const fetchProfile = supabase
              .from("profiles").select("*").eq("id", session.user.id).single();
            const fetchTimeout = new Promise((_, reject) =>
              setTimeout(() => reject(new Error("Profile fetch timed out after 8s")), 8000)
            );
            const { data: profile, error: profileError } = await Promise.race([fetchProfile, fetchTimeout]);
            console.log("[Auth] SIGNED_IN — profile fetch done, profile:", profile?.id ?? "null", "| passwordSetupRequired.current:", passwordSetupRequired.current);
            // Re-check after await — PASSWORD_RECOVERY may have fired during the fetch
            if (profile && !profileError && mounted && !passwordSetupRequired.current) {
              if (profile.active === false) {
                if (profile.removed === true) {
                  await supabase.auth.signOut();
                  clearCache(CACHE_KEYS.profile);
                  if (mounted) {
                    setUser(null);
                    setLoading(false);
                    setLoginData(p => ({ ...p, error: "Your account has been deactivated. Contact your manager.", submitting: false }));
                  }
                  return;
                }
                // Not removed — new invite pending password setup
                passwordSetupRequired.current = true;
                if (mounted) {
                  setUser(null);
                  setLoading(false);
                  setPasswordSetup(p => ({ ...p, active: true }));
                }
                return;
              }
              console.log("[Auth] SIGNED_IN — calling setUser (suppression check passed)");
              // Reset all data state before loading new user's data
              setDataLoaded(false);
              setShifts([]); setRequests([]); setOpenCovers([]); setAnnouncements([]);
              setEmployees([]); setAllProfiles([]); setPendingProfiles([]);
              setEvents([]); setEventStaff([]); setAllAvailability([]); setAllLocations([]);
              setView("today"); setHomeLocFilter(""); setLocationFilter("All");
              setUser(profile);
              writeCache(CACHE_KEYS.profile, profile);
              if (updatingStarsRef.current.size === 0) {
                loadData();
              }
            } else if (mounted && !passwordSetupRequired.current) {
              console.log("[Auth] SIGNED_IN — setUser SUPPRESSED (no profile or error) — profile:", !!profile, "| profileError:", !!profileError);
              setLoginData(p => ({ ...p, error: "Could not load your profile. Contact your manager.", submitting: false }));
            } else {
              console.log("[Auth] SIGNED_IN — setUser SUPPRESSED by passwordSetupRequired=true (fired during profile fetch)");
            }
          } catch (e) {
            if (mounted) setLoginData(p => ({ ...p, error: e.message.includes("timed out") ? "Connected but profile load timed out — check Supabase RLS policies." : "Login failed. Try again.", submitting: false }));
          }
        } else if (event === "SIGNED_OUT") {
          clearCache(CACHE_KEYS.profile);
          clearCache(CACHE_KEYS.tomorrow);
          try { localStorage.removeItem("superdome_last_location"); } catch {}
          if (mounted) {
            setUser(null);
            setDataLoaded(false);
            setShifts([]); setRequests([]); setOpenCovers([]); setAnnouncements([]);
            setEmployees([]); setAllProfiles([]); setPendingProfiles([]);
            setEvents([]); setEventStaff([]); setAllAvailability([]); setAllLocations([]);
            setView("today"); setHomeLocFilter(""); setLocationFilter("All");
            if (!passwordSetupRequired.current) {
              setPasswordSetup({ active: false, password: "", confirm: "", error: "", submitting: false });
              setPasswordReset({ active: false, password: "", confirm: "", error: "", submitting: false, success: false });
            }
          }
        }
      }
    );

    return () => {
      mounted = false;
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, [loadData]);

  // Real-time subscriptions — granular per-event state updates for instant cross-device sync
  useEffect(() => {
    if (!user) return;
    const byDate = (a, b) => a.date.localeCompare(b.date) || (a.start_time || "").localeCompare(b.start_time || "");
    const channel = supabase.channel("db-changes")
      // --- shifts ---
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "shifts" }, ({ new: row }) => {
        setShifts(prev => prev.some(s => s.id === row.id)
          ? prev.map(s => s.id === row.id ? row : s)
          : [...prev, row].sort((a, b) => a.date.localeCompare(b.date) || a.start_time.localeCompare(b.start_time))
        );
      })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "shifts" }, ({ new: row }) => {
        setShifts(prev => prev.map(s => s.id === row.id ? row : s));
      })
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "shifts" }, ({ old: row }) => {
        if (!row?.id) { console.warn("[Realtime] shifts DELETE received no id in old payload — run: ALTER TABLE public.shifts REPLICA IDENTITY FULL"); return; }
        setShifts(prev => prev.filter(s => s.id !== row.id));
      })
      // --- requests ---
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "requests" }, ({ new: row }) => {
        setRequests(prev => prev.some(r => r.id === row.id) ? prev : [row, ...prev]);
        if (row.type === "cover" && row.status === "pending" && row.shift_date >= getToday()) {
          setOpenCovers(prev => prev.some(c => c.id === row.id) ? prev : [row, ...prev]);
        }
      })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "requests" }, ({ new: row }) => {
        setRequests(prev => prev.some(r => r.id === row.id)
          ? prev.map(r => r.id === row.id ? { ...row, _cancelled: r._cancelled } : r)
          : prev
        );
        setOpenCovers(prev => {
          if (row.type === "cover" && row.status === "pending" && row.shift_date >= getToday()) {
            return prev.some(c => c.id === row.id) ? prev.map(c => c.id === row.id ? row : c) : [row, ...prev];
          }
          return prev.filter(c => c.id !== row.id);
        });
      })
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "requests" }, ({ old: row }) => {
        setRequests(prev => prev.filter(r => r.id !== row.id));
        setOpenCovers(prev => prev.filter(c => c.id !== row.id));
      })
      // --- announcements ---
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "announcements" }, ({ new: row }) => {
        setAnnouncements(prev => prev.some(a => a.id === row.id) ? prev : [row, ...prev]);
      })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "announcements" }, ({ new: row }) => {
        setAnnouncements(prev => prev.map(a => a.id === row.id ? row : a));
      })
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "announcements" }, ({ old: row }) => {
        setAnnouncements(prev => prev.filter(a => a.id !== row.id));
      })
      // --- events ---
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "events" }, ({ new: row }) => {
        setEvents(prev => prev.some(e => e.id === row.id)
          ? prev.map(e => e.id === row.id ? row : e)
          : [...prev, row].sort(byDate)
        );
      })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "events" }, ({ new: row }) => {
        setEvents(prev => prev.map(e => e.id === row.id ? row : e));
      })
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "events" }, ({ old: row }) => {
        setEvents(prev => prev.filter(e => e.id !== row.id));
        setEventStaff(prev => prev.filter(es => es.event_id !== row.id));
        setSelectedEvent(sel => sel?.id === row.id ? null : sel);
      })
      // --- event_staff ---
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "event_staff" }, ({ new: row }) => {
        setEventStaff(prev => prev.some(es => es.id === row.id) ? prev : [...prev, row]);
      })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "event_staff" }, ({ new: row }) => {
        setEventStaff(prev => prev.map(es => es.id === row.id ? row : es));
      })
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "event_staff" }, ({ old: row }) => {
        setEventStaff(prev =>
          row.id != null
            ? prev.filter(es => es.id !== row.id)
            : prev.filter(es => !(es.event_id === row.event_id && es.employee_id === row.employee_id))
        );
      })
      .subscribe((status, err) => {
        if (status === "CHANNEL_ERROR") console.error("[Realtime] channel error:", err);
        if (status === "TIMED_OUT") console.warn("[Realtime] subscription timed out — check Supabase Realtime is enabled and the table is in the supabase_realtime publication");
      });
    return () => supabase.removeChannel(channel);
  }, [user]);

  // Pull-to-refresh touch handlers — non-passive touchmove to allow preventDefault
  useEffect(() => {
    if (!user) return;
    const PULL_THRESHOLD = 60;
    const PULL_MAX = 80;

    const onTouchStart = (e) => {
      if (e.target.closest("button, input, select, [data-no-pull]")) return;
      if (window.scrollY === 0) {
        touchStartY.current = e.touches[0].clientY;
        pulling.current = true;
      }
    };

    const onTouchMove = (e) => {
      if (!pulling.current) return;
      const delta = e.touches[0].clientY - touchStartY.current;
      if (delta > 0) {
        e.preventDefault();
        const y = Math.min(delta * 0.45, PULL_MAX);
        pullYRef.current = y;
        setPullY(y);
      } else {
        pulling.current = false;
        pullYRef.current = 0;
        setPullY(0);
      }
    };

    const onTouchEnd = () => {
      if (!pulling.current) return;
      pulling.current = false;
      if (pullYRef.current >= PULL_THRESHOLD) {
        pullYRef.current = 0;
        setPullY(0);
        handleRefresh();
      } else {
        pullYRef.current = 0;
        setPullY(0);
      }
    };

    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
    };
  }, [user, handleRefresh]);

  // Advance weekStart to the current week's Sunday at each local midnight.
  // Also triggers a re-render so isToday highlights update automatically.
  useEffect(() => {
    let timer;
    const scheduleAdvance = () => {
      const now = new Date();
      const midnight = new Date(now);
      midnight.setHours(24, 0, 0, 0); // next local midnight
      timer = setTimeout(() => {
        const d = new Date(getToday() + "T00:00:00");
        d.setDate(d.getDate() - d.getDay()); // Sunday of the new current week
        const newWeekStart = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        setWeekStart(prev => {
          // Only snap back if the user is still viewing the now-stale week.
          // If they've navigated to a different week, leave them there.
          const prevSat = new Date(prev + "T00:00:00");
          prevSat.setDate(prevSat.getDate() + 6);
          const prevSatStr = `${prevSat.getFullYear()}-${String(prevSat.getMonth() + 1).padStart(2, "0")}-${String(prevSat.getDate()).padStart(2, "0")}`;
          return getToday() > prevSatStr ? newWeekStart : prev;
        });
        scheduleAdvance(); // reschedule for the next midnight
      }, midnight - now);
    };
    scheduleAdvance();
    return () => clearTimeout(timer);
  }, []);

  // Reset homeDay to "tomorrow" and close any open day sheet when navigating back to the Home tab
  useEffect(() => { if (view === "today") { setHomeDay("tomorrow"); setSelectedDay(null); } }, [view]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (allLocations.length > 0 && !homeLocFilter) setHomeLocFilter(allLocations[0].name); }, [allLocations]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset Time Off form when navigating away from the Requests tab
  useEffect(() => {
    if (view !== "requests") {
      setShowTimeOff(false);
      setTimeOffRequest({ startDate: "", endDate: "", reason: "" });
      setTimeOffReasonError(false);
      setTimeOffDateError("");
      setConfirmTimeOff(false);
    }
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps


  // Cache tomorrow shift so it's available instantly on next app open
  useEffect(() => {
    if (!user || !dataLoaded) return;
    const tomorrowDate = (() => {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    })();
    const shift = shifts.find(s => s.employee_id === user.id && s.date === tomorrowDate) || null;
    const entry = { userId: user.id, date: tomorrowDate, shift };
    writeCache(CACHE_KEYS.tomorrow, entry);
    setTomorrowCache(entry);
  }, [shifts, user, dataLoaded]);

  const verifyResetCode = async () => {
    if (verifyingResetCode.current) return;
    verifyingResetCode.current = true;
    if (!resetCodeStep.email.trim() || !resetCodeStep.code.trim()) {
      verifyingResetCode.current = false;
      setResetCodeStep(p => ({ ...p, error: "Please enter your email and reset code." }));
      return;
    }
    if (resetCodeStep.code.trim().length !== 6) {
      verifyingResetCode.current = false;
      setResetCodeStep(p => ({ ...p, error: "Code must be 6 digits." }));
      return;
    }
    setResetCodeStep(p => ({ ...p, submitting: true, error: "" }));
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    try {
      const res = await fetch(`${supabaseUrl}/functions/v1/verify-password-reset-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}` },
        body: JSON.stringify({ email: resetCodeStep.email.trim().toLowerCase(), code: resetCodeStep.code.trim() }),
      });
      const json = await res.json();
      if (!res.ok || json.error) {
        verifyingResetCode.current = false;
        setResetCodeStep(p => ({ ...p, submitting: false, error: json.error || "Invalid or expired code." }));
        return;
      }
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: json.email,
        password: json.temp_password,
      });
      if (signInError) {
        verifyingResetCode.current = false;
        setResetCodeStep(p => ({ ...p, submitting: false, error: "Failed to establish session. Please try again." }));
        return;
      }
      setResetCodeStep({ active: false, email: "", code: "", error: "", submitting: false });
      setForgotPassword({ active: false, email: "", submitting: false, sent: false, error: "" });
      setPasswordReset(p => ({ ...p, active: true }));
      passwordSetupRequired.current = true;
      verifyingResetCode.current = false;
    } catch (err) {
      verifyingResetCode.current = false;
      setResetCodeStep(p => ({ ...p, submitting: false, error: "Network error. Please try again." }));
    }
  };

  const verifyInviteCode = async () => {
    if (inviteCode.submitting) return;
    if (!inviteCode.email.trim() || !inviteCode.code.trim()) {
      setInviteCode(p => ({ ...p, error: "Please enter your email and invite code." }));
      return;
    }
    if (inviteCode.code.trim().length !== 6) {
      setInviteCode(p => ({ ...p, error: "Invite code must be 6 digits." }));
      return;
    }
    setInviteCode(p => ({ ...p, submitting: true, error: "" }));
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    try {
      const res = await fetch(`${supabaseUrl}/functions/v1/verify-invite-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}` },
        body: JSON.stringify({ email: inviteCode.email.trim().toLowerCase(), code: inviteCode.code.trim() }),
      });
      const json = await res.json();
      if (!res.ok || json.error) {
        setInviteCode(p => ({ ...p, submitting: false, error: json.error || "Something went wrong. Please try again." }));
        return;
      }
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: json.email,
        password: json.temp_password,
      });

      if (signInError) {
        verifyingCode.current = false;
        setInviteCode(p => ({ ...p, submitting: false, error: "Failed to establish session. Please try again." }));
        return;
      }

      passwordSetupRequired.current = true;
      // Fetch existing profile to prefill name fields
      const { data: { session: sess } } = await supabase.auth.getSession();
      let prefill = { firstName: "", lastName: "", email: json.email || "" };
      if (sess?.user) {
        const { data: prof } = await supabase.from("profiles").select("first_name, last_name, email").eq("id", sess.user.id).single();
        if (prof) { prefill.firstName = prof.first_name || ""; prefill.lastName = prof.last_name || ""; prefill.email = prof.email || json.email || ""; }
      }
      setInviteCode({ active: false, email: "", code: "", error: "", submitting: false });
      setPasswordSetup(p => ({ ...p, active: true, firstName: prefill.firstName, lastName: prefill.lastName, email: prefill.email, phone: "", phoneError: "" }));
      verifyingCode.current = false;
    } catch (err) {
      setInviteCode(p => ({ ...p, submitting: false, error: "Network error. Please try again." }));
    }
  };

  const login = async () => {
    setLoginData(p => ({ ...p, error: "", submitting: true }));

    const timeoutId = setTimeout(() => {
      setLoginData(p => ({
        ...p,
        error: "Sign in is taking too long. Check your connection and try again.",
        submitting: false,
      }));
    }, 10000);

    const { error } = await supabase.auth.signInWithPassword({
      email: loginData.email, password: loginData.password,
    });

    clearTimeout(timeoutId);
    if (error) setLoginData(p => ({ ...p, error: "Invalid email or password", submitting: false }));
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setLoading(false);
    setPasswordSetup({ active: false, password: "", confirm: "", error: "", submitting: false });
    setPasswordReset({ active: false, password: "", confirm: "", error: "", submitting: false, success: false });
    passwordSetupRequired.current = false;
    setView("today");
    setLoginData({ email: "", password: "", error: "", submitting: false });
  };

  const checkConflicts = (empId, date, start, end, excludeId = null) => {
    const emp = employees.find(u => u.id === empId);
    if (!emp) return null;
    if (requests.some(r =>
      r.type === "timeoff" && r.status === "approved" &&
      r.requester_id === empId &&
      date >= r.date && date <= (r.end_date || r.date)
    )) return `${formatName(emp.first_name || emp.name, emp.last_name, user.role)} has approved time off on ${fmtDate(date)}.`;
    const conflict = shifts.find(s =>
      s.employee_id === empId && s.date === date &&
      s.id !== excludeId &&
      timesOverlap(start, end, s.start_time, s.end_time)
    );
    if (conflict) return `${formatName(emp.first_name || emp.name, emp.last_name, user.role)} is already scheduled ${fmt(conflict.start_time)}–${fmt(conflict.end_time)} on ${fmtDate(date)}.`;
    return null;
  };

  useEffect(() => {
    if (!showAddShift || !newShift.employeeId) { setSelectedEmpAvailability([]); return; }
    supabase.from("availability").select("*").eq("employee_id", newShift.employeeId)
      .then(({ data }) => setSelectedEmpAvailability(data || []));
  }, [showAddShift, newShift.employeeId]);

  const submitShift = async () => {
    if (shiftSaving === "saving") return;
    const emp = allProfiles.find(u => u.id === newShift.employeeId);
    const loc = newShift.location || lastShiftLocation;
    if (!emp || !newShift.start || !newShift.end || !loc) { setAddShiftWarning("Please fill in all fields."); return; }
    if (editingShiftId && !newShift.date) { setAddShiftWarning("Please fill in all fields."); return; }
    if (!editingShiftId && selectedDates.size === 0) { setAddShiftWarning("Please select at least one date."); return; }
    const dates = editingShiftId ? [newShift.date] : [...selectedDates].sort();
    for (const d of dates) {
      const conflict = checkConflicts(newShift.employeeId, d, newShift.start, newShift.end, editingShiftId || null);
      if (conflict) { setAddShiftWarning(editingShiftId ? conflict : `${fmtDate(d)}: ${conflict}`); return; }
    }
    setAddShiftWarning("");
    setShiftSaving("saving");
    // Yield one frame so React flushes "Saving…" before the network call
    await new Promise(r => setTimeout(r, 16));

    if (editingShiftId) {
      const { data, error } = await supabase.from("shifts").update({
        employee_id: emp.id, employee_name: emp.name,
        date: dates[0], start_time: newShift.start, end_time: newShift.end,
        note: newShift.note || null, location: loc,
      }).eq("id", editingShiftId).select().single();
      if (error) { setShiftSaving(null); showToast("Error saving shift.", "error"); return; }
      const updated = data || { id: editingShiftId, employee_id: emp.id, employee_name: emp.name, date: dates[0], start_time: newShift.start, end_time: newShift.end, note: newShift.note || null, location: loc };
      setShifts(prev => prev.map(s => s.id === editingShiftId ? { ...s, ...updated } : s));
      setShiftSaving("saved");
      setTimeout(() => setShiftSaving(null), 2000);
      setAddShiftWarning(""); setEditingShiftId(null);
      setNewShift({ employeeId: "", date: "", start: "09:00", end: "17:00", note: "", location: loc });
      setSelectedDates(new Set()); setShowAddShift(false);
      showToast(`${formatName(emp.first_name || emp.name, emp.last_name, user.role)} · ${fmtDate(dates[0])} · ${fmt(newShift.start)}–${fmt(newShift.end)} · ${loc}`);
    } else {
      const rows = dates.map(d => ({
        employee_id: emp.id, employee_name: emp.name,
        date: d, start_time: newShift.start, end_time: newShift.end,
        note: newShift.note || null, location: loc,
        org_id: user?.org_id || null,
      }));
      const { data, error } = await supabase.from("shifts").insert(rows).select();
      if (error) { setShiftSaving(null); showToast("Error adding shifts.", "error"); return; }
      // Always update local state — use returned rows if available, else construct from known values
      const inserted = (data && data.length > 0)
        ? data
        : rows.map((r, i) => ({ ...r, id: `temp-${Date.now()}-${i}` }));
      setShifts(prev => [...prev, ...inserted].sort((a, b) => a.date.localeCompare(b.date) || a.start_time.localeCompare(b.start_time)));
      setShiftSaving("saved");
      setTimeout(() => setShiftSaving(null), 2000);
      setAddShiftWarning("");
      setNewShift({ employeeId: "", date: "", start: "09:00", end: "17:00", note: "", location: loc });
      setSelectedDates(new Set());
      const toastMsg = dates.length === 1
        ? `${formatName(emp.first_name || emp.name, emp.last_name, user.role)} · ${fmtDate(dates[0])} · ${fmt(newShift.start)}–${fmt(newShift.end)} · ${loc}`
        : `${dates.length} shifts added — ${formatName(emp.first_name || emp.name, emp.last_name, user.role)} · ${fmt(newShift.start)}–${fmt(newShift.end)} · ${loc}`;
      showToast(toastMsg);
    }
  };

  const revokeTimeOff = async (id) => {
    setProcessingId(id);
    const { error } = await supabase.from("requests").update({ status: "revoked" }).eq("id", id);
    if (error) { console.log("Revoke error:", error); showToast("Error revoking time off.", "error"); setProcessingId(null); return; }
    setRequests(prev => prev.map(r => r.id === id ? { ...r, status: "revoked", updated_at: new Date().toISOString() } : r));
    setProcessingId(null);
    showToast("Time off revoked.", "info");
  };

  const cancelRequest = async (r) => {
    if (!r?.id || r.id.toString().startsWith("opt-")) return;
    setCancelConfirmId(null);
    const { error } = await supabase.from("requests").update({ status: "revoked" }).eq("id", r.id).eq("requester_id", user.id);
    if (error) {
      showToast("Error cancelling request.", "error");
      return;
    }
    // Success — update local state after confirmed server write
    const cancelled = { ...r, status: "revoked", _cancelled: true, updated_at: new Date().toISOString() };
    if (r.type === "cover") {
      // Remove from openCovers (pending list source) and upsert into requests
      // so the completed filter — which only reads from `requests` — can find it.
      // Covers may not be in `requests` at all due to RLS, so add if missing.
      setOpenCovers(prev => prev.filter(c => c.id !== r.id));
      setRequests(prev => prev.some(req => req.id === r.id)
        ? prev.map(req => req.id === r.id ? cancelled : req)
        : [cancelled, ...prev]
      );
    } else {
      setRequests(prev => prev.map(req => req.id === r.id ? cancelled : req));
    }
    showToast("Request cancelled.");
  };

  const saveEvent = async () => {
    const typeVal = newEvent.type === "Other" ? newEvent.typeCustom.trim() : newEvent.type;
    const needsDate = editingEventId ? !newEvent.date : (newEvent.is_recurring ? !newEvent.date : selectedEventDates.size === 0);
    if (!typeVal || !newEvent.title.trim() || needsDate || !newEvent.start_time || !newEvent.end_time || !newEvent.location) {
      setAddEventWarning("Please fill in all required fields."); return;
    }
    if (newEvent.is_recurring && !newEvent.recurring_end_date) {
      setAddEventWarning("Please set a recurring end date."); return;
    }
    const baseEvent = {
      type: typeVal, title: newEvent.title.trim(),
      start_time: newEvent.start_time, end_time: newEvent.end_time,
      location: newEvent.location, notes: newEvent.notes.trim() || null,
      is_recurring: newEvent.is_recurring,
      recurring_end_date: newEvent.is_recurring ? newEvent.recurring_end_date : null,
      is_informational: newEvent.is_informational,
      org_id: user?.org_id || null,
    };
    const staffRows = [...selectedStaffIds].map(id => {
      const prof = allProfiles.find(pr => pr.id === id);
      return { employee_id: id, employee_name: prof?.name || "", org_id: user?.org_id || null };
    });
    const resetForm = () => {
      setAddEventWarning("");
      setNewEvent({ type: "", typeCustom: "", title: "", date: "", start_time: "", end_time: "", location: "", notes: "", is_recurring: false, recurring_end_date: "", is_informational: false });
      setSelectedStaffIds(new Set());
      setSelectedEventDates(new Set());
      setShowAddEvent(false);
      setEditingEventId(null);
    };

    // Compute dates upfront so we can check staff coverage
    const dates = [];
    if (editingEventId) {
      dates.push(newEvent.date);
    } else if (newEvent.is_recurring) {
      let cur = new Date(newEvent.date + "T00:00:00");
      const endDate = new Date(newEvent.recurring_end_date + "T00:00:00");
      while (cur <= endDate) { dates.push(cur.toISOString().split("T")[0]); cur.setDate(cur.getDate() + 7); }
    } else {
      dates.push(...[...selectedEventDates].sort());
    }

    // Warn if any assigned staff has no shift on one or more event dates
    const warnings = [];
    if (!newEvent.is_informational) {
      for (const id of selectedStaffIds) {
        const prof = allProfiles.find(pr => pr.id === id);
        const missing = dates.filter(d => !shifts.some(s => s.employee_id === id && s.date === d));
        if (missing.length === 0) continue;
        if (missing.length === 1) {
          warnings.push(`${prof?.name} is not scheduled to work on ${fmtDate(missing[0])}.`);
        } else {
          warnings.push(`${prof?.name} is not scheduled on ${missing.length} of the selected dates.`);
        }
      }
    }

    const doSave = async () => {
      if (editingEventId) {
        const { data: updatedEvent, error } = await supabase.from("events").update({ ...baseEvent, date: newEvent.date }).eq("id", editingEventId).select().single();
        if (error) { showToast("Error saving event.", "error"); return; }
        await supabase.from("event_staff").delete().eq("event_id", editingEventId);
        let newStaff = [];
        if (staffRows.length > 0) {
          const { data: staffData } = await supabase.from("event_staff").insert(staffRows.map(s => ({ ...s, event_id: editingEventId }))).select();
          if (staffData) newStaff = staffData;
        }
        const updated = updatedEvent || { id: editingEventId, ...baseEvent, date: newEvent.date };
        setEvents(prev => prev.map(e => e.id === editingEventId ? { ...e, ...updated } : e));
        setEventStaff(prev => [...prev.filter(es => es.event_id !== editingEventId), ...newStaff]);
        resetForm(); showToast("Event updated!");
      } else if (newEvent.is_recurring) {
        const { data: result, error: rpcError } = await supabase.rpc("insert_recurring_events", {
          p_dates:              dates,
          p_type:               baseEvent.type,
          p_title:              baseEvent.title,
          p_start_time:         baseEvent.start_time,
          p_end_time:           baseEvent.end_time,
          p_location:           baseEvent.location,
          p_notes:              baseEvent.notes,
          p_is_recurring:       baseEvent.is_recurring,
          p_recurring_end_date: baseEvent.recurring_end_date,
          p_is_informational:   baseEvent.is_informational,
          p_org_id:             user?.org_id || null,
          p_staff:              staffRows,
        });
        if (rpcError) { showToast("Error creating recurring events — no events were saved.", "error"); return; }
        const insertedEvents = result?.events || [];
        const insertedStaff  = result?.staff  || [];
        setEvents(prev => [...prev, ...insertedEvents].sort((a, b) => a.date.localeCompare(b.date) || (a.start_time || "").localeCompare(b.start_time || "")));
        if (insertedStaff.length > 0) setEventStaff(prev => [...prev, ...insertedStaff]);
        resetForm();
        showToast(`${dates.length} events created!`);
      } else {
        const insertedEvents = [];
        const insertedStaff = [];
        for (const d of dates) {
          const { data: evData, error: evErr } = await supabase.from("events").insert({ ...baseEvent, date: d }).select().single();
          if (evErr) { showToast("Error creating event.", "error"); return; }
          insertedEvents.push(evData);
          if (staffRows.length > 0) {
            const { data: staffData, error: staffErr } = await supabase.from("event_staff").insert(staffRows.map(s => ({ ...s, event_id: evData.id }))).select();
            if (staffErr) {
              setEvents(prev => [...prev, ...insertedEvents].sort((a, b) => a.date.localeCompare(b.date) || (a.start_time || "").localeCompare(b.start_time || "")));
              resetForm();
              showToast("Event created but staff could not be assigned — please edit the event to add staff manually.", "error");
              return;
            }
            if (staffData) insertedStaff.push(...staffData);
          }
        }
        setEvents(prev => [...prev, ...insertedEvents].sort((a, b) => a.date.localeCompare(b.date) || (a.start_time || "").localeCompare(b.start_time || "")));
        if (insertedStaff.length > 0) setEventStaff(prev => [...prev, ...insertedStaff]);
        resetForm();
        showToast(dates.length > 1 ? `${dates.length} events created!` : "Event created!");
      }
    };

    if (warnings.length > 0) {
      setConfirmDialog({
        message: `${warnings.join("\n")}\n\nProceed anyway?`,
        confirmColor: "#f97316",
        onConfirm: doSave,
      });
    } else {
      doSave();
    }
  };

  const deleteEvent = async (id) => {
    const { error: staffError } = await supabase.from("event_staff").delete().eq("event_id", id);
    if (staffError) { showToast("Error deleting event.", "error"); return; }
    const { data, error } = await supabase.from("events").delete().eq("id", id).select("id");
    if (error) { showToast("Error deleting event.", "error"); return; }
    if (!data || data.length === 0) { showToast("Delete failed — permission denied. Ask your admin to check RLS policies.", "error"); return; }
    setEvents(prev => prev.filter(e => e.id !== id));
    setEventStaff(prev => prev.filter(es => es.event_id !== id));
    setSelectedEvent(null);
    showToast("Event deleted.", "info");
  };

  const deleteShift = async (id) => {
    // Delete all rows in FK-referencing tables before removing the shift.
    // Supabase does not cascade deletes automatically, so each dependent table
    // must be cleared manually or the final shifts.delete() will be blocked by
    // a foreign key constraint violation.
    const { error: e1 } = await supabase.from("requests").delete().eq("shift_id", id);
    if (e1) { showToast("Error removing shift.", "error"); return; }
    const { error: e2 } = await supabase.from("requests").delete().eq("cover_shift_id", id);
    if (e2) { showToast("Error removing shift.", "error"); return; }
    const { error: e3 } = await supabase.from("shift_swap_requests").delete().eq("requester_shift_id", id);
    if (e3) { showToast("Error removing shift.", "error"); return; }
    const { error: e4 } = await supabase.from("shift_swap_requests").delete().eq("target_shift_id", id);
    if (e4) { showToast("Error removing shift.", "error"); return; }

    // Remove event_staff assignments for this employee on the same date.
    // event_staff rows reference events, not shifts, so this is a semantic
    // cleanup — the employee should no longer appear on events for a shift
    // date they no longer work.
    const shift = shifts.find(s => s.id === id);
    if (shift) {
      const { data: eventsOnDate, error: evLookupErr } = await supabase
        .from("events").select("id").eq("org_id", user.org_id).eq("date", shift.date);
      if (evLookupErr) { showToast("Error removing shift.", "error"); return; }
      const eventIds = (eventsOnDate || []).map(e => e.id);
      if (eventIds.length > 0) {
        const { error: e5 } = await supabase.from("event_staff").delete()
          .eq("employee_id", shift.employee_id)
          .in("event_id", eventIds);
        if (e5) { showToast("Error removing shift.", "error"); return; }
      }
    }

    const { data: deleteData, error: deleteError } = await supabase.from("shifts").delete().eq("id", id).select("id");
    if (deleteError) {
      showToast("Something went wrong — the shift could not be removed. Please try again.", "error");
      return;
    }
    if (!deleteData || deleteData.length === 0) {
      showToast("Delete failed — permission denied. Ask your admin to check RLS policies.", "error");
      return;
    }

    setShifts(prev => prev.filter(s => s.id !== id));
    setRequests(prev => prev.filter(r => r.shift_id !== id && r.cover_shift_id !== id));
    setOpenCovers(prev => prev.filter(c => c.cover_shift_id !== id));
    if (shift) {
      setEventStaff(prev => prev.filter(es => {
        if (es.employee_id !== shift.employee_id) return true;
        const ev = events.find(e => e.id === es.event_id);
        return !ev || ev.date !== shift.date;
      }));
    }
    showToast("Shift removed.", "info");
  };

  const confirmDeleteShift = (shift) => {
    const assignedEvents = eventStaff
      .filter(es => es.employee_id === shift.employee_id)
      .map(es => events.find(e => e.id === es.event_id))
      .filter(e => e && e.date === shift.date);

    if (assignedEvents.length === 0) {
      setConfirmDialog({ message: "Are you sure you want to remove this shift?", onConfirm: () => deleteShift(shift.id) });
      return;
    }

    const empName = shift.employee_name || "This employee";
    const eventLines = assignedEvents.map(e => `• ${e.title} (${fmt(e.start_time)}–${fmt(e.end_time)})`).join("\n");
    const message = `${empName} is assigned to the following event${assignedEvents.length > 1 ? "s" : ""} on this date:\n\n${eventLines}\n\nRemoving this shift will also unassign them. Continue?`;

    setConfirmDialog({
      message,
      onConfirm: () => deleteShift(shift.id),
      confirmLabel: "Remove Shift & Unassign",
      confirmColor: "#ef4444",
    });
  };

  const postAnnouncement = async () => {
    if (!newAnnouncement.trim()) return;
    const { data, error } = await supabase.from("announcements").insert({
      text: newAnnouncement.trim(), date: getToday(), pinned: pinNew, author_name: user.name,
      org_id: user?.org_id || null,
    }).select().single();
    if (error) { showToast("Error posting.", "error"); return; }
    setAnnouncements(prev => [data, ...prev]);
    setNewAnnouncement(""); setPinNew(false); showToast("Announcement posted!");
  };

  const deleteAnnouncement = async (id) => {
    const { data, error } = await supabase.from("announcements").delete().eq("id", id).select("id");
    if (error) { showToast("Error deleting announcement.", "error"); return; }
    if (!data || data.length === 0) { showToast("Delete failed — permission denied. Ask your admin to check RLS policies.", "error"); return; }
    setAnnouncements(prev => prev.filter(a => a.id !== id));
    showToast("Announcement removed.", "info");
  };

  const togglePin = async (id, currentPinned) => {
    if (pinningId === id) return;
    setPinningId(id);
    const { error } = await supabase.from("announcements").update({ pinned: !currentPinned }).eq("id", id);
    setPinningId(null);
    if (error) { showToast("Error updating announcement.", "error"); return; }
    setAnnouncements(prev => prev.map(a => a.id === id ? { ...a, pinned: !currentPinned } : a));
  };

  const submitSwap = async () => {
    if (!swapRequest.targetShiftId || !swapRequest.myShiftId) return;
    if (swapSubmitting) return;
    const myShift = shifts.find(s => s.id === swapRequest.myShiftId);
    const targetShift = shifts.find(s => s.id === swapRequest.targetShiftId);
    if (!myShift || !targetShift) return;
    setSwapSubmitting(true);
    const tempId = "opt-" + Date.now();
    setRequests(prev => [{
      id: tempId, type: "swap", status: "pending_target",
      requester_id: user.id, requester_name: user.name,
      shift_id: myShift.id, shift_date: myShift.date,
      shift_start: myShift.start_time, shift_end: myShift.end_time,
      shift_location: myShift.location || defaultLoc,
      target_id: targetShift.employee_id, target_name: targetShift.employee_name,
      cover_shift_id: targetShift.id,
      reason: swapRequest.reason || null,
      created_at: new Date().toISOString(),
    }, ...prev]);
    const { error } = await supabase.from("requests").insert({
      type: "swap", status: "pending_target",
      requester_id: user.id, requester_name: user.name,
      shift_id: myShift.id, shift_date: myShift.date,
      shift_start: myShift.start_time, shift_end: myShift.end_time,
      shift_location: myShift.location || defaultLoc,
      target_id: targetShift.employee_id, target_name: targetShift.employee_name,
      cover_shift_id: targetShift.id,
      reason: swapRequest.reason || null,
      org_id: user?.org_id || null,
    });
    setSwapSubmitting(false);
    if (error) {
      setRequests(prev => prev.filter(r => r.id !== tempId));
      showToast("Error submitting.", "error");
      return;
    }
    setSwapRequest({ targetShiftId: "", myShiftId: "", reason: "" });
    setShowSwap(false);
    showToast(`Swap request sent to ${formatName(targetShift.employee_name, null, user.role)}!`);
  };

  const handleTargetResponse = async (id, accepted) => {
    setProcessingId(id);
    const req = requests.find(r => r.id === id);
    if (accepted) {
      const aShift = shifts.find(s => s.id === req.shift_id);
      const bShift = shifts.find(s => s.id === req.cover_shift_id);
      const sameLocation = (aShift?.location || defaultLoc) === (bShift?.location || defaultLoc);
      if (sameLocation) {
        const { error: swapError } = await supabase.rpc("swap_shifts", {
          p_request_id:     id,
          p_shift_a_id:     req.shift_id,
          p_target_id:      req.target_id,
          p_target_name:    req.target_name,
          p_shift_b_id:     req.cover_shift_id,
          p_requester_id:   req.requester_id,
          p_requester_name: req.requester_name,
        });
        if (swapError) { showToast("Error approving swap.", "error"); setProcessingId(null); return; }
        setRequests(prev => prev.map(r => r.id === id ? { ...r, status: "approved", updated_at: new Date().toISOString() } : r));
        setShifts(prev => prev.map(s =>
          s.id === req.shift_id ? { ...s, employee_id: req.target_id, employee_name: req.target_name } :
          s.id === req.cover_shift_id ? { ...s, employee_id: req.requester_id, employee_name: req.requester_name } :
          s
        ));
        showToast("Swap accepted and approved!");
      } else {
        await supabase.from("requests").update({ status: "pending_manager" }).eq("id", id);
        setRequests(prev => prev.map(r => r.id === id ? { ...r, status: "pending_manager" } : r));
        showToast("Swap accepted — sent to manager for approval.");
      }
    } else {
      await supabase.from("requests").update({ status: "declined" }).eq("id", id);
      setRequests(prev => prev.map(r => r.id === id ? { ...r, status: "declined", updated_at: new Date().toISOString() } : r));
      showToast("Swap declined.");
    }
    setProcessingId(null);
  };

  const submitTimeOff = async () => {
    if (!timeOffRequest.startDate || !timeOffRequest.reason) return;
    setTimeOffSubmitting(true);
    const tempId = "opt-" + Date.now();
    setRequests(prev => [{
      id: tempId, type: "timeoff", status: "pending",
      requester_id: user.id, requester_name: user.name,
      date: timeOffRequest.startDate,
      end_date: timeOffRequest.endDate || timeOffRequest.startDate,
      reason: timeOffRequest.reason,
      created_at: new Date().toISOString(),
    }, ...prev]);
    const { error } = await supabase.from("requests").insert({
      type: "timeoff", status: "pending",
      requester_id: user.id, requester_name: user.name,
      date: timeOffRequest.startDate,
      end_date: timeOffRequest.endDate || timeOffRequest.startDate,
      reason: timeOffRequest.reason,
      org_id: user?.org_id || null,
    });
    if (error) {
      setRequests(prev => prev.filter(r => r.id !== tempId));
      showToast("Error submitting.", "error");
      setTimeOffSubmitting(false);
      return;
    }
    setTimeOffSubmitting(false);
    setTimeOffRequest({ startDate: "", endDate: "", reason: "" });
    setShowTimeOff(false); showToast("Time off request sent!");
  };

  const submitCoverRequest = async () => {
    if (!coverRequestShift) return;
    const payload = {
      type: "cover", status: "pending",
      requester_id: user.id, requester_name: user.name,
      cover_shift_id: coverRequestShift.id,
      shift_date: coverRequestShift.date,
      shift_start: coverRequestShift.start_time,
      shift_end: coverRequestShift.end_time,
      shift_location: coverRequestShift.location || defaultLoc,
      reason: coverNote.trim() || null,
      org_id: user?.org_id || null,
    };
    const tempId = "opt-" + Date.now();
    setOpenCovers(prev => [{ id: tempId, ...payload, created_at: new Date().toISOString() }, ...prev]);
    const { error } = await supabase.from("requests").insert(payload);
    if (error) {
      setOpenCovers(prev => prev.filter(r => r.id !== tempId));
      showToast("Error submitting cover request.", "error");
      return;
    }
    setCoverRequestShift(null);
    setCoverNote("");
    showToast("Cover request posted!");
  };

  // ── Employee home action sheet submitters ─────────────────────────────────

  const openEmpSheet = (type) => {
    if (type === "availability") {
      setEmpAvailRows([0,1,2,3,4,5,6].map(dow => ({ day_of_week: dow, is_available: false, start_time: "09:00:00", end_time: "22:00:00" })));
      setEmpAvailSaving(false);
      setEmpSheet({ type, visible: false });
      setTimeout(() => setEmpSheet({ type, visible: true }), 10);
      supabase.from("availability").select("*").eq("employee_id", user.id).then(({ data }) => {
        if (data && data.length > 0) {
          setEmpAvailRows(prev => prev.map(row => {
            const saved = data.find(r => r.day_of_week === row.day_of_week);
            return saved ? { ...row, is_available: saved.is_available, start_time: saved.start_time, end_time: saved.end_time } : row;
          }));
        }
      });
    } else {
      setEmpSheet({ type, visible: false });
      setTimeout(() => setEmpSheet({ type, visible: true }), 10);
    }
  };
  const closeEmpSheet = () => {
    setEmpSheet(s => ({ ...s, visible: false }));
    setTimeout(() => setEmpSheet({ type: null, visible: false }), 300);
    setEmpTimeOff({ startDate: "", endDate: "", reason: "", error: "", submitting: false });
    setEmpCover({ shiftId: null, reason: "", error: "", submitting: false });
    setEmpLate({ arrivalTime: "", note: "", error: "", submitting: false });
  };

  const submitEmpAvailability = async () => {
    setEmpAvailSaving(true);
    const rows = empAvailRows.map(r => ({
      employee_id: user.id,
      day_of_week: r.day_of_week,
      is_available: r.is_available,
      start_time: r.start_time,
      end_time: r.end_time,
    }));
    await supabase.from("availability").upsert(rows, { onConflict: "employee_id,day_of_week" });
    setEmpAvailSaving(false);
    closeEmpSheet();
  };

  const submitEmpTimeOff = async () => {
    if (!empTimeOff.startDate || !empTimeOff.reason.trim()) {
      setEmpTimeOff(s => ({ ...s, error: "Start date and reason are required." }));
      return;
    }
    const today = new Date().toISOString().slice(0, 10);
    if (empTimeOff.startDate < today) {
      setEmpTimeOff(s => ({ ...s, error: "Start date can't be in the past." }));
      return;
    }
    if (empTimeOff.endDate && empTimeOff.endDate < empTimeOff.startDate) {
      setEmpTimeOff(s => ({ ...s, error: "End date can't be before start date." }));
      return;
    }
    setEmpTimeOff(s => ({ ...s, submitting: true, error: "" }));
    const tempId = "opt-" + Date.now();
    const payload = {
      type: "timeoff", status: "pending",
      requester_id: user.id, requester_name: user.name,
      date: empTimeOff.startDate,
      end_date: empTimeOff.endDate || empTimeOff.startDate,
      reason: empTimeOff.reason.trim(),
      org_id: user?.org_id || null,
    };
    setRequests(prev => [{ id: tempId, ...payload, created_at: new Date().toISOString() }, ...prev]);
    const { error } = await supabase.from("requests").insert(payload);
    if (error) {
      setRequests(prev => prev.filter(r => r.id !== tempId));
      setEmpTimeOff(s => ({ ...s, submitting: false, error: "Failed to submit. Try again." }));
      return;
    }
    closeEmpSheet();
    showToast("Time off request sent!");
  };

  const submitEmpCover = async () => {
    const shift = shifts.find(s => s.id === empCover.shiftId);
    if (!shift) { setEmpCover(s => ({ ...s, error: "Please select a shift." })); return; }
    if (!empCover.reason.trim()) { setEmpCover(s => ({ ...s, error: "Please provide a reason." })); return; }
    const alreadyRequested = (
      requests.some(r => r.type === "cover" && r.requester_id === user.id && r.cover_shift_id === shift.id && r.status !== "declined" && r.status !== "revoked" && r.status !== "denied" && !r._cancelled)
    ) || (
      openCovers.some(r => r.requester_id === user.id && r.cover_shift_id === shift.id && r.status === "pending")
    );
    if (alreadyRequested) { setEmpCover(s => ({ ...s, error: "You already have a cover request for this shift." })); return; }
    setEmpCover(s => ({ ...s, submitting: true, error: "" }));
    const tempId = "opt-" + Date.now();
    const payload = {
      type: "cover", status: "pending",
      requester_id: user.id, requester_name: user.name,
      cover_shift_id: shift.id,
      shift_date: shift.date,
      shift_start: shift.start_time,
      shift_end: shift.end_time,
      shift_location: shift.location || defaultLoc,
      reason: empCover.reason.trim() || null,
      org_id: user?.org_id || null,
    };
    setOpenCovers(prev => [{ id: tempId, ...payload, created_at: new Date().toISOString() }, ...prev]);
    const { error } = await supabase.from("requests").insert(payload);
    if (error) {
      setOpenCovers(prev => prev.filter(r => r.id !== tempId));
      setEmpCover(s => ({ ...s, submitting: false, error: "Failed to submit. Try again." }));
      return;
    }
    closeEmpSheet();
    showToast("Cover request posted!");
  };

  const submitEmpLate = async () => {
    if (!empLate.arrivalTime) { setEmpLate(s => ({ ...s, error: "Please enter your estimated arrival time." })); return; }
    setEmpLate(s => ({ ...s, submitting: true, error: "" }));
    const tempId = "opt-" + Date.now();
    const payload = {
      type: "late_arrival", status: "pending",
      requester_id: user.id, requester_name: user.name,
      shift_date: getToday(),
      shift_start: empLate.arrivalTime,
      reason: empLate.note.trim() || null,
      org_id: user?.org_id || null,
    };
    setRequests(prev => [{ id: tempId, ...payload, created_at: new Date().toISOString() }, ...prev]);
    const { error } = await supabase.from("requests").insert(payload);
    if (error) {
      setRequests(prev => prev.filter(r => r.id !== tempId));
      setEmpLate(s => ({ ...s, submitting: false, error: "Failed to submit. Try again." }));
      return;
    }
    closeEmpSheet();
    showToast("Late arrival notice sent!");
  };

  const submitCoverForShift = async (shift) => {
    const payload = {
      type: "cover", status: "pending",
      requester_id: user.id, requester_name: user.name,
      cover_shift_id: shift.id,
      shift_date: shift.date,
      shift_start: shift.start_time,
      shift_end: shift.end_time,
      shift_location: shift.location || defaultLoc,
      reason: null,
      org_id: user?.org_id || null,
    };
    const tempId = "opt-" + Date.now();
    setOpenCovers(prev => [{ id: tempId, ...payload, created_at: new Date().toISOString() }, ...prev]);
    const { error } = await supabase.from("requests").insert(payload);
    if (error) {
      setOpenCovers(prev => prev.filter(r => r.id !== tempId));
      showToast("Error submitting cover request.", "error");
      return false;
    }
    showToast("Cover request posted!");
    return true;
  };

  const claimCover = async (r) => {
    if (processingId === r.id) return;

    // Guard: overlap with existing shift
    const hasOverlap = shifts.some(s =>
      s.employee_id === user.id &&
      s.date === r.shift_date &&
      s.start_time < r.shift_end &&
      s.end_time > r.shift_start
    );
    if (hasOverlap) {
      setAlertDialog("You're already scheduled during this time.");
      return;
    }

    // ⭐ Guard: star rating floor
    const requesterProfile = allProfiles.find(p => p.id === r.requester_id);
    const claimerProfile = allProfiles.find(p => p.id === user.id);
    if (
      requesterProfile?.cit_stars &&
      claimerProfile?.cit_stars &&
      claimerProfile.cit_stars < requesterProfile.cit_stars
    ) {
      setAlertDialog("This shift requires more experience. Contact your manager.");
      return;
    }

    // Optimistically remove from open covers
    setOpenCovers(prev => prev.filter(c => c.id !== r.id));
    setProcessingId(r.id);

    const { data, error } = await supabase.rpc("claim_cover", {
      p_request_id: r.id,
      p_user_id: user.id,
      p_user_name: user.name,
    });

    setProcessingId(null);

    if (error) {
      setOpenCovers(prev => [r, ...prev]);
      showToast("Error claiming cover.", "error");
      return;
    }

    if (data === false) {
      setAlertDialog("This shift was already claimed by someone else.");
      return;
    }
    if (data !== true) {
      setOpenCovers(prev => [r, ...prev]);
      showToast("Error claiming cover.", "error");
      return;
    }

    showToast(`You're covering ${formatName(r.requester_name, null, user.role)}'s shift`);
  };

  const updateStars = async (profileId, stars) => {
    setUpdatingStars(prev => new Set(prev).add(profileId));
    const { error } = await supabase
      .from("profiles")
      .update({ cit_stars: stars })
      .eq("id", profileId);
    if (error) {
      showToast("Error updating rating.", "error");
    } else {
      setAllProfiles(prev =>
        prev.map(p => p.id === profileId ? { ...p, cit_stars: stars } : p)
      );
      showToast("Rating updated.");
    }
    setUpdatingStars(prev => { const next = new Set(prev); next.delete(profileId); return next; });
  };

  const handleRequest = async (id, status, replacementId) => {
    setProcessingId(id);
    const req = requests.find(r => r.id === id) || openCovers.find(r => r.id === id);
    if (status === "approved" && req.type === "swap") {
      const { error: e1 } = await supabase.from("shifts")
        .update({ employee_id: req.target_id, employee_name: req.target_name })
        .eq("id", req.shift_id);
      if (e1) { showToast("Error approving swap.", "error"); setProcessingId(null); return; }
      if (req.cover_shift_id) {
        const { error: e2 } = await supabase.from("shifts")
          .update({ employee_id: req.requester_id, employee_name: req.requester_name })
          .eq("id", req.cover_shift_id);
        if (e2) { showToast("Error approving swap.", "error"); setProcessingId(null); return; }
        setShifts(prev => prev.map(s => s.id === req.cover_shift_id ? { ...s, employee_id: req.requester_id, employee_name: req.requester_name } : s));
      }
      setShifts(prev => prev.map(s => s.id === req.shift_id ? { ...s, employee_id: req.target_id, employee_name: req.target_name } : s));
      showToast(`Swap approved — shifts traded ✓`);
    } else if (status === "approved" && req.type === "cover" && replacementId) {
      const replacement = allProfiles.find(p => p.id === replacementId);
      const replacementName = replacement ? (replacement.first_name && replacement.last_name ? `${replacement.first_name} ${replacement.last_name}` : replacement.name) : null;
      const { error: shiftErr } = await supabase.from("shifts")
        .update({ employee_id: replacementId, ...(replacementName ? { employee_name: replacementName } : {}) })
        .eq("id", req.cover_shift_id);
      if (shiftErr) { showToast("Error assigning coverage.", "error"); setProcessingId(null); return; }
      if (replacementName) {
        setShifts(prev => prev.map(s => s.id === req.cover_shift_id ? { ...s, employee_id: replacementId, employee_name: replacementName } : s));
      }
      // Remove original employee from events overlapping the covered shift
      const overlappingEvents = events.filter(e =>
        e.date === req.shift_date &&
        e.start_time < req.shift_end &&
        e.end_time > req.shift_start
      );
      const assignedEventIds = overlappingEvents
        .filter(e => eventStaff.some(es => es.event_id === e.id && es.employee_id === req.requester_id))
        .map(e => e.id);
      if (assignedEventIds.length > 0) {
        await supabase.from("event_staff").delete()
          .eq("employee_id", req.requester_id)
          .in("event_id", assignedEventIds);
        setEventStaff(prev => prev.filter(es =>
          !(es.employee_id === req.requester_id && assignedEventIds.includes(es.event_id))
        ));
        const origName = formatName(req.requester_name, null, user.role);
        showToast(`Coverage assigned to ${replacementName || "replacement"} ✓ · ${origName} removed from ${assignedEventIds.length} event${assignedEventIds.length !== 1 ? "s" : ""}`);
      } else {
        showToast(`Coverage assigned to ${replacementName || "replacement"} ✓`);
      }
    } else if (status === "approved" && req.type === "timeoff") {
      const start = req.date;
      const end = req.end_date || req.date;
      const conflictingShifts = shifts.filter(s => s.employee_id === req.requester_id && s.date >= start && s.date <= end);
      if (conflictingShifts.length > 0) {
        setTimeOffConflictModal({ id, req, conflictingShifts });
        setProcessingId(null);
        return;
      }
      showToast(`Approved for ${formatName(req.requester_name, null, user.role)} ✓`);
    } else if (status === "approved") {
      showToast(`Approved for ${formatName(req.requester_name, null, user.role)} ✓`);
    } else {
      showToast("Request denied.");
    }
    await supabase.from("requests").update({ status }).eq("id", id);
    setRequests(prev => prev.map(r => r.id === id ? { ...r, status, updated_at: new Date().toISOString() } : r));
    if (status === "denied") {
      setOpenCovers(prev => prev.filter(c => c.id !== id));
    }
    setProcessingId(null);
  };

  const myShifts = user
    ? shifts.filter(s => s.employee_id === user.id && s.date >= getToday())
        .sort((a, b) => a.date.localeCompare(b.date) || a.start_time.localeCompare(b.start_time))
    : [];
  const pendingRequests = requests.filter(r =>
    (r.type === "timeoff" && r.status === "pending") ||
    (r.type === "swap" && r.status === "pending_manager")
  );
  const openCoversCount = user ? openCovers.filter(r => r.requester_id !== user.id && new Date() < new Date(`${r.shift_date}T${r.shift_start}`)).length : 0;
  const empPendingCount = user ? requests.filter(r =>
    (r.type === "swap" && r.target_id === user.id && r.status === "pending_target") ||
    (r.requester_id === user.id && r.status === "pending_manager")
  ).length + openCoversCount : 0;
const sortedShifts = [...shifts].sort((a, b) =>
    a.date.localeCompare(b.date) || a.start_time.localeCompare(b.start_time)
  );
  const sortedAnnouncements = [...announcements]
    .filter(a => {
      if (a.pinned) return true;
      const age = Date.now() - new Date(a.created_at).getTime();
      return age < 48 * 60 * 60 * 1000;
    })
    .sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));
  const approvedTimeOff = requests.filter(r =>
    r.type === "timeoff" && r.status === "approved" &&
    (r.end_date || r.date) >= getToday()
  );
  const activeLocationFilter = locationFilter;
  const unreadCount = (() => {
    if (!sortedAnnouncements.length) return 0;
    if (!lastSeenAnnouncementId) return sortedAnnouncements.length;
    const idx = sortedAnnouncements.findIndex(a => a.id === lastSeenAnnouncementId);
    return idx === -1 ? sortedAnnouncements.length : idx;
  })();
  const dayViewShifts = selectedDay
    ? [...shifts]
        .filter(s => s.date === selectedDay)
        .filter(s => activeLocationFilter === "All" || s.location === activeLocationFilter)
        .sort((a, b) => a.start_time.localeCompare(b.start_time))
    : [];

  const isAppLoading = loading || (user && !profileConfirmed);

  useEffect(() => {
    if (!isAppLoading) {
      const el = document.getElementById("tmrw-loading-overlay");
      const st = document.getElementById("tmrw-loading-style");
      if (el) el.remove();
      if (st) st.remove();
      return;
    }
    if (!document.getElementById("tmrw-loading-style")) {
      const st = document.createElement("style");
      st.id = "tmrw-loading-style";
      st.textContent = `@keyframes tmrw-spin{to{transform:rotate(360deg)}} #root{filter:blur(4px);opacity:0.3;transition:filter 0.3s,opacity 0.3s;pointer-events:none}`;
      document.head.appendChild(st);
    }
    if (!document.getElementById("tmrw-loading-overlay")) {
      const el = document.createElement("div");
      el.id = "tmrw-loading-overlay";
      el.style.cssText = "position:fixed;inset:0;z-index:99999;display:flex;align-items:center;justify-content:center;pointer-events:none";
      el.innerHTML = `<div style="width:36px;height:36px;border:3.5px solid transparent;border-top-color:#F55C00;border-radius:50%;animation:tmrw-spin 0.7s linear infinite"></div>`;
      document.body.appendChild(el);
    }
    return () => {
      const el = document.getElementById("tmrw-loading-overlay");
      const st = document.getElementById("tmrw-loading-style");
      if (el) el.remove();
      if (st) st.remove();
    };
  }, [isAppLoading]);

  console.log("[App render] loading:", loading, "| user:", user ? user.email : null, "| passwordSetup.active:", passwordSetup.active);

  if (window.location.search.includes("preview=eventcard")) return <EventCardPreview />;

  if (window.location.search.includes("preview=lineup")) {
    const today = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; })();
    const tomorrow = (() => { const d = new Date(); d.setDate(d.getDate()+1); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; })();
    const PREVIEW_PROFILES = [
      { id: "p1", name: "Jack",     role: "manager",  staff_type: "head" },
      { id: "p2", name: "Danny R.", role: "employee", staff_type: "head" },
      { id: "p3", name: "Sam L.",   role: "employee", staff_type: "assistant" },
      { id: "p4", name: "Ryan K.",  role: "employee", staff_type: "assistant" },
      { id: "p5", name: "Maya P.",  role: "employee", staff_type: "assistant" },
      { id: "p6", name: "Tyler M.", role: "employee", staff_type: "head" },
      { id: "p7", name: "Jordan F.",role: "employee", staff_type: "head" },
    ];
    const PREVIEW_EVENTS = [
      { id: "e1", date: tomorrow, location: "Main", title: "Spring Break Camp",  type: "Camp",    start_time: "09:00", end_time: "12:00", notes: "Ages 7–12" },
      { id: "e2", date: tomorrow, location: "Main", title: "Soccer Class",       type: "Class",   start_time: "16:00", end_time: "17:00", notes: "Ages 6–8" },
      { id: "e3", date: tomorrow, location: "Main", title: "Birthday Party",     type: "Party",   start_time: "11:00", end_time: "12:30", notes: "Ages 5–7" },
      { id: "e4", date: tomorrow, location: "Main", title: "Flag Football",      type: "Class",   start_time: "17:00", end_time: "18:00", notes: "Ages 9–11" },
      { id: "e5", date: today,    location: "Main", title: "Morning Training",   type: "Training",start_time: "08:00", end_time: "10:00", notes: "" },
    ];
    const PREVIEW_EVENT_STAFF = [
      { event_id: "e1", employee_id: "p1" },
      { event_id: "e1", employee_id: "p2" },
      { event_id: "e1", employee_id: "p3" },
      { event_id: "e2", employee_id: "p4" },
      { event_id: "e2", employee_id: "p5" },
      { event_id: "e3", employee_id: "p6" },
      { event_id: "e3", employee_id: "p7" },
      { event_id: "e5", employee_id: "p2" },
    ];
    const PREVIEW_SHIFTS = [
      { id: "s1", date: tomorrow, location: "Main", employee_id: "p1", employee_name: "Jack",     start_time: "08:00", end_time: "13:00" },
      { id: "s2", date: tomorrow, location: "Main", employee_id: "p2", employee_name: "Danny R.", start_time: "09:00", end_time: "14:00" },
      { id: "s3", date: tomorrow, location: "Main", employee_id: "p3", employee_name: "Sam L.",   start_time: "09:00", end_time: "12:00" },
      { id: "s4", date: tomorrow, location: "Main", employee_id: "p4", employee_name: "Ryan K.",  start_time: "15:00", end_time: "18:00" },
    ];
    const PREVIEW_REQUESTS = [
      { id: "r1", type: "swap",     status: "pending_manager" },
      { id: "r2", type: "cover",    status: "pending" },
      { id: "r3", type: "time_off", status: "pending" },
    ];
    return (
      <ManagerLineup
        shifts={PREVIEW_SHIFTS}
        events={PREVIEW_EVENTS}
        eventStaff={PREVIEW_EVENT_STAFF}
        allProfiles={PREVIEW_PROFILES}
        requests={PREVIEW_REQUESTS}
      />
    );
  }

  if (window.location.pathname === "/send-setup") {
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
    const inputStyle = { width: "100%", background: "#F5F5F5", border: "none", borderRadius: 10, padding: 14, fontSize: 14, marginBottom: 12, boxSizing: "border-box", fontFamily: "inherit", color: "#111", outline: "none" };
    return (
      <div style={{ minHeight: "100vh", background: "#ffffff", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Oswald', sans-serif" }}>
        <div style={{ width: "100%", maxWidth: 400, padding: "0 28px" }}>
          <div style={{ marginBottom: 28, textAlign: "center" }}>
            <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 52, letterSpacing: "-0.02em", lineHeight: 1, color: "#111" }}>
              TMRW<span style={{ color: "#F55C00" }}>.</span>
            </div>
            <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 16, marginTop: 6, letterSpacing: "0.01em" }}>
              <span style={{ color: "#F55C00" }}>Know Tomorrow</span><span style={{ color: "#111" }}>.</span>
            </div>
          </div>
          {sendSetupPage.success ? (
            <div style={{ textAlign: "center", color: "#F55C00", fontFamily: "'Oswald', sans-serif", fontSize: 15, fontWeight: 600 }}>
              Code sent to {sendSetupPage.successName} at {sendSetupPage.successEmail}
            </div>
          ) : (
            <>
              <input
                type="text"
                placeholder="Business name"
                value={sendSetupPage.businessName}
                onChange={e => setSendSetupPage(p => ({ ...p, businessName: e.target.value, error: "" }))}
                style={inputStyle}
              />
              <input
                type="text"
                placeholder="Owner full name"
                value={sendSetupPage.ownerName}
                onChange={e => setSendSetupPage(p => ({ ...p, ownerName: e.target.value, error: "" }))}
                style={inputStyle}
              />
              <input
                type="email"
                placeholder="Email address"
                value={sendSetupPage.email}
                onChange={e => setSendSetupPage(p => ({ ...p, email: e.target.value, error: "" }))}
                style={inputStyle}
              />
              <input
                type="tel"
                placeholder="Phone number"
                value={formatPhoneDisplay(sendSetupPage.phone)}
                onChange={e => setSendSetupPage(p => ({ ...p, phone: phoneDigitsOnly(e.target.value), error: "" }))}
                inputMode="tel"
                style={inputStyle}
              />
              {sendSetupPage.error && (
                <div style={{ color: "#E53935", fontSize: 13, marginBottom: 12 }}>{sendSetupPage.error}</div>
              )}
              <button
                onClick={async () => {
                  if (!sendSetupPage.email.trim()) return;
                  setSendSetupPage(p => ({ ...p, submitting: true, error: "" }));
                  try {
                    const res = await fetch(`${supabaseUrl}/functions/v1/send-setup-code`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${anonKey}` },
                      body: JSON.stringify({
                        email: sendSetupPage.email.trim().toLowerCase(),
                        firstName: sendSetupPage.ownerName.trim().split(" ")[0] || "",
                        businessName: sendSetupPage.businessName.trim(),
                        ownerName: sendSetupPage.ownerName.trim(),
                        phone: sendSetupPage.phone.length === 10 ? "+1" + sendSetupPage.phone : "",
                      }),
                    });
                    if (!res.ok) throw new Error("error");
                    setSendSetupPage(p => ({ ...p, submitting: false, success: true, successEmail: p.email.trim().toLowerCase(), successName: p.ownerName.trim() || p.email.trim() }));
                  } catch {
                    setSendSetupPage(p => ({ ...p, submitting: false, error: "Something went wrong" }));
                  }
                }}
                disabled={sendSetupPage.submitting}
                style={{ width: "100%", background: "#F55C00", border: "none", borderRadius: 10, padding: 14, color: "#fff", fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 15, letterSpacing: "0.05em", cursor: sendSetupPage.submitting ? "not-allowed" : "pointer", opacity: sendSetupPage.submitting ? 0.7 : 1 }}
              >
                {sendSetupPage.submitting ? "Sending…" : "SEND SETUP CODE"}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (window.location.pathname === "/setup") {
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
    const inputStyle = { width: "100%", background: "#F5F5F5", border: "none", borderRadius: 10, padding: 14, fontSize: 14, marginBottom: 12, boxSizing: "border-box", fontFamily: "inherit", color: "#111", outline: "none" };
    return (
      <div style={{ minHeight: "100vh", background: "#ffffff", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Oswald', sans-serif" }}>
        <div style={{ width: "100%", maxWidth: 400, padding: "0 28px" }}>
          <div style={{ marginBottom: 28, textAlign: "center" }}>
            <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 52, letterSpacing: "-0.02em", lineHeight: 1, color: "#111" }}>
              TMRW<span style={{ color: "#F55C00" }}>.</span>
            </div>
            <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 16, marginTop: 6, letterSpacing: "0.01em" }}>
              <span style={{ color: "#F55C00" }}>Know Tomorrow</span><span style={{ color: "#111" }}>.</span>
            </div>
          </div>

          {orgSetup.step === 1 ? (
            <>
              <div style={{ fontFamily: "'Oswald', sans-serif", fontSize: 18, fontWeight: 700, color: "#111", marginBottom: 20, textAlign: "center" }}>SET UP YOUR ACCOUNT</div>
              <input
                type="email"
                placeholder="Email address"
                value={orgSetup.email}
                onChange={e => setOrgSetup(p => ({ ...p, email: e.target.value, error: "" }))}
                style={inputStyle}
              />
              <input
                type="text"
                placeholder="6-digit code"
                value={orgSetup.code}
                onChange={e => setOrgSetup(p => ({ ...p, code: e.target.value.replace(/\D/g, "").slice(0, 6), error: "" }))}
                maxLength={6}
                inputMode="numeric"
                style={{ ...inputStyle, letterSpacing: 6, textAlign: "center" }}
              />
              {orgSetup.error && (
                <div style={{ color: "#E53935", fontSize: 13, marginBottom: 12 }}>{orgSetup.error}</div>
              )}
              <button
                onClick={async () => {
                  if (!orgSetup.email.trim() || !orgSetup.code.trim()) {
                    setOrgSetup(p => ({ ...p, error: "Please enter your email and code." }));
                    return;
                  }
                  setOrgSetup(p => ({ ...p, submitting: true, error: "" }));
                  try {
                    const res = await fetch(`${supabaseUrl}/functions/v1/verify-setup-code`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${anonKey}` },
                      body: JSON.stringify({ email: orgSetup.email.trim().toLowerCase(), code: orgSetup.code.trim() }),
                    });
                    const json = await res.json();
                    if (!res.ok || !json.valid) {
                      setOrgSetup(p => ({ ...p, submitting: false, error: json.error || "Invalid or expired code." }));
                      return;
                    }
                    const ownerParts = (json.owner_name || "").trim().split(" ");
                    const prefillFirst = ownerParts[0] || "";
                    const prefillLast = ownerParts.slice(1).join(" ") || "";
                    const prefillPhone = (json.phone || "").replace(/^\+1/, "").replace(/\D/g, "").slice(0, 10);
                    setOrgSetup(p => ({
                      ...p,
                      submitting: false,
                      step: 2,
                      businessName: json.business_name || p.businessName,
                      firstName: prefillFirst || p.firstName,
                      lastName: prefillLast || p.lastName,
                      phone: prefillPhone || p.phone,
                    }));
                  } catch {
                    setOrgSetup(p => ({ ...p, submitting: false, error: "Something went wrong. Try again." }));
                  }
                }}
                disabled={orgSetup.submitting}
                style={{ width: "100%", background: "#F55C00", border: "none", borderRadius: 10, padding: 14, color: "#fff", fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 15, letterSpacing: "0.05em", cursor: orgSetup.submitting ? "not-allowed" : "pointer", opacity: orgSetup.submitting ? 0.7 : 1 }}
              >
                {orgSetup.submitting ? "Verifying…" : "VERIFY CODE"}
              </button>
            </>
          ) : orgSetup.step === 2 ? (
            <>
              <div style={{ fontFamily: "'Oswald', sans-serif", fontSize: 18, fontWeight: 700, color: "#111", marginBottom: 20, textAlign: "center" }}>ALMOST THERE.</div>
              <input
                type="text"
                placeholder="Business name"
                value={orgSetup.businessName}
                onChange={e => setOrgSetup(p => ({ ...p, businessName: e.target.value, error: "" }))}
                style={inputStyle}
              />
              <div style={{ display: "flex", gap: 10 }}>
                <input
                  type="text"
                  placeholder="First name"
                  value={orgSetup.firstName}
                  onChange={e => setOrgSetup(p => ({ ...p, firstName: e.target.value, error: "" }))}
                  style={{ ...inputStyle, marginBottom: 12 }}
                />
                <input
                  type="text"
                  placeholder="Last name"
                  value={orgSetup.lastName}
                  onChange={e => setOrgSetup(p => ({ ...p, lastName: e.target.value, error: "" }))}
                  style={{ ...inputStyle, marginBottom: 12 }}
                />
              </div>
              <input
                type="tel"
                inputMode="numeric"
                placeholder="Phone number"
                value={formatPhoneDisplay(orgSetup.phone)}
                onChange={e => { const d = phoneDigitsOnly(e.target.value); setOrgSetup(p => ({ ...p, phone: d, phoneError: "", error: "" })); }}
                style={{ ...inputStyle, marginBottom: orgSetup.phoneError ? 4 : 12, borderColor: orgSetup.phoneError ? "#E53935" : undefined }}
              />
              {orgSetup.phoneError && (
                <div style={{ color: "#E53935", fontSize: 12, marginBottom: 12 }}>{orgSetup.phoneError}</div>
              )}
              <div style={{ position: "relative", marginBottom: 12 }}>
                <input
                  type={showSetupPassword ? "text" : "password"}
                  placeholder="Password"
                  value={orgSetup.password}
                  onChange={e => setOrgSetup(p => ({ ...p, password: e.target.value, error: "" }))}
                  style={{ ...inputStyle, marginBottom: 0, paddingRight: 44 }}
                />
                <button type="button" onClick={() => setShowSetupPassword(v => !v)} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: 4, color: "#aaa", lineHeight: 1 }} aria-label={showSetupPassword ? "Hide password" : "Show password"}>
                  {showSetupPassword
                    ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  }
                </button>
              </div>
              <div style={{ fontSize: 12, color: "#111", marginTop: -4, marginBottom: 12 }}>At least 8 characters</div>
              <div style={{ position: "relative", marginBottom: 12 }}>
                <input
                  type={showSetupConfirm ? "text" : "password"}
                  placeholder="Confirm password"
                  value={orgSetup.confirmPassword}
                  onChange={e => setOrgSetup(p => ({ ...p, confirmPassword: e.target.value, error: "" }))}
                  style={{ ...inputStyle, marginBottom: 0, paddingRight: 44 }}
                />
                <button type="button" onClick={() => setShowSetupConfirm(v => !v)} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: 4, color: "#aaa", lineHeight: 1 }} aria-label={showSetupConfirm ? "Hide password" : "Show password"}>
                  {showSetupConfirm
                    ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  }
                </button>
              </div>
              {orgSetup.error && (
                <div style={{ color: "#E53935", fontSize: 13, marginBottom: 12 }}>{orgSetup.error}</div>
              )}
              <button
                onClick={async () => {
                  const { email, code, businessName, firstName, lastName, phone, password, confirmPassword } = orgSetup;
                  if (!businessName.trim() || !firstName.trim() || !lastName.trim() || !password || !confirmPassword) {
                    setOrgSetup(p => ({ ...p, error: "Please fill in all fields." }));
                    return;
                  }
                  const setupPhoneDigits = phone.replace(/\D/g, "");
                  if (setupPhoneDigits.length > 0 && setupPhoneDigits.length !== 10) {
                    setOrgSetup(p => ({ ...p, phoneError: "Enter a valid 10-digit phone number" }));
                    return;
                  }
                  if (password.length < 8) {
                    setOrgSetup(p => ({ ...p, error: "Password must be at least 8 characters." }));
                    return;
                  }
                  if (password !== confirmPassword) {
                    setOrgSetup(p => ({ ...p, error: "Passwords don't match." }));
                    return;
                  }
                  setOrgSetup(p => ({ ...p, submitting: true, error: "", phoneError: "" }));
                  try {
                    const res = await fetch(`${supabaseUrl}/functions/v1/create-org`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${anonKey}` },
                      body: JSON.stringify({ email: email.trim().toLowerCase(), code: code.trim(), businessName: businessName.trim(), firstName: firstName.trim(), lastName: lastName.trim(), ...(setupPhoneDigits.length === 10 ? { phone: `+1${setupPhoneDigits}` } : {}), password }),
                    });
                    const json = await res.json();
                    if (!res.ok || !json.success) {
                      setOrgSetup(p => ({ ...p, submitting: false, error: json.error || "Something went wrong. Try again." }));
                      return;
                    }
                    setOrgSetup(p => ({ ...p, submitting: false, step: 3, orgId: json.orgId }));
                  } catch {
                    setOrgSetup(p => ({ ...p, submitting: false, error: "Something went wrong. Try again." }));
                  }
                }}
                disabled={orgSetup.submitting}
                style={{ width: "100%", background: "#F55C00", border: "none", borderRadius: 10, padding: 14, color: "#fff", fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 15, letterSpacing: "0.05em", cursor: orgSetup.submitting ? "not-allowed" : "pointer", opacity: orgSetup.submitting ? 0.7 : 1 }}
              >
                {orgSetup.submitting ? "Creating account…" : "CREATE MY ACCOUNT"}
              </button>
            </>
          ) : orgSetup.step === 3 ? (
            <>
              <div style={{ fontFamily: "'Oswald', sans-serif", fontSize: 18, fontWeight: 700, color: "#111", marginBottom: 8, textAlign: "center" }}>ADD YOUR LOCATIONS</div>
              <div style={{ fontSize: 13, color: "#888", textAlign: "center", marginBottom: 20 }}>Add each facility or location your team works at.</div>
              {orgSetup.locations.map((loc, i) => (
                <div key={i} style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                  <input
                    type="text"
                    placeholder={"Location " + (i + 1)}
                    value={loc}
                    onChange={e => { const locs = [...orgSetup.locations]; locs[i] = e.target.value; setOrgSetup(p => ({ ...p, locations: locs })); }}
                    style={{ flex: 1, background: "#F5F5F5", border: "none", borderRadius: 10, padding: 14, fontSize: 14, fontFamily: "inherit", color: "#111", outline: "none" }}
                  />
                  {orgSetup.locations.length > 1 && (
                    <button onClick={() => { const locs = orgSetup.locations.filter((_, j) => j !== i); setOrgSetup(p => ({ ...p, locations: locs })); }} style={{ background: "none", border: "none", color: "#aaa", fontSize: 20, cursor: "pointer", padding: "0 4px" }}>×</button>
                  )}
                </div>
              ))}
              <button onClick={() => setOrgSetup(p => ({ ...p, locations: [...p.locations, ""] }))} style={{ background: "none", border: "1px dashed #ccc", borderRadius: 10, padding: "10px 14px", width: "100%", color: "#888", fontSize: 13, fontFamily: "inherit", cursor: "pointer", marginBottom: 20 }}>+ Add another location</button>
              {orgSetup.error && <div style={{ color: "#E53935", fontSize: 13, marginBottom: 12 }}>{orgSetup.error}</div>}
              <button
                onClick={async () => {
                  const validLocs = orgSetup.locations.map(l => l.trim()).filter(Boolean);
                  if (validLocs.length === 0) { setOrgSetup(p => ({ ...p, error: "Add at least one location." })); return; }
                  setOrgSetup(p => ({ ...p, submitting: true, error: "" }));
                  try {
                    await supabase.auth.signInWithPassword({ email: orgSetup.email.trim().toLowerCase(), password: orgSetup.password });
                    const { error: locError } = await supabase.from("locations").insert(validLocs.map(name => ({ name, org_id: orgSetup.orgId })));
                    if (locError) { setOrgSetup(p => ({ ...p, submitting: false, error: "Failed to save locations. Try again." })); return; }
                    clearCache(CACHE_KEYS.profile);
                    clearCache(CACHE_KEYS.tomorrow);
                    try { localStorage.removeItem("superdome_last_location"); } catch {}
                    window.location.href = "/";
                  } catch { setOrgSetup(p => ({ ...p, submitting: false, error: "Something went wrong. Try again." })); }
                }}
                disabled={orgSetup.submitting}
                style={{ width: "100%", background: "#F55C00", border: "none", borderRadius: 10, padding: 14, color: "#fff", fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 15, letterSpacing: "0.05em", cursor: orgSetup.submitting ? "not-allowed" : "pointer", opacity: orgSetup.submitting ? 0.7 : 1 }}
              >{orgSetup.submitting ? "Finishing setup…" : "FINISH SETUP"}</button>
            </>
          ) : null}
        </div>
      </div>
    );
  }

  if (passwordSetup.active) return (
    <div style={{ minHeight: "100vh", background: "#ffffff", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Oswald', sans-serif" }}>
      <div style={{ width: "100%", maxWidth: 380, padding: "0 28px", textAlign: "center" }}>
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 52, letterSpacing: "-0.02em", lineHeight: 1, color: "#111" }}>
            TMRW<span style={{ color: "#F55C00" }}>.</span>
          </div>
          <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 16, marginTop: 6, letterSpacing: "0.01em" }}>
            <span style={{ color: "#F55C00" }}>Know Tomorrow</span><span style={{ color: "#111" }}>.</span>
          </div>
        </div>
        <div style={{ background: "#f9f9f9", border: "1px solid #e5e5e5", borderRadius: 12, padding: 28, textAlign: "left" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6, fontFamily: "'Oswald', sans-serif" }}>YOUR INFO</div>
          {/* Email — read-only */}
          <input
            type="text"
            value={passwordSetup.email}
            readOnly
            tabIndex={-1}
            style={{ width: "100%", background: "#eee", border: "1px solid #d1d1d1", borderRadius: 8, padding: "12px 14px", color: "#999", fontSize: 14, boxSizing: "border-box", fontFamily: "'Oswald', sans-serif", outline: "none", marginBottom: 10, cursor: "default" }}
          />
          {/* First / Last name — prefilled, editable */}
          <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
            <input
              type="text"
              placeholder="First name"
              value={passwordSetup.firstName}
              onChange={e => setPasswordSetup(p => ({ ...p, firstName: e.target.value, error: "" }))}
              style={{ flex: 1, background: "#fff", border: "1px solid #d1d1d1", borderRadius: 8, padding: "12px 14px", color: "#111", fontSize: 14, boxSizing: "border-box", fontFamily: "'Oswald', sans-serif", outline: "none" }}
            />
            <input
              type="text"
              placeholder="Last name"
              value={passwordSetup.lastName}
              onChange={e => setPasswordSetup(p => ({ ...p, lastName: e.target.value, error: "" }))}
              style={{ flex: 1, background: "#fff", border: "1px solid #d1d1d1", borderRadius: 8, padding: "12px 14px", color: "#111", fontSize: 14, boxSizing: "border-box", fontFamily: "'Oswald', sans-serif", outline: "none" }}
            />
          </div>
          {/* Phone */}
          <input
            type="tel"
            inputMode="numeric"
            placeholder="Phone number"
            value={formatPhoneDisplay(passwordSetup.phone)}
            onChange={e => { const d = phoneDigitsOnly(e.target.value); setPasswordSetup(p => ({ ...p, phone: d, phoneError: "" })); }}
            style={{ width: "100%", background: "#fff", border: `1px solid ${passwordSetup.phoneError ? "#E53935" : "#d1d1d1"}`, borderRadius: 8, padding: "12px 14px", color: "#111", fontSize: 14, boxSizing: "border-box", fontFamily: "'Oswald', sans-serif", outline: "none", marginBottom: passwordSetup.phoneError ? 4 : 16 }}
          />
          {passwordSetup.phoneError && (
            <div style={{ color: "#E53935", fontSize: 12, marginBottom: 12 }}>{passwordSetup.phoneError}</div>
          )}
          <div style={{ fontSize: 11, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6, fontFamily: "'Oswald', sans-serif" }}>SET PASSWORD</div>
          <div style={{ position: "relative", marginBottom: 4 }}>
            <input
              type={showSetupPassword ? "text" : "password"}
              placeholder="New password"
              value={passwordSetup.password}
              onChange={e => setPasswordSetup(p => ({ ...p, password: e.target.value }))}
              style={{ width: "100%", background: "#fff", border: "1px solid #d1d1d1", borderRadius: 8, padding: "12px 44px 12px 14px", color: "#111", fontSize: 14, boxSizing: "border-box", fontFamily: "'Oswald', sans-serif", outline: "none" }}
            />
            <button
              type="button"
              onClick={() => setShowSetupPassword(v => !v)}
              style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: 4, color: "#aaa", lineHeight: 1 }}
              aria-label={showSetupPassword ? "Hide password" : "Show password"}
            >
              {showSetupPassword
                ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              }
            </button>
          </div>
          <div style={{ fontSize: 12, color: "#111", marginTop: 4, marginBottom: 12, fontFamily: "inherit" }}>At least 8 characters.</div>
          <div style={{ position: "relative", marginBottom: 16 }}>
            <input
              type={showSetupConfirm ? "text" : "password"}
              placeholder="Confirm password"
              value={passwordSetup.confirm}
              onChange={e => setPasswordSetup(p => ({ ...p, confirm: e.target.value }))}
              onKeyDown={e => e.key === "Enter" && !passwordSetup.submitting && submitPasswordSetup()}
              style={{ width: "100%", background: "#fff", border: "1px solid #d1d1d1", borderRadius: 8, padding: "12px 44px 12px 14px", color: "#111", fontSize: 14, boxSizing: "border-box", fontFamily: "'Oswald', sans-serif", outline: "none" }}
            />
            <button
              type="button"
              onClick={() => setShowSetupConfirm(v => !v)}
              style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: 4, color: "#aaa", lineHeight: 1 }}
              aria-label={showSetupConfirm ? "Hide password" : "Show password"}
            >
              {showSetupConfirm
                ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              }
            </button>
          </div>
          {passwordSetup.error && (
            <div style={{ color: "#ef4444", fontSize: 13, marginBottom: 12 }}>{passwordSetup.error}</div>
          )}
          <button
            onClick={submitPasswordSetup}
            disabled={passwordSetup.submitting}
            style={{ width: "100%", background: "#F55C00", border: "none", borderRadius: 8, padding: "13px", color: "#fff", fontWeight: 700, fontSize: 15, cursor: passwordSetup.submitting ? "default" : "pointer", opacity: passwordSetup.submitting ? 0.7 : 1, fontFamily: "'Oswald', sans-serif", letterSpacing: "0.03em" }}
          >
            {passwordSetup.submitting ? "Setting up account…" : "COMPLETE SETUP"}
          </button>
        </div>
      </div>
    </div>
  );

  if (passwordReset.active) return (
    <div style={{ minHeight: "100vh", background: "#ffffff", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Oswald', sans-serif" }}>
      <div style={{ width: "100%", maxWidth: 380, padding: "0 28px", textAlign: "center" }}>
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 52, letterSpacing: "-0.02em", lineHeight: 1, color: "#111" }}>
            TMRW<span style={{ color: "#F55C00" }}>.</span>
          </div>
          <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 16, marginTop: 6, letterSpacing: "0.01em" }}>
            <span style={{ color: "#F55C00" }}>Know Tomorrow</span><span style={{ color: "#111" }}>.</span>
          </div>
        </div>
        <div style={{ background: "#f9f9f9", border: "1px solid #e5e5e5", borderRadius: 12, padding: 28, textAlign: "left" }}>
          <input
            type="password"
            placeholder="New password"
            value={passwordReset.password}
            onChange={e => setPasswordReset(p => ({ ...p, password: e.target.value, error: "" }))}
            style={{ width: "100%", background: "#fff", border: "1px solid #d1d1d1", borderRadius: 8, padding: "12px 14px", color: "#111", fontSize: 14, marginBottom: 4, boxSizing: "border-box", fontFamily: "'Oswald', sans-serif", outline: "none" }}
          />
          <div style={{ fontSize: 12, color: "#111", marginTop: 4, marginBottom: 12, fontFamily: "inherit" }}>At least 8 characters.</div>
          <input
            type="password"
            placeholder="Confirm new password"
            value={passwordReset.confirm}
            onChange={e => setPasswordReset(p => ({ ...p, confirm: e.target.value, error: "" }))}
            onKeyDown={e => e.key === "Enter" && !passwordReset.submitting && submitPasswordReset()}
            style={{ width: "100%", background: "#fff", border: "1px solid #d1d1d1", borderRadius: 8, padding: "12px 14px", color: "#111", fontSize: 14, marginBottom: 16, boxSizing: "border-box", fontFamily: "'Oswald', sans-serif", outline: "none" }}
          />
          {passwordReset.error && <div style={{ color: "#ef4444", fontSize: 13, marginBottom: 12 }}>{passwordReset.error}</div>}
          <button
            onClick={submitPasswordReset}
            disabled={passwordReset.submitting}
            style={{ width: "100%", background: "#F55C00", border: "none", borderRadius: 8, padding: "13px", color: "#fff", fontWeight: 700, fontSize: 15, cursor: passwordReset.submitting ? "not-allowed" : "pointer", opacity: passwordReset.submitting ? 0.7 : 1, fontFamily: "'Oswald', sans-serif", letterSpacing: "0.03em" }}
          >
            {passwordReset.submitting ? "Updating…" : "Set new password"}
          </button>
        </div>
      </div>
    </div>
  );

  if (!user) {
    if (inviteCode.active) return (
      <div style={{ minHeight: "100vh", background: "#ffffff", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "'Oswald', sans-serif" }}>
        <div style={{ width: "100%", maxWidth: 380, padding: "0 28px", textAlign: "center" }}>

          {/* Logo */}
          <div style={{ marginBottom: 28 }}>
            <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 52, letterSpacing: "-0.02em", lineHeight: 1, color: "#111" }}>
              TMRW<span style={{ color: "#F55C00" }}>.</span>
            </div>
            <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 16, marginTop: 6, letterSpacing: "0.01em" }}>
              <span style={{ color: "#F55C00" }}>Know Tomorrow</span><span style={{ color: "#111" }}>.</span>
            </div>
          </div>

          <div style={{ fontSize: 18, fontWeight: 700, color: "#111", marginBottom: 8, textAlign: "center", fontFamily: "'Oswald', sans-serif" }}>Enter your invite code</div>
          <div style={{ fontSize: 13, color: "#888", marginBottom: 24, textAlign: "center", fontFamily: "'Oswald', sans-serif" }}>Check your email for your 6-digit code</div>

          <input
            placeholder="Your email address"
            value={inviteCode.email}
            onChange={e => setInviteCode(p => ({ ...p, email: e.target.value, error: "" }))}
            style={{ width: "100%", background: "#fff", border: "1px solid #E0E0E0", borderRadius: 10, padding: "13px 14px", color: "#111", fontSize: 15, marginBottom: 12, boxSizing: "border-box", outline: "none", fontFamily: "'Oswald', sans-serif" }}
          />
          <input
            placeholder="6-digit invite code"
            value={inviteCode.code}
            onChange={e => setInviteCode(p => ({ ...p, code: e.target.value.replace(/\D/g, "").slice(0, 6), error: "" }))}
            maxLength={6}
            inputMode="numeric"
            onKeyDown={e => e.key === "Enter" && !inviteCode.submitting && verifyInviteCode()}
            style={{ width: "100%", background: "#fff", border: "1px solid #E0E0E0", borderRadius: 10, padding: "13px 14px", color: "#111", fontSize: 15, marginBottom: 12, boxSizing: "border-box", outline: "none", fontFamily: "'Oswald', sans-serif", letterSpacing: 4, textAlign: "center" }}
          />
          {inviteCode.error && (
            <div style={{ color: "#E53935", fontSize: 13, marginBottom: 12, fontWeight: 500, fontFamily: "'Oswald', sans-serif" }}>{inviteCode.error}</div>
          )}
          <button
            onClick={verifyInviteCode}
            disabled={inviteCode.submitting}
            style={{ width: "100%", background: "#F55C00", border: "none", borderRadius: 10, padding: "14px", color: "#fff", fontWeight: 700, fontSize: 15, cursor: inviteCode.submitting ? "not-allowed" : "pointer", opacity: inviteCode.submitting ? 0.7 : 1, letterSpacing: "0.03em", fontFamily: "'Oswald', sans-serif", marginBottom: 12 }}
          >
            {inviteCode.submitting ? "Verifying…" : "Submit Code"}
          </button>
          <button
            onClick={() => setInviteCode({ active: false, email: "", code: "", error: "", submitting: false })}
            style={{ background: "none", border: "none", color: "#111", fontSize: 12, cursor: "pointer", padding: 0, fontFamily: "'Oswald', sans-serif", fontWeight: 500 }}
          >
            ← Back to Sign In
          </button>

        </div>
      </div>
    );

    return (
      <>
      <div style={{ minHeight: "100vh", background: "#ffffff", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "'Oswald', sans-serif" }}>
        <div style={{ width: "100%", maxWidth: 380, padding: "0 28px", textAlign: "center" }}>

          {/* Logo */}
          <div style={{ marginBottom: 28 }}>
            <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 52, letterSpacing: "-0.02em", lineHeight: 1, color: "#111" }}>
              TMRW<span style={{ color: "#F55C00" }}>.</span>
            </div>
            <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 16, marginTop: 6, letterSpacing: "0.01em" }}>
              <span style={{ color: "#F55C00" }}>Know Tomorrow</span><span style={{ color: "#111" }}>.</span>
            </div>
          </div>

          {/* Form area */}
          {forgotPassword.active ? (
            forgotPassword.sent ? (
              <>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#111", marginBottom: 8, textAlign: "center", fontFamily: "'Oswald', sans-serif" }}>Enter your reset code</div>
                <div style={{ fontSize: 13, color: "#888", marginBottom: 24, textAlign: "center", fontFamily: "'Oswald', sans-serif" }}>We sent a 6-digit code to {resetCodeStep.email}</div>
                <input
                  placeholder="Your email address"
                  value={resetCodeStep.email}
                  onChange={e => setResetCodeStep(p => ({ ...p, email: e.target.value, error: "" }))}
                  style={{ width: "100%", background: "#fff", border: "1px solid #E0E0E0", borderRadius: 10, padding: "13px 14px", color: "#111", fontSize: 15, marginBottom: 12, boxSizing: "border-box", outline: "none", fontFamily: "'Oswald', sans-serif" }}
                />
                <input
                  placeholder="6-digit reset code"
                  value={resetCodeStep.code}
                  onChange={e => setResetCodeStep(p => ({ ...p, code: e.target.value.replace(/\D/g, "").slice(0, 6), error: "" }))}
                  maxLength={6}
                  inputMode="numeric"
                  onKeyDown={e => e.key === "Enter" && !resetCodeStep.submitting && verifyResetCode()}
                  style={{ width: "100%", background: "#fff", border: "1px solid #E0E0E0", borderRadius: 10, padding: "13px 14px", color: "#111", fontSize: 15, marginBottom: 12, boxSizing: "border-box", outline: "none", fontFamily: "'Oswald', sans-serif", letterSpacing: 4, textAlign: "center" }}
                />
                {resetCodeStep.error && (
                  <div style={{ color: "#E53935", fontSize: 13, marginBottom: 12, fontWeight: 500, fontFamily: "'Oswald', sans-serif" }}>{resetCodeStep.error}</div>
                )}
                <button
                  onClick={verifyResetCode}
                  disabled={resetCodeStep.submitting}
                  style={{ width: "100%", background: "#F55C00", border: "none", borderRadius: 10, padding: "14px", color: "#fff", fontWeight: 700, fontSize: 15, cursor: resetCodeStep.submitting ? "not-allowed" : "pointer", opacity: resetCodeStep.submitting ? 0.7 : 1, letterSpacing: "0.03em", fontFamily: "'Oswald', sans-serif", marginBottom: 12 }}
                >
                  {resetCodeStep.submitting ? "Verifying…" : "Submit Code"}
                </button>
                <button
                  onClick={() => { setForgotPassword({ active: false, email: "", submitting: false, sent: false, error: "" }); setResetCodeStep({ active: false, email: "", code: "", error: "", submitting: false }); }}
                  style={{ background: "none", border: "none", color: "#111", fontSize: 12, cursor: "pointer", padding: 0, fontFamily: "'Oswald', sans-serif", fontWeight: 500 }}
                >
                  ← Back to Sign In
                </button>
              </>
            ) : (
              <>
                <input
                  placeholder="Your email address"
                  value={forgotPassword.email}
                  onChange={e => setForgotPassword(p => ({ ...p, email: e.target.value, error: "" }))}
                  onKeyDown={e => e.key === "Enter" && !forgotPassword.submitting && submitForgotPassword()}
                  style={{ width: "100%", background: "#fff", border: "1px solid #E0E0E0", borderRadius: 10, padding: "13px 14px", color: "#111", fontSize: 15, marginBottom: 12, boxSizing: "border-box", outline: "none", fontFamily: "'Oswald', sans-serif" }}
                />
                {forgotPassword.error && <div style={{ color: "#E53935", fontSize: 13, marginBottom: 12, fontFamily: "'Oswald', sans-serif" }}>{forgotPassword.error}</div>}
                <button
                  onClick={submitForgotPassword}
                  disabled={forgotPassword.submitting}
                  style={{ width: "100%", background: "#F55C00", border: "none", borderRadius: 10, padding: "14px", color: "#fff", fontWeight: 700, fontSize: 15, cursor: forgotPassword.submitting ? "not-allowed" : "pointer", opacity: forgotPassword.submitting ? 0.7 : 1, marginBottom: 16, fontFamily: "'Oswald', sans-serif", letterSpacing: "0.03em" }}
                >
                  {forgotPassword.submitting ? "Sending…" : "Send reset code"}
                </button>
                <div>
                  <button onClick={() => setForgotPassword({ active: false, email: "", submitting: false, sent: false, error: "" })} style={{ background: "none", border: "none", color: "#F55C00", fontSize: 13, cursor: "pointer", padding: 0, fontFamily: "'Oswald', sans-serif", fontWeight: 500 }}>← Back to sign in</button>
                </div>
              </>
            )
          ) : (
            <>
              {loginData.successMsg && <div style={{ color: "#2E7D32", fontSize: 13, marginBottom: 16, fontWeight: 500, fontFamily: "'Oswald', sans-serif" }}>{loginData.successMsg}</div>}
              <input
                placeholder="Email"
                value={loginData.email}
                onChange={e => setLoginData(p => ({ ...p, email: e.target.value }))}
                onKeyDown={e => e.key === "Enter" && login()}
                style={{ width: "100%", background: "#fff", border: "1px solid #E0E0E0", borderRadius: 10, padding: "13px 14px", color: "#111", fontSize: 15, marginBottom: 12, boxSizing: "border-box", outline: "none", fontFamily: "'Oswald', sans-serif" }}
              />
              <div style={{ position: "relative", marginBottom: 8 }}>
                <input
                  type={showLoginPassword ? "text" : "password"}
                  placeholder="Password"
                  value={loginData.password}
                  onChange={e => setLoginData(p => ({ ...p, password: e.target.value }))}
                  onKeyDown={e => e.key === "Enter" && login()}
                  style={{ width: "100%", background: "#fff", border: "1px solid #E0E0E0", borderRadius: 10, padding: "13px 44px 13px 14px", color: "#111", fontSize: 15, boxSizing: "border-box", outline: "none", fontFamily: "'Oswald', sans-serif" }}
                />
                <button
                  type="button"
                  onClick={() => setShowLoginPassword(v => !v)}
                  style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: 4, color: "#aaa", lineHeight: 1 }}
                  aria-label={showLoginPassword ? "Hide password" : "Show password"}
                >
                  {showLoginPassword
                    ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  }
                </button>
              </div>
              <div style={{ marginBottom: 20 }}>
                <button onClick={() => setForgotPassword(p => ({ ...p, active: true }))} style={{ background: "none", border: "none", color: "#111", fontSize: 14, cursor: "pointer", padding: 0, fontFamily: "'Oswald', sans-serif", fontWeight: 600 }}>Forgot password?</button>
              </div>
              {loginData.error && <div style={{ color: "#E53935", fontSize: 13, marginBottom: 14, fontWeight: 500, fontFamily: "'Oswald', sans-serif" }}>{loginData.error}</div>}
              <button
                onClick={login}
                disabled={loginData.submitting}
                style={{ width: "100%", background: "#F55C00", border: "none", borderRadius: 10, padding: "14px", color: "#fff", fontWeight: 700, fontSize: 15, cursor: loginData.submitting ? "not-allowed" : "pointer", opacity: loginData.submitting ? 0.7 : 1, letterSpacing: "0.03em", fontFamily: "'Oswald', sans-serif" }}
              >
                {loginData.submitting ? "Signing in…" : "Sign In"}
              </button>
              <button
                onClick={() => setInviteCode(p => ({ ...p, active: true }))}
                style={{ background: "none", border: "none", color: "#111", fontSize: 14, cursor: "pointer", padding: "12px 0 0", fontFamily: "'Oswald', sans-serif", fontWeight: 600, display: "block", width: "100%", textAlign: "center" }}
              >
                Have an invite code?
              </button>

            </>
          )}

        </div>
      </div>

      </>
    );
  }

  if (!dataLoaded) {
    return (
      <div style={{ minHeight: "100vh", background: (user.role !== "manager" && !user.is_admin) ? "#fff" : "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 32, color: (user.role !== "manager" && !user.is_admin) ? "#111" : "#fff", letterSpacing: "-0.02em" }}>
            TMRW<span style={{ color: "#F55C00" }}>.</span>
          </div>
          <div style={{ marginTop: 16 }}>
            <div style={{ width: 24, height: 24, border: "2px solid #333", borderTopColor: "#F55C00", borderRadius: "50%", animation: "ptr-spin 0.6s linear infinite", margin: "0 auto" }} />
          </div>
        </div>
        <style>{`@keyframes ptr-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const navItems = user.role === "manager" || user.is_admin
    ? [
        { id: "today", label: "Home" },
        { id: "schedule", label: "Schedule" },
        { id: "events", label: "Events" },
        { id: "requests", label: `Requests${pendingRequests.length ? ` (${pendingRequests.length})` : ""}` },
      ]
    : [
        { id: "today", label: "Home" },
        { id: "schedule", label: "Schedule" },
        { id: "requests", label: `Requests${empPendingCount ? ` (${empPendingCount})` : ""}` },
      ];

  return (
    <div style={{ minHeight: "100vh", background: (user.role !== "manager" && !user.is_admin) ? "#fff" : "#0a0a0a", fontFamily: "'Georgia', serif", color: (user.role !== "manager" && !user.is_admin) ? "#111" : "#fff" }}>
      <style>{`@keyframes ptr-spin { to { transform: rotate(360deg); } } @keyframes emp-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }`}</style>

      {toast && (
        <div style={{
          position: "fixed", bottom: 80, left: "50%", transform: "translateX(-50%)",
          background: toast.type === "error" ? "#ef4444" : toast.type === "info" ? "#3b82f6" : "#f97316",
          color: "#000", padding: "10px 18px", borderRadius: 8, fontWeight: 600, fontSize: 14,
          zIndex: 9999, boxShadow: "0 4px 20px rgba(0,0,0,0.5)", maxWidth: 280, textAlign: "center",
        }}>
          {toast.msg}
        </div>
      )}

      {timeOffConflictModal && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 24px" }}
          onClick={() => setTimeOffConflictModal(null)}
        >
          <div
            style={{ background: "white", borderRadius: 16, padding: "28px 24px", width: "100%", maxWidth: 340 }}
            onClick={e => e.stopPropagation()}
          >
            <p style={{ fontSize: 16, fontWeight: 700, color: "#111", marginBottom: 8, lineHeight: 1.4 }}>
              {formatName(timeOffConflictModal.req.requester_name, null, user.role)} is scheduled on:
            </p>
            <div style={{ marginBottom: 20 }}>
              {timeOffConflictModal.conflictingShifts.map(s => (
                <div key={s.id} style={{ fontSize: 13, color: "#F55C00", fontWeight: 600, marginBottom: 4 }}>
                  {fmtDate(s.date)} · {fmt(s.start_time)} – {fmt(s.end_time)}
                </div>
              ))}
            </div>
            <p style={{ fontSize: 13, color: "#111", marginBottom: 24 }}>Remove them from the schedule and approve this time off?</p>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => setTimeOffConflictModal(null)}
                style={{ flex: 1, background: "white", color: "#111", fontSize: 13, fontWeight: 700, borderRadius: 20, padding: "10px", border: "1.5px solid #111", cursor: "pointer", fontFamily: "inherit" }}
              >CANCEL</button>
              <button
                onClick={async () => {
                  const { id, req, conflictingShifts } = timeOffConflictModal;
                  setTimeOffConflictModal(null);
                  setProcessingId(id);
                  const shiftIds = conflictingShifts.map(s => s.id);
                  await supabase.from("shifts").delete().in("id", shiftIds);
                  setShifts(prev => prev.filter(s => !shiftIds.includes(s.id)));
                  await supabase.from("requests").update({ status: "approved" }).eq("id", id);
                  setRequests(prev => prev.map(r => r.id === id ? { ...r, status: "approved", updated_at: new Date().toISOString() } : r));
                  showToast(`Approved — ${conflictingShifts.length} shift${conflictingShifts.length > 1 ? "s" : ""} removed ✓`);
                  setProcessingId(null);
                }}
                style={{ flex: 1, background: "#2E7D32", color: "white", fontSize: 13, fontWeight: 700, borderRadius: 20, padding: "10px", border: "none", cursor: "pointer", fontFamily: "inherit" }}
              >REMOVE & APPROVE</button>
            </div>
          </div>
        </div>
      )}
      {confirmDialog && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={() => setConfirmDialog(null)}>
          <div style={{ background: "#161616", border: "1px solid #2a2a2a", borderRadius: 14, padding: 28, maxWidth: 340, width: "100%", boxSizing: "border-box" }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 600, color: "#fff", marginBottom: 22, lineHeight: 1.5, whiteSpace: "pre-line" }}>{confirmDialog.message}</div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => { confirmDialog.onConfirm(); setConfirmDialog(null); }} style={{ flex: 1, background: confirmDialog.confirmColor || "#ef4444", border: "none", borderRadius: 8, padding: "12px", color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>{confirmDialog.confirmLabel || "Confirm"}</button>
              <button onClick={() => setConfirmDialog(null)} style={{ flex: 1, background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 8, padding: "12px", color: "#666", fontSize: 14, cursor: "pointer" }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {alertDialog && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={() => setAlertDialog(null)}>
          <div style={{ background: "#161616", border: "1px solid #2a2a2a", borderRadius: 14, padding: 28, maxWidth: 340, width: "100%", boxSizing: "border-box" }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 600, color: "#fff", marginBottom: 22, lineHeight: 1.5 }}>{alertDialog}</div>
            <button onClick={() => setAlertDialog(null)} style={{ width: "100%", background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 8, padding: "12px", color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>OK</button>
          </div>
        </div>
      )}

      {/* Announcements panel overlay */}
      {showAnnouncementsPanel && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 500, display: "flex", alignItems: "flex-end" }} onClick={() => setShowAnnouncementsPanel(false)}>
          <div style={{ background: "#141414", border: "1px solid #2a2a2a", borderRadius: "16px 16px 0 0", padding: 24, width: "100%", maxWidth: 640, margin: "0 auto", boxSizing: "border-box", maxHeight: "80vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div style={{ fontSize: 17, fontWeight: 700 }}>Announcements</div>
              <button onClick={() => setShowAnnouncementsPanel(false)} style={{ background: "none", border: "1px solid #2a2a2a", borderRadius: 8, padding: "5px 12px", color: "#666", fontSize: 12, cursor: "pointer" }}>Close</button>
            </div>
            {user.role === "manager" && (
              <div style={{ background: "#111", border: "1px solid #222", borderRadius: 12, padding: 18, marginBottom: 20 }}>
                <div style={{ fontSize: 13, color: "#f97316", fontWeight: 600, marginBottom: 12 }}>New Announcement</div>
                <textarea placeholder="Write a message for all staff..." value={newAnnouncement} onChange={e => setNewAnnouncement(e.target.value)} rows={3} style={{ ...inputStyle, resize: "none", lineHeight: 1.6 }} />
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: pinNew ? "#f97316" : "#555" }}>
                    <input type="checkbox" checked={pinNew} onChange={e => setPinNew(e.target.checked)} style={{ accentColor: "#f97316", width: 15, height: 15 }} />
                    Pin to Today
                  </label>
                  <button onClick={postAnnouncement} style={{ background: "#f97316", border: "none", borderRadius: 8, padding: "8px 18px", color: "#000", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Post</button>
                </div>
              </div>
            )}
            {sortedAnnouncements.length === 0 && <div style={{ color: "#444", textAlign: "center", padding: 32 }}>No announcements yet.</div>}
            {sortedAnnouncements.map(a => (
              <div key={a.id} style={{ background: "#111", border: `1px solid ${a.pinned ? "#f9731644" : "#1e1e1e"}`, borderRadius: 10, padding: "14px 16px", marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    {a.pinned && <div style={{ fontSize: 11, color: "#f97316", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 5 }}>📌 Pinned</div>}
                    <div style={{ fontSize: 14, color: "#ddd", lineHeight: 1.6 }}>{a.text}</div>
                    <div style={{ fontSize: 11, color: "#444", marginTop: 6 }}>{(() => { const d = new Date(a.created_at); const datePart = d.toLocaleDateString("en-US", { month: "short", day: "numeric" }); const timePart = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }); return `${datePart} · ${timePart}${a.author_name ? ` · Posted by ${formatName(a.author_name, null, user.role)}` : ""}`; })()}</div>
                  </div>
                  {user.role === "manager" && (
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      <button onClick={() => togglePin(a.id, a.pinned)} disabled={pinningId === a.id} style={{ background: "none", border: "1px solid #2a2a2a", borderRadius: 6, padding: "4px 8px", color: pinningId === a.id ? "#444" : a.pinned ? "#f97316" : "#555", fontSize: 11, cursor: pinningId === a.id ? "default" : "pointer" }}>{pinningId === a.id ? "…" : a.pinned ? "Unpin" : "Pin"}</button>
                      <button onClick={() => deleteAnnouncement(a.id)} style={{ background: "none", border: "1px solid #2a2a2a", borderRadius: 6, padding: "4px 8px", color: "#555", fontSize: 11, cursor: "pointer" }}>Delete</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Header (employees) */}
      {user.role !== "manager" && (
        <div style={{ background: "#fff", borderBottom: "1px solid #EFEFEF", position: "sticky", top: 0, zIndex: 100, paddingTop: "env(safe-area-inset-top, 20px)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px 12px" }}>
            <div>
              <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 26, lineHeight: 1, color: "#111", letterSpacing: "-0.01em" }}>
                TMRW<span style={{ color: "#F55C00" }}>.</span>
              </div>
              <div style={{ fontSize: 12, color: "#F55C00", fontWeight: 600, marginTop: 2 }}>
                Hi {user.first_name || user.name?.split(" ")[0] || "there"}!
              </div>
            </div>
            <button
              onClick={() => { setEmpDrawerOpen(true); setTimeout(() => setEmpDrawerVisible(true), 10); }}
              style={{ background: "none", border: "none", cursor: "pointer", padding: 8, display: "flex", flexDirection: "column", gap: 5, WebkitTapHighlightColor: "transparent" }}
              aria-label="Menu"
            >
              {[0,1,2].map(i => <span key={i} style={{ display: "block", width: 20, height: 2, background: "#111", borderRadius: 2 }} />)}
            </button>
          </div>
        </div>
      )}


      {/* Pull-to-refresh indicator */}
      {(pullY > 0 || refreshing) && (
        <div style={user.role !== "manager" ? {
          position: "fixed", top: "env(safe-area-inset-top, 0px)", left: 0, right: 0, zIndex: 999,
          display: "flex", justifyContent: "center", alignItems: "center",
          height: refreshing ? 44 : Math.round(pullY * 0.7),
          background: "#fff",
          transition: refreshing ? "height 0.15s ease" : "none",
        } : {
          position: "fixed",
          top: "env(safe-area-inset-top, 0px)",
          left: 0,
          right: 0,
          zIndex: 999,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: refreshing ? 44 : Math.round(pullY * 0.7),
          background: "#0a0a0a",
          transition: refreshing ? "height 0.15s ease" : "none",
          overflow: "hidden",
        }}>
          <div style={{
            width: 22, height: 22,
            border: user.role !== "manager" ? "2px solid #e0e0e0" : "2px solid #222",
            borderTopColor: "#f97316",
            borderRadius: "50%",
            opacity: refreshing ? 1 : Math.min(pullY / 60, 1),
            animation: refreshing ? "ptr-spin 0.7s linear infinite" : "none",
            transform: refreshing ? "none" : `rotate(${Math.round(pullY * 5)}deg)`,
          }} />
        </div>
      )}

      <div style={{ padding: "20px 16px", maxWidth: user.role === "manager" ? "none" : 640, margin: "0 auto", boxSizing: "border-box" }}>

        {/* Manager day-view bottom sheet — shared across Schedule and Team Calendar */}
        {user.role === "manager" && selectedDay && (
          <div
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 200, display: "flex", alignItems: "flex-end" }}
            onClick={() => setSelectedDay(null)}
          >
            <div
              style={{ background: "#161616", border: "1px solid #2a2a2a", borderRadius: "16px 16px 0 0", padding: 24, width: "100%", maxWidth: 640, margin: "0 auto", boxSizing: "border-box", maxHeight: "70vh", overflowY: "auto" }}
              onClick={e => e.stopPropagation()}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 11, color: "#555", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 2 }}>Shifts</div>
                  <div style={{ fontSize: 17, fontWeight: 700 }}>{fmtDate(selectedDay)}</div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  {selectedDay >= getToday() && scheduleSubview !== "calendar" && (
                    <button
                      onClick={() => { setNewShift(p => ({ ...p, date: selectedDay })); setShowAddShift(true); setAddShiftWarning(""); setSelectedDay(null); setView("schedule"); }}
                      style={{ background: "#f97316", border: "none", borderRadius: 8, padding: "8px 14px", color: "#000", fontWeight: 700, fontSize: 12, cursor: "pointer" }}
                    >+ Add</button>
                  )}
                  <button onClick={() => setSelectedDay(null)} style={{ background: "none", border: "1px solid #2a2a2a", borderRadius: 8, padding: "8px 14px", color: "#666", fontSize: 12, cursor: "pointer" }}>Close</button>
                </div>
              </div>
              {dayViewShifts.length === 0 && approvedTimeOff.filter(r => selectedDay >= r.date && selectedDay <= (r.end_date || r.date)).length === 0 && (
                <div style={{ color: "#444", fontSize: 14, textAlign: "center", padding: "20px 0" }}>No shifts scheduled.</div>
              )}
              {(() => {
                const ShiftCard = s => {
                  const color = employeeColor(s.employee_id);
                  const isMgr = allProfiles.find(p => p.id === s.employee_id)?.role === "manager";
                  return (
                    <div key={s.id} style={{ background: "#111", border: "1px solid #1e1e1e", borderRadius: 10, padding: "12px 14px", marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 15, color: "#fff" }}>{formatName(s.employee_name, null, user.role)}{isMgr ? " (Manager)" : ""}</div>
                        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 3 }}>
                          <div style={{ fontSize: 11, background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 4, padding: "1px 6px", color: "#888" }}>{s.location || defaultLoc}</div>
                        </div>
                        <div style={{ color: "#888", fontSize: 13, marginTop: 3 }}>{fmt(s.start_time)} – {fmt(s.end_time)}</div>
                        {s.note && <div style={{ color: "#aaa", fontSize: 12, marginTop: 3 }}>{s.note}</div>}
                      </div>
                      {selectedDay >= getToday() && scheduleSubview !== "calendar" && (
                        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                          <button onClick={() => {
                            setNewShift({ employeeId: s.employee_id, date: s.date, start: s.start_time.slice(0, 5), end: s.end_time.slice(0, 5), note: s.note || "", location: s.location || defaultLoc });
                            setEditingShiftId(s.id);
                            setShowAddShift(true);
                            setAddShiftWarning("");
                            setSelectedDay(null);
                            setView("schedule");
                          }} style={{ background: "none", border: "1px solid #2a2a2a", borderRadius: 6, padding: "5px 10px", color: "#aaa", fontSize: 12, cursor: "pointer" }}>Edit</button>
                          <button onClick={() => confirmDeleteShift(s)} style={{ background: "none", border: "1px solid #2a2a2a", borderRadius: 6, padding: "5px 10px", color: "#555", fontSize: 12, cursor: "pointer" }}>Remove</button>
                        </div>
                      )}
                    </div>
                  );
                };
                if (view === "schedule" && scheduleSubview === "calendar" && locationFilter === "All" && dayViewShifts.length > 0 && allLocations.length > 1) {
                  const locGroups = allLocations.map(l => ({ name: l.name, shifts: dayViewShifts.filter(s => (s.location || allLocations[0]?.name) === l.name) })).filter(g => g.shifts.length > 0);
                  if (locGroups.length > 0) return (
                    <>
                      {locGroups.map((g, i) => (
                        <div key={g.name}>
                          <div style={{ fontSize: 10, color: "#555", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 8, marginTop: i > 0 ? 12 : 0 }}>{g.name}</div>
                          {g.shifts.map(ShiftCard)}
                        </div>
                      ))}
                    </>
                  );
                }
                return dayViewShifts.map(ShiftCard);
              })()}
              {approvedTimeOff.filter(r => selectedDay >= r.date && selectedDay <= (r.end_date || r.date)).map(r => (
                <div key={`off-${r.id}`} style={{ background: "#1a0f00", border: "1px solid #7c3a00", borderRadius: 10, padding: "12px 14px", marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: "#f97316" }}>{formatName(r.requester_name, null, user.role)} — Time Off</div>
                    <div style={{ color: "#888", fontSize: 12, marginTop: 2 }}>{fmtDateRange(r.date, r.end_date)}</div>
                  </div>
                  <button onClick={() => setConfirmDialog({ message: "Are you sure you want to revoke this time off?", onConfirm: () => { revokeTimeOff(r.id); setSelectedDay(null); } })} style={{ background: "none", border: "1px solid #7c3a00", borderRadius: 6, padding: "5px 10px", color: "#f97316", fontSize: 12, cursor: "pointer", flexShrink: 0 }}>Revoke</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* HOME */}
        {view === "today" && (() => {
          if (user.role === "manager" || user.is_admin) {
            return (
              <div>
                <ManagerLineup
                  user={user}
                  shifts={shifts}
                  events={events}
                  eventStaff={eventStaff}
                  allProfiles={allProfiles}
                  pendingProfiles={pendingProfiles}
                  requests={requests}
                  currentLocation={homeLocFilter}
                  onLocationChange={setHomeLocFilter}
                  onShiftAdded={loadData}
                  onHandleRequest={handleRequest}
                  allLocations={allLocations}
                  onInvite={openInviteSheet}
                  onEditLocations={openLocationEdit}
                  onEditName={(p) => setEditNameSheet({ open: true, visible: false, profileId: p.id, firstName: p.first_name || "", lastName: p.last_name || "" })}
                  onChangeRole={changeRole}
                  onRemove={(p) => setRemoveStaffTarget(p)}
                  onResendInvite={resendInvite}
                />

                {removeStaffTarget && ReactDOM.createPortal(
                  <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 24px" }}
                    onClick={() => setRemoveStaffTarget(null)}>
                    <div style={{ background: "white", borderRadius: 16, padding: "28px 24px", width: "100%", maxWidth: 340 }}
                      onClick={e => e.stopPropagation()}>
                      <p style={{ fontSize: 16, fontWeight: 700, color: "#111", marginBottom: 8, lineHeight: 1.4, fontFamily: "'Oswald', sans-serif" }}>
                        REMOVE {(removeStaffTarget.first_name && removeStaffTarget.last_name ? `${removeStaffTarget.first_name} ${removeStaffTarget.last_name}` : removeStaffTarget.name || "").toUpperCase()}?
                      </p>
                      <p style={{ fontSize: 13, color: "#111", marginBottom: 24, lineHeight: 1.4 }}>
                        They will lose access to TMRW immediately.
                      </p>
                      <div style={{ display: "flex", gap: 10 }}>
                        <button
                          onClick={() => setRemoveStaffTarget(null)}
                          style={{ flex: 1, background: "white", color: "#111", fontSize: 13, fontWeight: 700, borderRadius: 20, padding: "10px", border: "1.5px solid #111", cursor: "pointer", fontFamily: "inherit" }}>
                          CANCEL
                        </button>
                        <button
                          onClick={async () => {
                            await supabase.from("profiles").update({ active: false, removed: true }).eq("id", removeStaffTarget.id);
                            setRemoveStaffTarget(null);
                            loadData();
                          }}
                          style={{ flex: 1, background: "#E53935", color: "white", fontSize: 13, fontWeight: 700, borderRadius: 20, padding: "10px", border: "none", cursor: "pointer", fontFamily: "inherit" }}>
                          REMOVE
                        </button>
                      </div>
                    </div>
                  </div>,
                  document.body
                )}

                {editNameSheet.open && ReactDOM.createPortal(
                  <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 2000, display: "flex", alignItems: "flex-end" }} onClick={() => setEditNameSheet(s => ({ ...s, open: false }))}>
                    <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: "20px 20px 0 0", padding: "28px 24px calc(40px + env(safe-area-inset-bottom, 34px))", width: "100%", boxSizing: "border-box" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
                        <span style={{ fontSize: 17, fontWeight: 700, color: "#111" }}>Edit Name</span>
                        <button type="button" onClick={() => setEditNameSheet({ open: false, visible: false, profileId: null, firstName: "", lastName: "" })} style={{ background: "none", border: "none", fontSize: 15, color: "#888", cursor: "pointer", fontFamily: "inherit", fontWeight: 500 }}>Cancel</button>
                      </div>
                      <input
                        placeholder="First name"
                        value={editNameSheet.firstName}
                        onChange={e => setEditNameSheet(s => ({ ...s, firstName: e.target.value }))}
                        style={{ width: "100%", border: "1px solid #E0E0E0", borderRadius: 10, padding: "13px 14px", fontSize: 15, marginBottom: 12, boxSizing: "border-box", outline: "none", fontFamily: "inherit" }}
                      />
                      <input
                        placeholder="Last name"
                        value={editNameSheet.lastName}
                        onChange={e => setEditNameSheet(s => ({ ...s, lastName: e.target.value }))}
                        style={{ width: "100%", border: "1px solid #E0E0E0", borderRadius: 10, padding: "13px 14px", fontSize: 15, marginBottom: 20, boxSizing: "border-box", outline: "none", fontFamily: "inherit" }}
                      />
                      <button
                        type="button"
                        onClick={async () => {
                          if (!editNameSheet.firstName.trim() || !editNameSheet.lastName.trim()) return;
                          const fullName = `${editNameSheet.firstName.trim()} ${editNameSheet.lastName.trim()}`;
                          await supabase.from("profiles").update({ first_name: editNameSheet.firstName.trim(), last_name: editNameSheet.lastName.trim(), name: fullName }).eq("id", editNameSheet.profileId);
                          setEditNameSheet({ open: false, visible: false, profileId: null, firstName: "", lastName: "" });
                          clearTimeout(inviteToastTimer.current);
                          setInviteToast("Name updated.");
                          inviteToastTimer.current = setTimeout(() => setInviteToast(""), 4000);
                          loadData();
                        }}
                        style={{ width: "100%", background: "#F55C00", color: "#fff", border: "none", borderRadius: 14, padding: "16px", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
                      >Save</button>
                    </div>
                  </div>,
                  document.body
                )}

                {/* ── Invite Staff bottom sheet ── */}
                {inviteSheet.open && ReactDOM.createPortal(
                  <div
                    style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 2000 }}
                    onMouseDown={(e) => { if (e.target === e.currentTarget) closeInviteSheet(); }}
                    onTouchStart={(e) => { if (e.target === e.currentTarget) closeInviteSheet(); }}
                  >
                    <div
                      onMouseDown={(e) => e.stopPropagation()}
                      onTouchStart={(e) => e.stopPropagation()}
                      style={{
                        position: "fixed", bottom: 0, left: 0, right: 0,
                        background: "white", borderRadius: "20px 20px 0 0",
                        maxHeight: "60vh", display: "flex", flexDirection: "column",
                        transform: inviteSheet.visible ? "translateY(0)" : "translateY(100%)",
                        transition: "transform 300ms ease", zIndex: 2001,
                      }}
                    >
                      <div style={{ width: 36, height: 4, background: "#E0E0E0", borderRadius: 2, margin: "10px auto 4px", flexShrink: 0 }} />
                      <div style={{ padding: "10px 20px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #EFEFEF", flexShrink: 0 }}>
                        <span style={{ fontSize: 17, fontWeight: 700, color: "#F55C00" }}>{inviteSheet.adminInvite ? "Invite Admin" : "Invite Staff"}</span>
                        <button type="button" onClick={closeInviteSheet} style={{ background: "none", border: "none", fontSize: 15, color: "#888", cursor: "pointer", fontFamily: "inherit", fontWeight: 500 }}>Cancel</button>
                      </div>
                      <div style={{ overflowY: "auto", flex: 1, padding: "20px 20px 48px", WebkitOverflowScrolling: "touch" }}>

                        {/* Admin note */}
                        {inviteSheet.adminInvite && (
                          <p style={{ fontSize: 12, color: "#F55C00", fontWeight: 600, margin: "0 0 16px 0", lineHeight: 1.4 }}>This person will have full admin access.</p>
                        )}

                        {/* Name row */}
                        <div style={{ marginBottom: 20 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Name</div>
                          <div style={{ display: "flex", gap: 10 }}>
                            <input
                              type="text"
                              placeholder="First name"
                              value={inviteSheet.firstName}
                              onChange={e => setInviteSheet(s => ({ ...s, firstName: e.target.value }))}
                              style={{ flex: 1, background: "#f9f9f9", border: "1px solid #E0E0E0", color: "#111", borderRadius: 8, padding: "10px 12px", fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }}
                            />
                            <input
                              type="text"
                              placeholder="Last name"
                              value={inviteSheet.lastName}
                              onChange={e => setInviteSheet(s => ({ ...s, lastName: e.target.value }))}
                              style={{ flex: 1, background: "#f9f9f9", border: "1px solid #E0E0E0", color: "#111", borderRadius: 8, padding: "10px 12px", fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }}
                            />
                          </div>
                        </div>

                        {/* Email */}
                        <div style={{ marginBottom: 20 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Email</div>
                          <input
                            type="email"
                            placeholder="staff@example.com"
                            value={inviteSheet.email}
                            onChange={e => setInviteSheet(s => ({ ...s, email: e.target.value }))}
                            style={{ width: "100%", background: "#f9f9f9", border: "1px solid #E0E0E0", color: "#111", borderRadius: 8, padding: "10px 12px", fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }}
                          />
                        </div>

                        {/* Role */}
                        {!inviteSheet.adminInvite && (
                          <div style={{ marginBottom: 28 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Role</div>
                            <div style={{ display: "flex", background: "#f4f4f4", borderRadius: 10, padding: 3, gap: 2 }}>
                              {(user?.is_admin === true ? ["Manager", "Lead", "Assistant"] : ["Lead", "Assistant"]).map(r => (
                                <button
                                  key={r}
                                  type="button"
                                  onClick={() => setInviteSheet(s => ({ ...s, role: r }))}
                                  style={{
                                    flex: 1, padding: "8px 0", border: "none", borderRadius: 8,
                                    background: inviteSheet.role === r ? "#F55C00" : "transparent",
                                    color: inviteSheet.role === r ? "#ffffff" : "#555",
                                    fontSize: 13, fontWeight: 700, cursor: "pointer",
                                    fontFamily: "inherit", transition: "all 0.15s",
                                  }}
                                >{r}</button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Location */}
                        {!inviteSheet.adminInvite && (() => {
                          const toggleLocation = (loc) => {
                            setInviteSheet(s => {
                              if (s.locations.includes(loc)) {
                                if (s.locations.length === 1) return s;
                                return { ...s, locations: s.locations.filter(l => l !== loc) };
                              }
                              return { ...s, locations: [...s.locations, loc] };
                            });
                          };
                          return (
                            <div style={{ marginBottom: 20 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10, marginTop: 20 }}>Location</div>
                              {(user?.is_admin === true ? allLocations.map(l => l.name) : allLocations.map(l => l.name).filter(n => user?.locations?.includes(n))).map(loc => (
                                <div key={loc} style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: 12, marginBottom: 12, borderBottom: "0.5px solid #EFEFEF" }}>
                                  <input
                                    type="checkbox"
                                    checked={inviteSheet.locations.includes(loc)}
                                    onChange={() => toggleLocation(loc)}
                                    style={{ accentColor: "#F55C00", width: 16, height: 16 }}
                                  />
                                  <span style={{ fontSize: 14, color: "#111", fontWeight: 500 }}>{loc}</span>
                                </div>
                              ))}
                            </div>
                          );
                        })()}

                        {inviteSheet.error && (
                          <p style={{ color: "#E53935", fontSize: 13, marginBottom: 12 }}>{inviteSheet.error}</p>
                        )}

                        <button
                          type="button"
                          onClick={submitInvite}
                          disabled={inviteSheet.submitting}
                          style={{ width: "100%", background: "#F55C00", color: "#fff", border: "none", borderRadius: 14, padding: "16px", fontSize: 15, fontWeight: 700, cursor: inviteSheet.submitting ? "default" : "pointer", opacity: inviteSheet.submitting ? 0.7 : 1, fontFamily: "inherit" }}
                        >
                          {inviteSheet.submitting ? "Sending…" : "Send Invite"}
                        </button>

                      </div>
                    </div>
                  </div>,
                  document.body
                )}

                {/* ── Invite toast ── */}
                {inviteToast && ReactDOM.createPortal(
                  <div style={{
                    position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
                    background: "white", border: "0.5px solid #E0E0E0", borderLeft: "3.5px solid #F55C00",
                    borderRadius: 14, padding: "14px 18px", zIndex: 9999,
                    maxWidth: 360, width: "calc(100vw - 48px)",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                    fontSize: 13, fontWeight: 600, color: "#111",
                  }}>
                    {inviteToast}
                  </div>,
                  document.body
                )}
                {locationEditSheet.open && ReactDOM.createPortal(
                  <div
                    style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 2000 }}
                    onMouseDown={(e) => { if (e.target === e.currentTarget) closeLocationEdit(); }}
                    onTouchStart={(e) => { if (e.target === e.currentTarget) closeLocationEdit(); }}
                  >
                    <div
                      onMouseDown={(e) => e.stopPropagation()}
                      onTouchStart={(e) => e.stopPropagation()}
                      style={{
                        position: "fixed", bottom: 0, left: 0, right: 0,
                        background: "white", borderRadius: "20px 20px 0 0",
                        maxHeight: "50vh", display: "flex", flexDirection: "column",
                        transform: locationEditSheet.visible ? "translateY(0)" : "translateY(100%)",
                        transition: "transform 300ms ease", zIndex: 2001,
                      }}
                    >
                      <div style={{ width: 36, height: 4, background: "#E0E0E0", borderRadius: 2, margin: "10px auto 4px", flexShrink: 0 }} />
                      <div style={{ padding: "10px 20px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #EFEFEF", flexShrink: 0 }}>
                        <span style={{ fontSize: 17, fontWeight: 700, color: "#F55C00" }}>Edit Locations</span>
                        <button type="button" onClick={closeLocationEdit} style={{ background: "none", border: "none", fontSize: 15, color: "#888", cursor: "pointer", fontFamily: "inherit", fontWeight: 500 }}>Cancel</button>
                      </div>
                      <div style={{ overflowY: "auto", flex: 1, padding: "20px 20px 48px", WebkitOverflowScrolling: "touch" }}>
                        {(() => {
                          const toggleEditLocation = (loc) => {
                            setLocationEditSheet(s => {
                              if (s.locations.includes(loc)) {
                                if (s.locations.length === 1) return s;
                                return { ...s, locations: s.locations.filter(l => l !== loc) };
                              }
                              return { ...s, locations: [...s.locations, loc] };
                            });
                          };
                          return (
                            <>
                              {(user?.is_admin === true ? allLocations.map(l => l.name) : allLocations.map(l => l.name).filter(n => user?.locations?.includes(n))).map(loc => {
                                const on = locationEditSheet.locations.includes(loc);
                                return (
                                  <div key={loc} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingBottom: 16, marginBottom: 16, borderBottom: "0.5px solid #EFEFEF" }}>
                                    <span style={{ fontSize: 15, color: "#111", fontWeight: 500 }}>{loc}</span>
                                    <div
                                      onClick={() => toggleEditLocation(loc)}
                                      style={{ width: 40, height: 24, borderRadius: 12, background: on ? "#F55C00" : "#e0e0e0", position: "relative", cursor: "pointer", transition: "background 0.2s", flexShrink: 0 }}
                                    >
                                      <div style={{ position: "absolute", top: 2, left: on ? 18 : 2, width: 20, height: 20, borderRadius: "50%", background: "white", boxShadow: "0 1px 3px rgba(0,0,0,0.4)", transition: "left 0.2s" }} />
                                    </div>
                                  </div>
                                );
                              })}
                              <button
                                type="button"
                                onClick={saveLocationEdit}
                                style={{ width: "100%", background: "#F55C00", color: "#fff", border: "none", borderRadius: 14, padding: "16px", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", marginTop: 8 }}
                              >Save</button>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>,
                  document.body
                )}
              </div>
            );
          }

          const TODAY_STR = getToday();
          const TOMORROW_STR = (() => {
            const d = new Date();
            d.setDate(d.getDate() + 1);
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
          })();
          const baseDate = homeDay === "today" ? TODAY_STR : TOMORROW_STR;
          const myShift = dataLoaded
            ? (shifts.find(s => s.employee_id === user.id && s.date === baseDate) || null)
            : homeDay === "tomorrow" && tomorrowCache?.userId === user.id && tomorrowCache?.date === baseDate
              ? tomorrowCache.shift
              : undefined;
          const homeShiftEnded = homeDay === "today" && !!myShift && new Date() >= new Date(`${baseDate}T${myShift.end_time}`);
          const _nowForEvents = new Date();
          const shiftLocation = myShift?.location || defaultLoc;
          // Only show events when: employee has a shift today that hasn't ended
          // Filter to same location, and hide any events that have already ended
          const dayEvents = (myShift && !homeShiftEnded)
            ? events
                .filter(ev => {
                  if (ev.date !== baseDate) return false;
                  if ((ev.location || defaultLoc) !== shiftLocation) return false;
                  const evEnd = ev.end_time ? new Date(`${baseDate}T${ev.end_time}`) : ev.start_time ? new Date(`${baseDate}T${ev.start_time}`) : null;
                  return !evEnd || _nowForEvents < evEnd;
                })
                .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""))
            : [];

          return (
            <div style={{ display: "flex", flexDirection: "column" }}>

              {/* Today / Tomorrow toggle (manager only) */}
              {user.role === "manager" && (
              <div style={{ display: "flex", justifyContent: "center", paddingTop: 16, paddingBottom: 4 }}>
                <div style={{ display: "flex", background: "#161616", borderRadius: 20, padding: 3 }}>
                  {["today", "tomorrow"].map(opt => (
                    <button
                      key={opt}
                      onClick={() => setHomeDay(opt)}
                      style={{
                        background: homeDay === opt ? "#f97316" : "transparent",
                        color: "#fff",
                        border: "none",
                        borderRadius: 16,
                        padding: "5px 20px",
                        fontSize: 13,
                        fontWeight: homeDay === opt ? 600 : 400,
                        cursor: "pointer",
                        fontFamily: "inherit",
                        letterSpacing: "0.01em",
                      }}
                    >
                      {opt === "today" ? "Today" : "Tomorrow"}
                    </button>
                  ))}
                </div>
              </div>
              )}

              {/* Manager Home */}
              {user.role === "manager" ? (() => {
                const dayShifts = shifts.filter(s => s.date === baseDate);
                const myShiftToday = dayShifts.find(s => s.employee_id === user.id) || null;
                const otherShifts = dayShifts.filter(s => s.employee_id !== user.id);
                // Staff groups filtered by selected location
                const filteredOtherShifts = otherShifts.filter(s => (s.location || defaultLoc) === homeLocFilter);
                const mgrShifts = filteredOtherShifts.filter(s => allProfiles.find(p => p.id === s.employee_id)?.role === "manager");
                const empShifts = filteredOtherShifts.filter(s => allProfiles.find(p => p.id === s.employee_id)?.role !== "manager");
                const myLocCovers = myShiftToday && (myShiftToday.location || defaultLoc) === homeLocFilter;
                const filteredGroup = { label: homeLocFilter, list: [...mgrShifts, ...empShifts], hasMgr: mgrShifts.length > 0 || myLocCovers };
                const todayEvents = events
                  .filter(ev => ev.date === baseDate && (ev.location || defaultLoc) === homeLocFilter)
                  .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
                const capitalize = str => str ? str.replace(/\b\w/g, c => c.toUpperCase()) : str;
                return (
                  <>
                    {/* Location filter pills */}
                    <div style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: 20 }}>
                      {allLocations.map(l => l.name).map(loc => (
                        <button
                          key={loc}
                          onClick={() => setHomeLocFilter(loc)}
                          style={{
                            background: homeLocFilter === loc ? "#fff" : "transparent",
                            color: homeLocFilter === loc ? "#000" : "#555",
                            border: `1px solid ${homeLocFilter === loc ? "#fff" : "#2a2a2a"}`,
                            borderRadius: 20,
                            padding: "6px 18px",
                            fontSize: 13,
                            fontWeight: homeLocFilter === loc ? 700 : 400,
                            cursor: "pointer",
                            fontFamily: "inherit",
                          }}
                        >{loc}</button>
                      ))}
                    </div>

                    {/* Date headline */}
                    <div style={{ textAlign: "center", padding: "0 8px 4px" }}>
                      <div style={{ fontSize: 32, fontWeight: 800, color: "#fff", lineHeight: 1.1 }}>
                        {new Date(baseDate + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
                      </div>
                    </div>

                    {/* Manager's own shift status — always based on their own shift, not the location filter */}
                    <div style={{ textAlign: "center", padding: "8px 8px 20px", fontSize: 14, color: myShiftToday ? "#f97316" : "#888" }}>
                      {myShiftToday
                        ? `You're working ${fmt(myShiftToday.start_time)} – ${fmt(myShiftToday.end_time)} · ${myShiftToday.location || defaultLoc}`
                        : `You're off ${homeDay === "today" ? "today" : "tomorrow"}`}
                    </div>

                    {/* 1. Pending requests pill */}
                    {pendingRequests.length > 0 && (
                      <div style={{ marginBottom: 24 }}>
                        <button onClick={() => setView("requests")} style={{ background: "transparent", border: "1px solid #f97316", borderRadius: 20, padding: "5px 14px", color: "#f97316", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                          {pendingRequests.length} pending request{pendingRequests.length !== 1 ? "s" : ""}
                        </button>
                      </div>
                    )}

                    {/* 2. Events Today (filtered by location) */}
                    {todayEvents.length > 0 && (
                      <div style={{ marginBottom: 28 }}>
                        <div style={{ fontSize: 11, color: "#fff", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 700, marginBottom: 10 }}>Events Today</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                          {todayEvents.map(ev => {
                            const assigned = eventStaff.filter(es => es.event_id === ev.id);
                            const headcount = ev.required_headcount ?? null;
                            const shortfall = headcount !== null ? headcount - assigned.length : 0;
                            return (
                              <div key={ev.id} style={{ background: "#111", border: "1px solid #1e1e1e", borderRadius: 10, padding: "12px 14px" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: 14, fontWeight: 700, color: "#fff", marginBottom: 3 }}>{capitalize(ev.title)}</div>
                                    <div style={{ fontSize: 12, color: "#fff" }}>
                                      {ev.start_time ? `${fmt(ev.start_time)}${ev.end_time ? ` – ${fmt(ev.end_time)}` : ""}` : ""}
                                      {ev.start_time && ev.location ? "  ·  " : ""}
                                      {ev.location || ""}
                                    </div>
                                  </div>
                                </div>
                                {/* Staff chips */}
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", marginTop: 10 }}>
                                  {assigned.map(es => (
                                    <div key={es.employee_id} style={{ fontSize: 11, color: "#fff", background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 6, padding: "3px 8px" }}>
                                      {formatName(es.employee_name, null, user.role)}
                                    </div>
                                  ))}
                                  {assigned.length === 0 && (
                                    <div style={{ fontSize: 11, color: "#f97316", background: "rgba(249,115,22,0.1)", border: "1px solid rgba(249,115,22,0.35)", borderRadius: 6, padding: "3px 8px", fontWeight: 600 }}>
                                      No staff assigned
                                    </div>
                                  )}
                                  {headcount !== null && shortfall > 0 && (
                                    <div style={{ fontSize: 11, color: "#f97316", background: "rgba(249,115,22,0.1)", border: "1px solid rgba(249,115,22,0.35)", borderRadius: 6, padding: "3px 8px", fontWeight: 600 }}>
                                      Needs {shortfall} more
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* 3. Staff on Shift (filtered by location) */}
                    {filteredGroup.list.length > 0 && (
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                          <div style={{ fontSize: 11, color: "#fff", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 700 }}>Staff on Shift</div>
                          {!filteredGroup.hasMgr && (
                            <div style={{ fontSize: 10, color: "#f97316", background: "rgba(249,115,22,0.1)", border: "1px solid rgba(249,115,22,0.35)", borderRadius: 10, padding: "2px 7px", fontWeight: 600 }}>No manager</div>
                          )}
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          {filteredGroup.list.map(s => {
                            const isMgr = allProfiles.find(p => p.id === s.employee_id)?.role === "manager";
                            return (
                              <div key={s.id} style={{ background: "#111", border: "1px solid #1e1e1e", borderRadius: 10, padding: "12px 14px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                                <div style={{ fontWeight: isMgr ? 700 : 400, fontSize: 14, color: isMgr ? "#f97316" : "#fff" }}>
                                  {formatName(s.employee_name, null, user.role)}
                                </div>
                                <div style={{ fontSize: 13, color: "#f97316", flexShrink: 0 }}>{fmt(s.start_time)} – {fmt(s.end_time)}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                );
              })() : (() => {
                const _nowEmp = new Date();
                const todayShiftsEmp = shifts.filter(s => s.employee_id === user.id && s.date === TODAY_STR).sort((a, b) => a.start_time.localeCompare(b.start_time));
                const tomorrowShiftEmp = shifts.filter(s => s.employee_id === user.id && s.date === TOMORROW_STR).sort((a, b) => a.start_time.localeCompare(b.start_time))[0] || null;
                const todayShiftEmp = todayShiftsEmp.find(s => _nowEmp >= new Date(`${TODAY_STR}T${s.start_time}`) && _nowEmp < new Date(`${TODAY_STR}T${s.end_time}`))
                  || todayShiftsEmp.find(s => _nowEmp < new Date(`${TODAY_STR}T${s.start_time}`))
                  || todayShiftsEmp[todayShiftsEmp.length - 1]
                  || null;
                const shiftStartDt = todayShiftEmp ? new Date(`${TODAY_STR}T${todayShiftEmp.start_time}`) : null;
                const shiftEndDt = todayShiftEmp ? new Date(`${TODAY_STR}T${todayShiftEmp.end_time}`) : null;
                const isDuringShift = todayShiftEmp && _nowEmp >= shiftStartDt && _nowEmp < shiftEndDt;
                const isAfterShift = todayShiftsEmp.length > 0 && _nowEmp >= new Date(`${TODAY_STR}T${todayShiftsEmp[todayShiftsEmp.length - 1].end_time}`);
                const heroIsToday = !isAfterShift;
                const heroDate = heroIsToday ? TODAY_STR : TOMORROW_STR;
                const secDate = heroIsToday ? TOMORROW_STR : TODAY_STR;
                const heroShiftEmp = heroIsToday ? todayShiftEmp : tomorrowShiftEmp;
                const secShiftEmp = heroIsToday ? tomorrowShiftEmp : todayShiftEmp;
                const heroDark = isDuringShift || isAfterShift;

                const findEmpMgr = (dateStr, empShift) => {
                  if (!empShift) return [];
                  const mgrShifts = shifts.filter(s =>
                    s.date === dateStr &&
                    allProfiles.find(p => p.id === s.employee_id)?.role === "manager" &&
                    s.start_time < empShift.end_time &&
                    s.end_time > empShift.start_time
                  );
                  return mgrShifts.map(s => {
                    const mp = allProfiles.find(p => p.id === s.employee_id);
                    return mp ? (mp.first_name || mp.name?.split(" ")[0] || null) : null;
                  }).filter(Boolean);
                };
                const heroMgrName = findEmpMgr(heroDate, heroShiftEmp);
                const secMgrName = findEmpMgr(secDate, secShiftEmp);

                const renderEmpCard = ({ label, date, shift, dark, showLive, showUpcoming, mgrNames }) => {
                  const isDark = dark && !!shift;
                  const textColor = isDark ? "#ffffff" : "#111111";
                  const ordinal = n => { const s = ["th","st","nd","rd"]; const v = n % 100; return n + (s[(v-20)%10] || s[v] || s[0]); };
                  const formattedDate = date ? (() => {
                    const d = new Date(date + "T00:00:00");
                    const weekday = d.toLocaleDateString("en-US", { weekday: "long" });
                    const month = d.toLocaleDateString("en-US", { month: "long" });
                    return `${weekday}, ${month} ${ordinal(d.getDate())}`;
                  })() : null;
                  const isFinished = !isDark && !!shift && date ? new Date() >= new Date(`${date}T${shift.end_time}`) : false;
                  return (
                  <div style={{ background: isDark ? "#111" : "#fff", border: isDark ? "none" : shift ? "2px solid #111" : "1px solid #EFEFEF", borderRadius: 18, padding: "20px 20px 18px", position: "relative", marginBottom: 12 }}>
                    {showLive && (
                      <div style={{ position: "absolute", top: 18, right: 18, display: "flex", alignItems: "center", gap: 5 }}>
                        <span style={{ display: "inline-block", width: 7, height: 7, borderRadius: "50%", background: "#F55C00", animation: "emp-pulse 1.4s ease-in-out infinite" }} />
                        <span style={{ fontSize: 10, fontWeight: 700, color: "#F55C00", letterSpacing: 1, textTransform: "uppercase" }}>Live</span>
                      </div>
                    )}
                    {isFinished && (
                      <div style={{ position: "absolute", top: 18, right: 18, display: "flex", alignItems: "center", gap: 4 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#2E7D32", fontFamily: "'Oswald', sans-serif" }}>FINISHED ✓</span>
                      </div>
                    )}
                    {showUpcoming && !showLive && !isFinished && (
                      <div style={{ position: "absolute", top: 18, right: 18, display: "flex", alignItems: "center", gap: 5 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: "#F55C00", letterSpacing: 1, textTransform: "uppercase", background: "#FFF4EE", borderRadius: 20, padding: "4px 10px", border: "1.5px solid #F55C00", fontFamily: "'Oswald', sans-serif" }}>UPCOMING</span>
                      </div>
                    )}
                    <div style={{ opacity: isFinished ? 0.45 : 1 }}>
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 16, fontWeight: 700, color: textColor, textTransform: "uppercase", letterSpacing: 1, fontFamily: "'Oswald', sans-serif" }}>{label}</div>
                        {formattedDate && <div style={{ fontSize: 12, fontWeight: 400, color: textColor, textTransform: "uppercase", fontFamily: "'Oswald', sans-serif" }}>{formattedDate}</div>}
                      </div>
                      {shift ? (
                        <>
                          <div style={{ fontSize: 26, fontWeight: 700, color: isDark ? "#F55C00" : "#111", lineHeight: 1, marginBottom: 14, fontFamily: "'Oswald', sans-serif" }}>
                            {fmt(shift.start_time)} – {fmt(shift.end_time)}
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                            <span style={{ fontSize: 11, color: isDark ? "#ffffff" : "#111111", textTransform: "uppercase", letterSpacing: "0.08em" }}>{shift.location || defaultLoc}</span>
                          </div>
                        </>
                      ) : (
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#BBBBBB", textTransform: "uppercase", fontFamily: "'Oswald', sans-serif" }}>No shift {label.toLowerCase()}</div>
                      )}
                    </div>
                  </div>
                  );
                };

                // Derive a display-friendly status for the employee view.
                // Manager-revoked time off should surface as "Pending" (employee needs to re-request).
                const empDisplayStatus = r => {
                  if (r.status === "revoked" && r.type === "timeoff") return "pending";
                  return r.status;
                };
                const statusSortRank = r => {
                  const s = empDisplayStatus(r);
                  if (s === "pending" || s === "pending_target" || s === "pending_manager") return 0;
                  if (s === "approved" || s === "claimed") return 1;
                  return 2; // declined, denied, revoked-self, cancelled
                };
                const myRequests = requests
                  .filter(r => r.requester_id === user.id && r.type !== "swap" && r.status !== "revoked" && !r._cancelled) // exclude swaps — different flow
                  .sort((a, b) => statusSortRank(a) - statusSortRank(b) || new Date(b.created_at) - new Date(a.created_at));
                const reqTypeLabel = r => {
                  if (r.type === "timeoff") return "Time Off";
                  if (r.type === "cover") return "Cover Request";
                  if (r.type === "late_arrival") return "Late Arrival";
                  return r.type;
                };
                const reqDateLabel = r => {
                  if (r.type === "timeoff") return fmtDateRange(r.date, r.end_date);
                  if (r.type === "cover") return r.shift_date ? fmtShortDate(r.shift_date) : "";
                  if (r.type === "late_arrival") return r.shift_date ? `Today · ETA ${fmt(r.shift_start)}` : "Today";
                  return "";
                };
                const reqBadge = r => {
                  const s = empDisplayStatus(r);
                  if (s === "pending" || s === "pending_target" || s === "pending_manager") return { bg: "#F5F5F5", color: "#888", label: "Pending" };
                  if (s === "approved" || s === "claimed") return { bg: "#E8F5E9", color: "#2E7D32", label: "Approved" };
                  return { bg: "#F5F5F5", color: "#bbb", label: "Denied" };
                };

                return (
                  <div style={{ paddingBottom: "calc(40px + env(safe-area-inset-bottom, 20px))" }}>
                    {renderEmpCard({ label: heroIsToday ? "Today" : "Tomorrow", date: heroDate, shift: heroShiftEmp, dark: heroDark, showLive: isDuringShift, showUpcoming: heroIsToday && !!heroShiftEmp && !isDuringShift && !isAfterShift, mgrNames: heroMgrName })}
                    {renderEmpCard({ label: heroIsToday ? "Tomorrow" : "Today", date: secDate, shift: secShiftEmp, dark: false, showLive: false, mgrNames: secMgrName })}

                    {/* ── Employee Calendar Strip ── */}
                    {(() => {
                      const EMP_DAY_LABELS = ["SUN","MON","TUE","WED","THU","FRI","SAT"];
                      const EMP_MONTH_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
                      const empTodayStr = getToday();
                      const empStripDates = Array.from({ length: empStripDayCount }, (_, i) => {
                        const d = new Date(empTodayStr + "T00:00:00");
                        d.setDate(d.getDate() + i);
                        const dateStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
                        return { dateStr, dayLabel: EMP_DAY_LABELS[d.getDay()], dayNum: d.getDate(), monthLabel: EMP_MONTH_SHORT[d.getMonth()], year: d.getFullYear() };
                      });
                      // Dot indicators: orange for shift, green for approved time off
                      const empShiftDates = new Set(shifts.filter(s => s.employee_id === user.id).map(s => s.date));
                      const empTimeOffDates = new Set();
                      approvedTimeOff.filter(r => r.requester_id === user.id).forEach(r => {
                        const start = new Date(r.date + "T00:00:00");
                        const end = new Date((r.end_date || r.date) + "T00:00:00");
                        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                          empTimeOffDates.add(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`);
                        }
                      });
                      return (
                        <div style={{ margin: "16px 0 4px", overflow: "visible" }}>
                          {/* Back to Today */}
                          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4, minHeight: 22 }}>
                            {(!empStripTodayVisible || empSelectedDate !== empTodayStr) && (
                              <button
                                type="button"
                                onClick={() => { setEmpSelectedDate(empTodayStr); if (empStripScrollRef.current) empStripScrollRef.current.scrollLeft = 0; setEmpStripTodayVisible(true); }}
                                style={{ fontSize: 11, fontWeight: 600, color: "#888", background: "none", border: "none", borderRadius: 20, padding: "2px 8px", cursor: "pointer", fontFamily: "inherit", WebkitTapHighlightColor: "transparent" }}
                              >← Back to Today</button>
                            )}
                          </div>
                          <style>{`.emp-strip::-webkit-scrollbar{display:none}.emp-strip{scrollbar-width:none}`}</style>
                          <div
                            ref={empStripScrollRef}
                            className="emp-strip"
                            onScroll={() => {
                              const el = empStripScrollRef.current;
                              if (!el) return;
                              if (el.scrollWidth - el.scrollLeft - el.clientWidth < 300) setEmpStripDayCount(c => c + 60);
                              setEmpStripTodayVisible(el.scrollLeft < 56);
                            }}
                            style={{ display: "flex", gap: 6, overflowX: "auto", overflowY: "visible", paddingBottom: 4, WebkitOverflowScrolling: "touch" }}
                          >
                            {empStripDates.map(({ dateStr, dayLabel, dayNum, monthLabel, year }) => {
                              const isTodayTile = dateStr === empTodayStr;
                              const isSelected = dateStr === empSelectedDate;
                              const hasShift = empShiftDates.has(dateStr);
                              const hasTimeOff = empTimeOffDates.has(dateStr);

                              let bg = "#f4f4f4";
                              let numColor = "#0A0A0A";
                              let labelColor = "rgba(0,0,0,0.45)";
                              let border = "none";
                              if (isTodayTile && !isSelected) { bg = "#0A0A0A"; numColor = "white"; labelColor = "rgba(255,255,255,0.6)"; }
                              if (isSelected) { bg = "#F55C00"; numColor = "white"; labelColor = "rgba(255,255,255,0.8)"; border = isTodayTile ? "none" : "2px solid #0A0A0A"; }

                              return (
                                <div key={dateStr} style={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center" }}>
                                  <div style={{ height: 18, display: "flex", alignItems: "center", marginBottom: 3 }}>
                                    {dayNum === 1 && (
                                      <span style={{ fontSize: 10, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: 0.5, whiteSpace: "nowrap", fontFamily: "'Oswald', sans-serif" }}>
                                        {monthLabel} {year}
                                      </span>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => { setEmpSelectedDate(dateStr); setEmpCalSheet(true); }}
                                    style={{ position: "relative", width: 52, height: 52, borderRadius: 10, background: bg, border, flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: "pointer", fontFamily: "inherit", WebkitTapHighlightColor: "transparent" }}
                                  >
                                    <span style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: (isTodayTile || isSelected) ? labelColor : "#111111" }}>{dayLabel}</span>
                                    <span style={{ fontSize: 16, fontWeight: 700, color: numColor, lineHeight: 1, marginTop: 2, fontFamily: "'Oswald', sans-serif" }}>{dayNum}</span>
                                    {(hasShift || hasTimeOff) && (
                                      <span style={{ position: "absolute", bottom: 5, width: 5, height: 5, borderRadius: "50%", background: hasTimeOff ? "#22c55e" : "#F55C00" }} />
                                    )}
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}

                    {empCalSheet && (() => {
                      const selectedShifts = shifts.filter(s => s.employee_id === user.id && s.date === empSelectedDate).sort((a, b) => a.start_time.localeCompare(b.start_time));
                      const mgrShifts = selectedShifts.length > 0 ? shifts.filter(s =>
                        s.date === empSelectedDate &&
                        allProfiles.find(p => p.id === s.employee_id)?.role === "manager" &&
                        selectedShifts.some(es => s.start_time < es.end_time && s.end_time > es.start_time)
                      ) : [];
                      const mgrNames = mgrShifts.map(s => {
                        const mp = allProfiles.find(p => p.id === s.employee_id);
                        return mp ? (mp.first_name || mp.name?.split(" ")[0] || null) : null;
                      }).filter(Boolean);
                      const hasTimeOff = approvedTimeOff.some(r =>
                        r.requester_id === user.id &&
                        empSelectedDate >= r.date &&
                        empSelectedDate <= (r.end_date || r.date)
                      );
                      const empTodayStr = getToday();
                      const empTomorrowStr = (() => { const d = new Date(empTodayStr + "T00:00:00"); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); })();
                      const dayLabel = empSelectedDate === empTodayStr ? "TODAY" : empSelectedDate === empTomorrowStr ? "TOMORROW" : null;
                      const formattedDate = (() => {
                        const d = new Date(empSelectedDate + "T00:00:00");
                        const days = ["SUNDAY","MONDAY","TUESDAY","WEDNESDAY","THURSDAY","FRIDAY","SATURDAY"];
                        const months = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];
                        const day = d.getDate();
                        const suffix = day === 1 || day === 21 || day === 31 ? "ST" : day === 2 || day === 22 ? "ND" : day === 3 || day === 23 ? "RD" : "TH";
                        return `${days[d.getDay()]}, ${months[d.getMonth()]} ${day}${suffix}`;
                      })();
                      return (
                        <div style={{ background: "#fff", border: "1px solid #EFEFEF", borderRadius: 14, padding: "14px 16px", marginTop: 6, marginBottom: 4, position: "relative" }}>
                          <button
                            type="button"
                            onClick={() => setEmpCalSheet(false)}
                            style={{ position: "absolute", top: 10, right: 12, background: "none", border: "none", fontSize: 13, color: "#888", cursor: "pointer", fontFamily: "inherit" }}
                          >✕</button>
                          {dayLabel && (
                            <div style={{ fontSize: 13, fontWeight: 700, color: "#F55C00", fontFamily: "'Oswald', sans-serif", textTransform: "uppercase", marginBottom: 2 }}>{dayLabel}</div>
                          )}
                          <div style={{ fontSize: 11, fontWeight: 700, color: "#111", fontFamily: "'Oswald', sans-serif", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>{formattedDate}</div>
                          {selectedShifts.length > 0 ? (
                            <>
                              {selectedShifts.map((s, i) => {
                                const shiftDone = new Date() >= new Date(`${empSelectedDate}T${s.end_time}`);
                                return (
                                  <div key={s.id} style={{ marginBottom: i < selectedShifts.length - 1 ? 10 : 0, paddingBottom: i < selectedShifts.length - 1 ? 10 : 0, borderBottom: i < selectedShifts.length - 1 ? "1px solid #EFEFEF" : "none", opacity: shiftDone ? 0.4 : 1 }}>
                                    <div style={{ fontSize: 22, fontWeight: 700, color: "#111", fontFamily: "'Oswald', sans-serif" }}>
                                      {fmt(s.start_time)} – {fmt(s.end_time)}
                                    </div>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: "#111", fontFamily: "'Oswald', sans-serif", textTransform: "uppercase", letterSpacing: "0.08em", marginTop: 4 }}>
                                      {s.location || defaultLoc}
                                    </div>
                                  </div>
                                );
                              })}
                            </>
                          ) : (
                            <div style={{ fontSize: 16, fontWeight: 700, color: "#BBBBBB", fontFamily: "'Oswald', sans-serif", textTransform: "uppercase" }}>No Shift</div>
                          )}
                          {hasTimeOff && (
                            <div style={{ display: "inline-block", background: "#E8F5E9", color: "#2E7D32", fontSize: 11, fontWeight: 700, borderRadius: 20, padding: "4px 12px", marginTop: 12 }}>
                              TIME OFF APPROVED
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    <div style={{ height: 1, background: "#EFEFEF", margin: "8px 0 10px" }} />

                    {(() => {
                      const allMyPending = [
                        ...myRequests.filter(r => { const s = empDisplayStatus(r); return s === "pending" || s === "pending_manager"; }),
                        ...openCovers.filter(r => r.requester_id === user.id && r.shift_date >= getToday() && r.status === "pending")
                      ];
                      const seen = new Set();
                      const pendingCount = allMyPending.filter(r => { if (seen.has(r.id)) return false; seen.add(r.id); return true; }).length;
                      const actions = [
                        { icon: "ti-clock", title: "Availability", sub: "Set your weekly hours", sheet: "availability" },
                        { icon: "ti-sun", title: "Time Off", sub: "Request days off", sheet: "timeoff" },
                        { icon: "ti-user-search", title: "Need Coverage", sub: "Flag a shift you can't make", sheet: "cover" },
                        { icon: "ti-bell", title: "Requests", sub: null, sheet: "requests", pendingCount },
                      ];
                      return (
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                          {actions.map(action => (
                            <button key={action.title} onClick={() => openEmpSheet(action.sheet)} style={{ background: "#fff", border: "1px solid #D0D0D0", borderRadius: 14, padding: 16, textAlign: "left", cursor: "pointer", fontFamily: "inherit", display: "flex", flexDirection: "column", gap: 6, WebkitTapHighlightColor: "transparent" }}>
                              <i className={`ti ${action.icon}`} style={{ fontSize: 24, color: "#F55C00", lineHeight: 1 }} />
                              <span style={{ fontSize: 15, fontWeight: 700, color: "#111111", display: "block", fontFamily: "'Oswald', sans-serif", textTransform: "uppercase" }}>{action.title}</span>
                              {action.pendingCount !== undefined
                                ? <span style={{ fontSize: 12, color: "#F55C00", lineHeight: 1.4, display: "block", fontWeight: 700, fontFamily: "'Oswald', sans-serif", textTransform: "uppercase" }}>
                                    {action.pendingCount > 0 ? `${action.pendingCount} PENDING` : "ALL CAUGHT UP"}
                                  </span>
                                : <span style={{ fontSize: 12, color: "#111111", lineHeight: 1.4, display: "block" }}>{action.sub}</span>
                              }
                            </button>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                );
              })()}

            </div>
          );
        })()}

        {/* MANAGER: Schedule (includes week view + calendar toggle) */}
        {user.role === "manager" && view === "schedule" && (() => {
          const weekDays = Array.from({ length: 7 }, (_, i) => {
            const d = new Date(weekStart + "T00:00:00");
            d.setDate(d.getDate() + i);
            return d.toISOString().split("T")[0];
          });
          const weekLabel = `${new Date(weekDays[0] + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${new Date(weekDays[6] + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
          const filteredShifts = locationFilter === "All" ? sortedShifts : sortedShifts.filter(s => s.location === locationFilter);
          const shiftsByDate = {};
          filteredShifts.forEach(s => {
            if (!shiftsByDate[s.date]) shiftsByDate[s.date] = [];
            shiftsByDate[s.date].push(s);
          });
          const navBtn = { background: "none", border: "1px solid #2a2a2a", borderRadius: 8, padding: "7px 14px", color: "#aaa", fontSize: 16, cursor: "pointer" };
          const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

          return (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <div style={{ display: "flex", gap: 2, background: "#111", border: "1px solid #2a2a2a", borderRadius: 9, padding: 2 }}>
                  <button onClick={() => setScheduleSubview("week")} style={{ padding: "6px 14px", background: scheduleSubview === "week" ? "#f97316" : "none", border: "none", borderRadius: 7, color: scheduleSubview === "week" ? "#000" : "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Week</button>
                  <button onClick={() => setScheduleSubview("calendar")} style={{ padding: "6px 14px", background: scheduleSubview === "calendar" ? "#f97316" : "none", border: "none", borderRadius: 7, color: scheduleSubview === "calendar" ? "#000" : "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Month</button>
                </div>
                <button onClick={() => { setShowAddShift(true); setAddShiftWarning(""); setNewShift({ employeeId: "", date: "", start: "09:00", end: "17:00", note: "", location: lastShiftLocation }); }} style={{ background: "#f97316", border: "none", borderRadius: 8, padding: "9px 16px", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>+ Add Shift</button>
              </div>

              <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
                {["All", ...allLocations.map(l => l.name)].map(loc => (
                  <button key={loc} onClick={() => setLocationFilter(loc)} style={{ flex: 1, padding: "8px 4px", background: locationFilter === loc ? "#f97316" : "#141414", border: `1px solid ${locationFilter === loc ? "#f97316" : "#2a2a2a"}`, borderRadius: 8, color: locationFilter === loc ? "#000" : "#fff", fontSize: 12, fontWeight: locationFilter === loc ? 700 : 400, cursor: "pointer" }}>{loc}</button>
                ))}
              </div>

              {showAddShift && (
                <div style={{ background: "#141414", border: "1px solid #222", borderRadius: 12, padding: 20, marginBottom: 20 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#f97316" }}>{editingShiftId ? "Edit Shift" : "Add Shifts"}</div>
                    <button onClick={() => { setShowAddShift(false); setAddShiftWarning(""); setEditingShiftId(null); setNewShift({ employeeId: "", date: "", start: "09:00", end: "17:00", note: "", location: lastShiftLocation }); setSelectedDates(new Set()); }} style={{ background: "none", border: "1px solid #2a2a2a", borderRadius: 7, padding: "5px 14px", color: "#aaa", fontSize: 13, cursor: "pointer" }}>Done</button>
                  </div>
                  {(() => {
                    const shiftLocation = (locationFilter && locationFilter !== "All") ? locationFilter : (newShift.location || lastShiftLocation || (allLocations[0]?.name ?? ""));
                    const filteredProfiles = allProfiles.filter(profile => {
                      if (profile.locations && profile.locations.length > 0) {
                        return profile.locations.includes(shiftLocation);
                      }
                      return (profile.location || defaultLoc) === shiftLocation;
                    });
                    const DAY_ORDER = [0,1,2,3,4,5,6];
                    const DAY_LABELS = ["MON","TUE","WED","THU","FRI","SAT","SUN"];
                    const fmtTime = t => { const [h, m] = t.split(':'); const hr = parseInt(h); return `${hr % 12 || 12}:${m} ${hr < 12 ? 'AM' : 'PM'}`; };
                    return (
                      <div style={{ background: "#0a0a0a", border: "1px solid #2a2a2a", borderRadius: 8, marginBottom: 10, maxHeight: 220, overflowY: "auto" }}>
                        {filteredProfiles.map((emp, i) => {
                          const isSelected = newShift.employeeId === emp.id;
                          const name = emp.first_name ? formatName(emp.first_name, emp.last_name, user.role) : formatName(emp.name, null, user.role);
                          return (
                            <div key={emp.id}>
                              <button
                                type="button"
                                onClick={() => { setNewShift(p => ({ ...p, employeeId: isSelected ? "" : emp.id })); setAddShiftWarning(""); }}
                                style={{
                                  display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%",
                                  padding: "10px 14px", background: isSelected ? "#1a1a1a" : "transparent", border: "none",
                                  borderBottom: (!isSelected || selectedEmpAvailability.length === 0) && i < filteredProfiles.length - 1 ? "1px solid #1e1e1e" : "none",
                                  cursor: "pointer", textAlign: "left",
                                }}
                              >
                                <span style={{ fontSize: 14, color: isSelected ? "#f97316" : "#fff", fontWeight: isSelected ? 600 : 400 }}>
                                  {name}{emp.role === "manager" ? " (Manager)" : ""}
                                </span>
                                <i className={`ti ti-chevron-${isSelected ? "up" : "down"}`} style={{ fontSize: 12, color: isSelected ? "#f97316" : "#555" }} />
                              </button>
                              {isSelected && selectedEmpAvailability.length > 0 && (
                                <div style={{ background: "#1a1a1a", padding: "8px 14px 12px", borderBottom: i < filteredProfiles.length - 1 ? "1px solid #1e1e1e" : "none" }}>
                                  <div style={{ fontSize: 10, fontWeight: 600, color: "#f97316", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.08em" }}>Availability</div>
                                  {DAY_ORDER.map(dow => {
                                    const row = selectedEmpAvailability.find(r => r.day_of_week === dow);
                                    if (!row) return null;
                                    return (
                                      <div key={dow} style={{ display: "flex", alignItems: "center", gap: 8, padding: "2px 0" }}>
                                        <span style={{ color: "#666", fontSize: 11, minWidth: 30 }}>{DAY_LABELS[dow]}</span>
                                        {row.is_available
                                          ? <span style={{ color: "#ccc", fontSize: 11 }}>{fmtTime(row.start_time)} – {fmtTime(row.end_time)}</span>
                                          : <span style={{ color: "#444", fontSize: 11 }}>—</span>
                                        }
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                  <input placeholder="Note (optional — e.g. Front desk, Reffing 3pm game)" value={newShift.note} onChange={e => setNewShift(p => ({ ...p, note: e.target.value }))} style={inputStyle} />
                  {editingShiftId ? (
                    <input type="date" value={newShift.date} onChange={e => { setNewShift(p => ({ ...p, date: e.target.value })); setAddShiftWarning(""); }} style={inputStyle} />
                  ) : (
                    <div style={{ background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 10, padding: 12, marginBottom: 8 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <button onClick={() => setPickerMonth(p => { const d = new Date(p.year, p.month - 1); return { year: d.getFullYear(), month: d.getMonth() }; })} style={{ background: "none", border: "none", color: "#aaa", fontSize: 18, cursor: "pointer", padding: "0 4px", lineHeight: 1 }}>‹</button>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#ccc" }}>{new Date(pickerMonth.year, pickerMonth.month).toLocaleDateString("en-US", { month: "long", year: "numeric" })}</div>
                        <button onClick={() => setPickerMonth(p => { const d = new Date(p.year, p.month + 1); return { year: d.getFullYear(), month: d.getMonth() }; })} style={{ background: "none", border: "none", color: "#aaa", fontSize: 18, cursor: "pointer", padding: "0 4px", lineHeight: 1 }}>›</button>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 4 }}>
                        {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d => <div key={d} style={{ textAlign: "center", fontSize: 10, color: "#444", fontWeight: 600, padding: "2px 0" }}>{d}</div>)}
                      </div>
                      {(() => {
                        const firstDay = new Date(pickerMonth.year, pickerMonth.month, 1).getDay();
                        const daysInMonth = new Date(pickerMonth.year, pickerMonth.month + 1, 0).getDate();
                        const cells = [];
                        for (let i = 0; i < firstDay; i++) cells.push(null);
                        for (let d = 1; d <= daysInMonth; d++) cells.push(d);
                        return (
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
                            {cells.map((day, i) => {
                              if (!day) return <div key={`e${i}`} />;
                              const dateStr = `${pickerMonth.year}-${String(pickerMonth.month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                              const sel = selectedDates.has(dateStr);
                              const past = dateStr < getToday();
                              const todPick = isToday(dateStr);
                              const tomPick = isTomorrow(dateStr);
                              return (
                                <button key={dateStr} onClick={() => { if (past) return; setSelectedDates(prev => { const n = new Set(prev); sel ? n.delete(dateStr) : n.add(dateStr); return n; }); setAddShiftWarning(""); }} style={{ background: sel ? "#f97316" : "none", border: `1px solid ${todPick && !sel ? "#f97316" : sel ? "#f97316" : tomPick ? "rgba(249,115,22,0.5)" : "#2a2a2a"}`, borderRadius: 6, padding: "5px 2px", color: todPick && !sel ? "#f97316" : sel ? "#000" : past ? "#333" : tomPick ? "rgba(249,115,22,0.85)" : "#ccc", fontSize: 12, fontWeight: todPick || sel ? 700 : 400, cursor: past ? "default" : "pointer", textAlign: "center", opacity: past ? 0.4 : 1 }}>{day}</button>
                              );
                            })}
                          </div>
                        );
                      })()}
                      {selectedDates.size > 0 && (
                        <div style={{ marginTop: 8, fontSize: 11, color: "#f97316", fontWeight: 600 }}>{selectedDates.size} day{selectedDates.size !== 1 ? "s" : ""} selected</div>
                      )}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                    <TimePicker value={newShift.start} onChange={v => { setNewShift(p => ({ ...p, start: v })); setAddShiftWarning(""); }} />
                    <div style={{ display: "flex", alignItems: "center", color: "#555", fontSize: 13, flexShrink: 0 }}>to</div>
                    <TimePicker value={newShift.end} onChange={v => { setNewShift(p => ({ ...p, end: v })); setAddShiftWarning(""); }} />
                  </div>
                  {addShiftWarning && (
                    <div style={{ background: "#1a0a0a", border: "1px solid #ef4444", borderRadius: 8, padding: "10px 14px", marginBottom: 10, fontSize: 13, color: "#ef4444" }}>
                      ⚠ {addShiftWarning}
                    </div>
                  )}
                  <button
                    onClick={submitShift}
                    disabled={shiftSaving === "saving"}
                    style={{ width: "100%", background: shiftSaving === "saved" ? "#22c55e" : "#f97316", border: "none", borderRadius: 8, padding: "11px", color: "#000", fontWeight: 700, fontSize: 13, cursor: shiftSaving === "saving" ? "not-allowed" : "pointer", marginTop: 4, opacity: shiftSaving === "saving" ? 0.7 : 1, transition: "background 0.2s" }}
                  >
                    {shiftSaving === "saving" ? "Saving…" : shiftSaving === "saved" ? "Saved ✓" : editingShiftId ? "Save Changes" : selectedDates.size > 1 ? `Add ${selectedDates.size} Shifts` : "Add Shift"}
                  </button>
                </div>
              )}

              {scheduleSubview === "week" && (
                <>
                  {/* Week navigation */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <button style={navBtn} onClick={() => { const d = new Date(weekStart + "T00:00:00"); d.setDate(d.getDate() - 7); setWeekStart(d.toISOString().split("T")[0]); }}>‹</button>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#ccc" }}>{weekLabel}</div>
                    <button style={navBtn} onClick={() => { const d = new Date(weekStart + "T00:00:00"); d.setDate(d.getDate() + 7); setWeekStart(d.toISOString().split("T")[0]); }}>›</button>
                  </div>

                  {/* Week grid */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 3 }}>
                    {weekDays.map((dateStr, i) => {
                      const dayShifts = shiftsByDate[dateStr] || [];
                      const dayIsToday = isToday(dateStr);
                      const dayIsTomorrow = isTomorrow(dateStr);
                      const isPast = dateStr < getToday();
                      const day = parseInt(dateStr.split("-")[2]);
                      return (
                        <div key={dateStr} onClick={() => !isPast && setSelectedDay(dateStr)} style={{
                          background: "#111",
                          border: `1px solid ${dayIsToday && selectedDay === dateStr ? "rgba(249,115,22,0.5)" : dayIsTomorrow ? "rgba(249,115,22,0.3)" : "#1e1e1e"}`,
                          borderRadius: 8, minHeight: 120, padding: "6px 4px",
                          cursor: isPast ? "default" : "pointer",
                          opacity: isPast ? 0.4 : 1,
                        }}>
                          <div style={{ textAlign: "center", marginBottom: 4 }}>
                            <div style={{ fontSize: 9, color: dayIsToday ? "#f97316" : dayIsTomorrow ? "rgba(249,115,22,0.7)" : "#fff", textTransform: "uppercase" }}>{DOW[i]}</div>
                            <div style={{ display: "flex", justifyContent: "center" }}>
                              <div style={dayIsToday ? todayBadge : dayIsTomorrow ? tomorrowBadge : { fontSize: 13, color: "#fff" }}>{day}</div>
                            </div>
                          </div>
                          {dayShifts.length > 0 && (
                            <div style={{ textAlign: "center", fontSize: 22, fontWeight: 700, color: "#f97316", lineHeight: 1.2 }}>{dayShifts.length}</div>
                          )}
                          {approvedTimeOff.filter(r => dateStr >= r.date && dateStr <= (r.end_date || r.date)).map(r => (
                            <div key={`off-${r.id}`} style={{ textAlign: "center", fontSize: 8, color: "#a05a20", fontWeight: 600, marginTop: 2 }}>
                              {formatName(r.requester_name, null, user.role).split(" ")[0]} off
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>

                  {approvedTimeOff.length > 0 && (
                    <div style={{ background: "#0f0f0f", border: "1px solid #1e1e1e", borderRadius: 10, padding: "12px 16px", marginTop: 16 }}>
                      <div style={{ fontSize: 11, color: "#555", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>Approved Time Off</div>
                      {approvedTimeOff.map(r => (
                        <div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                          <div style={{ fontSize: 13, color: "#888" }}>
                            <span style={{ color: "#ddd" }}>{formatName(r.requester_name, null, user.role)}</span> — {fmtDateRange(r.date, r.end_date)}
                          </div>
                          <button onClick={() => setConfirmDialog({ message: "Are you sure you want to revoke this time off?", onConfirm: () => revokeTimeOff(r.id) })} style={{ background: "none", border: "1px solid #2a2a2a", borderRadius: 6, padding: "3px 8px", color: "#555", fontSize: 11, cursor: "pointer", flexShrink: 0 }}>Revoke</button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {scheduleSubview === "calendar" && (() => {
                const firstDay = new Date(teamCalMonth.year, teamCalMonth.month, 1).getDay();
                const daysInMonth = new Date(teamCalMonth.year, teamCalMonth.month + 1, 0).getDate();
                const monthLabel = new Date(teamCalMonth.year, teamCalMonth.month).toLocaleDateString("en-US", { month: "long", year: "numeric" });
                const calNavBtn = { background: "none", border: "1px solid #2a2a2a", borderRadius: 8, padding: "7px 14px", color: "#aaa", fontSize: 16, cursor: "pointer" };
                const calDOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
                const filteredCalShifts = locationFilter === "All" ? sortedShifts : sortedShifts.filter(s => s.location === locationFilter);
                const calShiftsByDate = {};
                filteredCalShifts.forEach(s => {
                  if (!calShiftsByDate[s.date]) calShiftsByDate[s.date] = [];
                  calShiftsByDate[s.date].push(s);
                });
                const cells = [];
                for (let i = 0; i < firstDay; i++) cells.push(null);
                for (let d = 1; d <= daysInMonth; d++) cells.push(d);
                return (
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                      <button style={calNavBtn} onClick={() => setTeamCalMonth(p => { const d = new Date(p.year, p.month - 1); return { year: d.getFullYear(), month: d.getMonth() }; })}>‹</button>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#ccc" }}>{monthLabel}</div>
                      <button style={calNavBtn} onClick={() => setTeamCalMonth(p => { const d = new Date(p.year, p.month + 1); return { year: d.getFullYear(), month: d.getMonth() }; })}>›</button>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 2 }}>
                      {calDOW.map(d => <div key={d} style={{ textAlign: "center", fontSize: 10, color: "#444", fontWeight: 600, padding: "4px 0" }}>{d}</div>)}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
                      {cells.map((day, i) => {
                        if (!day) return <div key={`e${i}`} />;
                        const dateStr = `${teamCalMonth.year}-${String(teamCalMonth.month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                        const dayShifts = calShiftsByDate[dateStr] || [];
                        const dayIsToday = isToday(dateStr);
                        const dayIsTomorrow = isTomorrow(dateStr);
                        const isPast = dateStr < getToday();
                        const offEntries = approvedTimeOff.filter(r => dateStr >= r.date && dateStr <= (r.end_date || r.date));
                        const isSelected = dateStr === selectedDay;
                        console.log("[monthGrid]", dateStr, "dayIsToday:", dayIsToday, "isSelected:", isSelected, "getToday():", getToday());
                        return (
                          <div key={dateStr} onClick={() => setSelectedDay(dateStr)} style={{
                            background: isSelected && !dayIsToday ? "#f97316" : "#111",
                            border: `1px solid ${dayIsTomorrow ? "rgba(249,115,22,0.3)" : isSelected && !dayIsToday ? "#f97316" : "#1e1e1e"}`,
                            borderRadius: 6, minHeight: 80, padding: "4px 3px", cursor: "pointer", opacity: isPast ? 0.5 : 1,
                          }}>
                            <div style={{ textAlign: "center", marginBottom: 2, display: "flex", justifyContent: "center" }}>
                              <div style={dayIsToday ? { ...todayBadge, width: 20, height: 20, fontSize: 11 } : dayIsTomorrow ? { ...tomorrowBadge, width: 20, height: 20, fontSize: 11 } : { fontSize: 11, color: isSelected ? "#000" : "#fff" }}>{day}</div>
                            </div>
                            {dayShifts.length > 0 && <div style={{ textAlign: "center", fontSize: 18, fontWeight: 700, color: dayIsToday ? "#fff" : isSelected ? "#000" : "#f97316", lineHeight: 1.2 }}>{dayShifts.length}</div>}
                            {offEntries.length > 0 && <div style={{ textAlign: "center", fontSize: 9, color: dayIsToday ? "#888" : isSelected ? "#000" : "#a05a20", fontWeight: 600, marginTop: 2 }}>{offEntries.length} off</div>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          );
        })()}

        {/* MANAGER: Events */}
        {user.role === "manager" && view === "events" && (() => {
          const EVENT_TYPES = ["Class", "Birthday Party", "Field Day", "Camp", "Other"];
          const KNOWN_TYPES = ["Class", "Birthday Party", "Field Day", "Camp"];
          const EVENT_FILTERS = ["All", "Classes", "Birthday Parties", "Field Days", "Camps", "Other"];
          const filterMatch = (ev) => {
            if (eventTypeFilter === "All") return true;
            if (eventTypeFilter === "Classes") return ev.type === "Class";
            if (eventTypeFilter === "Birthday Parties") return ev.type === "Birthday Party";
            if (eventTypeFilter === "Field Days") return ev.type === "Field Day";
            if (eventTypeFilter === "Camps") return ev.type === "Camp";
            if (eventTypeFilter === "Other") return !KNOWN_TYPES.includes(ev.type);
            return true;
          };
          const nowDate = new Date().toISOString().split("T")[0];
          const upcomingEvents = events.filter(ev => ev.date >= nowDate && filterMatch(ev)).sort((a, b) => a.date.localeCompare(b.date) || (a.start_time || "").localeCompare(b.start_time || ""));
          const byDate = {};
          upcomingEvents.forEach(ev => { if (!byDate[ev.date]) byDate[ev.date] = []; byDate[ev.date].push(ev); });
          const groupedDates = Object.keys(byDate).sort();

          const EventCard = ({ ev }) => {
            const staff = eventStaff.filter(es => es.event_id === ev.id);
            return (
              <div onClick={() => setSelectedEvent(ev)} style={{ ...shiftCard, flexDirection: "column", alignItems: "flex-start", gap: 8, cursor: "pointer" }}>
                <div style={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "flex-start" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4, flexWrap: "wrap" }}>
                      <div style={{ fontSize: 10, background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 4, padding: "2px 7px", color: "#888" }}>{ev.type}</div>
                      {ev.is_informational && <div style={{ fontSize: 10, background: "#161616", border: "1px solid #2a2a2a", borderRadius: 4, padding: "2px 7px", color: "#555" }}>Info</div>}
                    </div>
                    <div style={{ fontWeight: 600, fontSize: 15, color: "#fff" }}>{ev.title}</div>
                    <div style={{ color: "#888", fontSize: 13, marginTop: 3 }}>{fmtDate(ev.date)}{ev.start_time ? ` · ${fmt(ev.start_time)}–${fmt(ev.end_time)}` : ""}</div>
                    <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>{ev.location}</div>
                  </div>
                  <div style={{ fontSize: 11, color: "#555", textAlign: "right", flexShrink: 0, marginLeft: 8 }}>
                    {ev.is_informational ? "FYI" : `${staff.length} staff`}
                  </div>
                </div>
              </div>
            );
          };

          return (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <div style={{ fontSize: 20, fontWeight: 700 }}>Events</div>
                <button onClick={() => { setShowAddEvent(true); setAddEventWarning(""); }} style={{ background: "#f97316", border: "none", borderRadius: 8, padding: "9px 16px", color: "#000", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>+ Add Event</button>
              </div>

              <div style={{ display: "flex", gap: 6, overflowX: "auto", marginBottom: 16, paddingBottom: 2 }}>
                {EVENT_FILTERS.map(f => (
                  <button key={f} onClick={() => setEventTypeFilter(f)} style={{ flexShrink: 0, padding: "7px 12px", background: eventTypeFilter === f ? "#f97316" : "#141414", border: `1px solid ${eventTypeFilter === f ? "#f97316" : "#2a2a2a"}`, borderRadius: 8, color: eventTypeFilter === f ? "#000" : "#fff", fontSize: 12, fontWeight: eventTypeFilter === f ? 700 : 400, cursor: "pointer" }}>{f}</button>
                ))}
              </div>

              {showAddEvent && (
                <div style={{ background: "#141414", border: "1px solid #222", borderRadius: 12, padding: 20, marginBottom: 20 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14, color: "#f97316" }}>{editingEventId ? "Edit Event" : "New Event"}</div>

                  <select value={newEvent.type} onChange={e => { setNewEvent(p => ({ ...p, type: e.target.value })); setAddEventWarning(""); }} style={inputStyle}>
                    <option value="">Event Type</option>
                    {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  {newEvent.type === "Other" && (
                    <input placeholder="Custom type name" value={newEvent.typeCustom} onChange={e => setNewEvent(p => ({ ...p, typeCustom: e.target.value }))} style={inputStyle} />
                  )}
                  <input placeholder="Title" value={newEvent.title} onChange={e => setNewEvent(p => ({ ...p, title: e.target.value }))} style={inputStyle} />
                  {editingEventId ? (
                    <input type="date" value={newEvent.date} onChange={e => { setNewEvent(p => ({ ...p, date: e.target.value })); setAddEventWarning(""); }} style={inputStyle} />
                  ) : newEvent.is_recurring ? (
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 11, color: "#555", marginBottom: 4 }}>Start Date</div>
                      <input type="date" value={newEvent.date} onChange={e => { setNewEvent(p => ({ ...p, date: e.target.value })); setAddEventWarning(""); }} style={{ ...inputStyle, marginBottom: 0 }} />
                    </div>
                  ) : (
                    <div style={{ background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 10, padding: 12, marginBottom: 8 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <button onClick={() => setEventPickerMonth(p => { const d = new Date(p.year, p.month - 1); return { year: d.getFullYear(), month: d.getMonth() }; })} style={{ background: "none", border: "none", color: "#aaa", fontSize: 18, cursor: "pointer", padding: "0 4px", lineHeight: 1 }}>‹</button>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#ccc" }}>{new Date(eventPickerMonth.year, eventPickerMonth.month).toLocaleDateString("en-US", { month: "long", year: "numeric" })}</div>
                        <button onClick={() => setEventPickerMonth(p => { const d = new Date(p.year, p.month + 1); return { year: d.getFullYear(), month: d.getMonth() }; })} style={{ background: "none", border: "none", color: "#aaa", fontSize: 18, cursor: "pointer", padding: "0 4px", lineHeight: 1 }}>›</button>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 4 }}>
                        {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d => <div key={d} style={{ textAlign: "center", fontSize: 10, color: "#444", fontWeight: 600, padding: "2px 0" }}>{d}</div>)}
                      </div>
                      {(() => {
                        const firstDay = new Date(eventPickerMonth.year, eventPickerMonth.month, 1).getDay();
                        const daysInMonth = new Date(eventPickerMonth.year, eventPickerMonth.month + 1, 0).getDate();
                        const cells = [];
                        for (let i = 0; i < firstDay; i++) cells.push(null);
                        for (let d = 1; d <= daysInMonth; d++) cells.push(d);
                        return (
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
                            {cells.map((day, i) => {
                              if (!day) return <div key={`e${i}`} />;
                              const dateStr = `${eventPickerMonth.year}-${String(eventPickerMonth.month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                              const sel = selectedEventDates.has(dateStr);
                              const past = dateStr < getToday();
                              const todPick = isToday(dateStr);
                              const tomPick = isTomorrow(dateStr);
                              return (
                                <button key={dateStr} onClick={() => { if (past) return; setSelectedEventDates(prev => { const n = new Set(prev); sel ? n.delete(dateStr) : n.add(dateStr); return n; }); setAddEventWarning(""); }} style={{ background: sel ? "#f97316" : "none", border: `1px solid ${todPick && !sel ? "#f97316" : sel ? "#f97316" : tomPick ? "rgba(249,115,22,0.5)" : "#2a2a2a"}`, borderRadius: 6, padding: "5px 2px", color: todPick && !sel ? "#f97316" : sel ? "#000" : past ? "#333" : tomPick ? "rgba(249,115,22,0.85)" : "#ccc", fontSize: 12, fontWeight: todPick || sel ? 700 : 400, cursor: past ? "default" : "pointer", textAlign: "center", opacity: past ? 0.4 : 1 }}>{day}</button>
                              );
                            })}
                          </div>
                        );
                      })()}
                      {selectedEventDates.size > 0 && (
                        <div style={{ marginTop: 8, fontSize: 11, color: "#f97316", fontWeight: 600 }}>{selectedEventDates.size} day{selectedEventDates.size !== 1 ? "s" : ""} selected</div>
                      )}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                    <TimePicker value={newEvent.start_time} onChange={v => setNewEvent(p => ({ ...p, start_time: v }))} />
                    <div style={{ display: "flex", alignItems: "center", color: "#555", fontSize: 13, flexShrink: 0 }}>to</div>
                    <TimePicker value={newEvent.end_time} onChange={v => setNewEvent(p => ({ ...p, end_time: v }))} />
                  </div>
                  <select value={newEvent.location} onChange={e => setNewEvent(p => ({ ...p, location: e.target.value }))} style={inputStyle}>
                    {allLocations.map(l => <option key={l.name} value={l.name}>{l.name}</option>)}
                  </select>
                  <input placeholder="Notes (optional)" value={newEvent.notes} onChange={e => setNewEvent(p => ({ ...p, notes: e.target.value }))} style={inputStyle} />

                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: newEvent.is_recurring ? "#f97316" : "#666", marginBottom: 10 }}>
                    <input type="checkbox" checked={newEvent.is_recurring} onChange={e => { setNewEvent(p => ({ ...p, is_recurring: e.target.checked })); if (e.target.checked) setSelectedEventDates(new Set()); }} style={{ accentColor: "#f97316", width: 15, height: 15 }} />
                    Recurring (weekly)
                  </label>
                  {newEvent.is_recurring && (
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 11, color: "#555", marginBottom: 4 }}>Repeat until</div>
                      <input type="date" value={newEvent.recurring_end_date} min={newEvent.date} onChange={e => setNewEvent(p => ({ ...p, recurring_end_date: e.target.value }))} style={{ ...inputStyle, marginBottom: 0 }} />
                    </div>
                  )}

                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: newEvent.is_informational ? "#f97316" : "#666", marginBottom: 10 }}>
                    <input type="checkbox" checked={newEvent.is_informational} onChange={e => setNewEvent(p => ({ ...p, is_informational: e.target.checked }))} style={{ accentColor: "#f97316", width: 15, height: 15 }} />
                    Informational (FYI only — no staff assignment)
                  </label>

                  {!newEvent.is_informational && (
                    <div style={{ background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 8, padding: 12, marginBottom: 10 }}>
                      <div style={{ fontSize: 11, color: "#555", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>Assign Staff</div>
                      {allProfiles.map(pr => (
                        <label key={pr.id} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: selectedStaffIds.has(pr.id) ? "#fff" : "#666", marginBottom: 6 }}>
                          <input type="checkbox" checked={selectedStaffIds.has(pr.id)} onChange={e => setSelectedStaffIds(prev => { const n = new Set(prev); e.target.checked ? n.add(pr.id) : n.delete(pr.id); return n; })} style={{ accentColor: "#f97316", width: 14, height: 14 }} />
                          {pr.first_name ? formatName(pr.first_name, pr.last_name, user.role) : formatName(pr.name, null, user.role)}{pr.role === "manager" ? " (Manager)" : ""}
                        </label>
                      ))}
                    </div>
                  )}

                  {addEventWarning && (
                    <div style={{ background: "#1a0a0a", border: "1px solid #ef4444", borderRadius: 8, padding: "10px 14px", marginBottom: 10, fontSize: 13, color: "#ef4444" }}>⚠ {addEventWarning}</div>
                  )}

                  <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                    <button onClick={saveEvent} style={{ flex: 1, background: "#f97316", border: "none", borderRadius: 8, padding: "11px", color: "#000", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>{editingEventId ? "Save Changes" : "Create Event"}</button>
                    <button onClick={() => { setShowAddEvent(false); setEditingEventId(null); setAddEventWarning(""); setNewEvent({ type: "", typeCustom: "", title: "", date: "", start_time: "", end_time: "", location: "Main", notes: "", is_recurring: false, recurring_end_date: "", is_informational: false }); setSelectedStaffIds(new Set()); setSelectedEventDates(new Set()); }} style={{ flex: 1, background: "#1a1a1a", border: "none", borderRadius: 8, padding: "11px", color: "#666", fontSize: 13, cursor: "pointer" }}>Cancel</button>
                  </div>
                </div>
              )}

              {/* Event detail bottom sheet */}
              {selectedEvent && !showAddEvent && (
                <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 200, display: "flex", alignItems: "flex-end" }} onClick={() => setSelectedEvent(null)}>
                  <div style={{ background: "#161616", border: "1px solid #2a2a2a", borderRadius: "16px 16px 0 0", padding: 24, width: "100%", maxWidth: 640, margin: "0 auto", boxSizing: "border-box", maxHeight: "70vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6, flexWrap: "wrap" }}>
                        <div style={{ fontSize: 10, background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 4, padding: "2px 7px", color: "#888" }}>{selectedEvent.type}</div>
                        {selectedEvent.is_informational && <div style={{ fontSize: 10, background: "#161616", border: "1px solid #2a2a2a", borderRadius: 4, padding: "2px 7px", color: "#555" }}>Informational</div>}
                      </div>
                      <div style={{ fontSize: 19, fontWeight: 700, color: "#fff", marginBottom: 4 }}>{selectedEvent.title}</div>
                      <div style={{ fontSize: 13, color: "#888" }}>{fmtDate(selectedEvent.date)}</div>
                      {selectedEvent.start_time && <div style={{ fontSize: 13, color: "#f97316", marginTop: 2 }}>{fmt(selectedEvent.start_time)} – {fmt(selectedEvent.end_time)}</div>}
                      <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>{selectedEvent.location}</div>
                      {selectedEvent.notes && <div style={{ fontSize: 13, color: "#aaa", marginTop: 8, lineHeight: 1.5 }}>{selectedEvent.notes}</div>}
                    </div>

                    {!selectedEvent.is_informational && (
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 11, color: "#555", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>Staff</div>
                        {eventStaff.filter(es => es.event_id === selectedEvent.id).length === 0
                          ? <div style={{ color: "#444", fontSize: 13 }}>No staff assigned.</div>
                          : eventStaff.filter(es => es.event_id === selectedEvent.id).map(es => (
                              <div key={es.employee_id} style={{ fontSize: 13, color: "#ccc", padding: "5px 0", borderBottom: "1px solid #1a1a1a" }}>{formatName(es.employee_name, null, user.role)}</div>
                            ))
                        }
                      </div>
                    )}

                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => {
                        const staffForEvent = eventStaff.filter(es => es.event_id === selectedEvent.id);
                        setNewEvent({
                          type: ["Class","Birthday Party","Field Day","Camp"].includes(selectedEvent.type) ? selectedEvent.type : "Other",
                          typeCustom: ["Class","Birthday Party","Field Day","Camp"].includes(selectedEvent.type) ? "" : selectedEvent.type,
                          title: selectedEvent.title,
                          date: selectedEvent.date,
                          start_time: selectedEvent.start_time || "",
                          end_time: selectedEvent.end_time || "",
                          location: selectedEvent.location || defaultLoc,
                          notes: selectedEvent.notes || "",
                          is_recurring: selectedEvent.is_recurring || false,
                          recurring_end_date: selectedEvent.recurring_end_date || "",
                          is_informational: selectedEvent.is_informational || false,
                        });
                        setSelectedStaffIds(new Set(staffForEvent.map(es => es.employee_id)));
                        setEditingEventId(selectedEvent.id);
                        setSelectedEvent(null);
                        setShowAddEvent(true);
                        setAddEventWarning("");
                      }} style={{ flex: 1, background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 8, padding: "11px", color: "#aaa", fontSize: 13, cursor: "pointer" }}>Edit</button>
                      <button onClick={() => setConfirmDialog({ message: "Are you sure you want to delete this event?", onConfirm: () => deleteEvent(selectedEvent.id) })} style={{ flex: 1, background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 8, padding: "11px", color: "#ef4444", fontSize: 13, cursor: "pointer" }}>Delete</button>
                      <button onClick={() => setSelectedEvent(null)} style={{ flex: 1, background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 8, padding: "11px", color: "#666", fontSize: 13, cursor: "pointer" }}>Close</button>
                    </div>
                  </div>
                </div>
              )}

              {upcomingEvents.length === 0 && !showAddEvent && (
                <div style={{ color: "#444", textAlign: "center", padding: 40 }}>{eventTypeFilter === "All" ? "No upcoming events." : `No upcoming ${eventTypeFilter.toLowerCase()}.`}</div>
              )}
              {groupedDates.map(date => (
                <div key={date} style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11, color: "#f97316", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 8 }}>{fmtDate(date)}</div>
                  {byDate[date].map(ev => <EventCard key={ev.id} ev={ev} />)}
                </div>
              ))}
            </div>
          );
        })()}

        {/* MANAGER: Requests */}
        {user.role === "manager" && view === "requests" && (() => {
          const pendingAll = requests.filter(r =>
            (r.type === "timeoff" && r.status === "pending") ||
            (r.type === "swap" && (r.status === "pending_manager" || r.status === "pending_target")) ||
            (r.type === "cover" && r.status === "pending")
          );
          const pending = pendingAll.filter(r =>
            r.type !== "cover" || locationFilter === "All" || (r.shift_location || defaultLoc) === locationFilter
          );
          const cutoff = Date.now() - 24 * 60 * 60 * 1000;
          const completed = requests
            .filter(r =>
              ((r.status === "approved" || r.status === "denied" || r.status === "declined") ||
               (r.type === "cover" && r.status === "claimed")) &&
              new Date(r.updated_at || r.created_at).getTime() >= cutoff &&
              (r.type !== "cover" || locationFilter === "All" || (r.shift_location || defaultLoc) === locationFilter)
            )
            .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));

          const statusColor = s => (s === "approved" || s === "claimed") ? "#22c55e" : (s === "denied" || s === "declined") ? "#ef4444" : "#f97316";
          const statusText = s => ({ pending: "Pending", pending_target: "Waiting for response", pending_manager: "Awaiting manager approval", approved: "Approved", denied: "Denied", declined: "Declined", claimed: "Claimed" }[s] || s);

          const RequestCard = ({ r, showActions }) => (
            <div style={{ ...shiftCard, flexDirection: "column", alignItems: "flex-start", gap: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{formatName(r.requester_name, null, user.role)}</div>
                  <div style={{ color: "#888", fontSize: 12, marginTop: 2 }}>{r.type === "cover" ? `Cover request · ${fmtDate(r.shift_date)} · ${r.shift_location || defaultLoc}` : r.type === "swap" ? "⇄ Shift Swap" : "📅 Time Off"}</div>
                  {r.type === "cover" && <div style={{ color: "#888", fontSize: 13, marginTop: 4 }}>{fmt(r.shift_start)}–{fmt(r.shift_end)}{r.target_name ? ` · Covered by ${formatName(r.target_name, null, "employee")}` : ""}</div>}
                  {r.type === "swap" && (() => {
                    const tShift = shifts.find(s => s.id === r.cover_shift_id);
                    return <>
                      <div style={{ color: "#888", fontSize: 13, marginTop: 4 }}>With {formatName(r.target_name, null, user.role)}</div>
                      <div style={{ color: "#888", fontSize: 13, marginTop: 2 }}>{formatName(r.requester_name, null, user.role)} gives up: {fmtDate(r.shift_date)} · {fmt(r.shift_start)}–{fmt(r.shift_end)} · {r.shift_location || defaultLoc}</div>
                      {tShift && <div style={{ color: "#888", fontSize: 13, marginTop: 2 }}>{formatName(r.target_name, null, user.role)} gives up: {fmtDate(tShift.date)} · {fmt(tShift.start_time)}–{fmt(tShift.end_time)} · {tShift.location || defaultLoc}</div>}
                    </>;
                  })()}
                  {r.type === "timeoff" && <div style={{ color: "#888", fontSize: 13, marginTop: 4 }}>{fmtDateRange(r.date, r.end_date)}</div>}
                  {r.reason && <div style={{ color: "#666", fontSize: 12, marginTop: 4 }}>Reason: {r.reason}</div>}
                </div>
                <div style={{ fontSize: 11, color: statusColor(r.status), fontWeight: 600, textTransform: "uppercase", textAlign: "right" }}>{statusText(r.status)}</div>
              </div>
              {showActions && r.type !== "cover" && (
                processingId === r.id
                  ? <div style={{ fontSize: 13, color: "#f97316", textAlign: "center", padding: "9px 0", fontStyle: "italic" }}>Processing…</div>
                  : <div style={{ display: "flex", gap: 8, width: "100%" }}>
                      <button onClick={() => setConfirmDialog({ message: r.type === "timeoff" ? "Are you sure you want to approve this time off request?" : "Are you sure you want to approve this swap request?", confirmColor: "#22c55e", onConfirm: () => handleRequest(r.id, "approved") })} style={{ flex: 1, background: "#22c55e", border: "none", borderRadius: 7, padding: "9px", color: "#000", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Approve</button>
                      <button onClick={() => setConfirmDialog({ message: r.type === "timeoff" ? "Are you sure you want to deny this time off request?" : "Are you sure you want to deny this swap request?", onConfirm: () => handleRequest(r.id, "denied") })} style={{ flex: 1, background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 7, padding: "9px", color: "#666", fontSize: 13, cursor: "pointer" }}>Deny</button>
                    </div>
              )}
              {showActions && r.type === "cover" && (
                <div style={{ fontSize: 12, color: "#555", fontStyle: "italic" }}>Employees claim this directly — no action needed.</div>
              )}
            </div>
          );

          const today = getToday();
          const approvedTimeOff = requests.filter(r =>
            r.type === "timeoff" && r.status === "approved" &&
            (r.end_date || r.date) >= today
          ).sort((a, b) => a.date.localeCompare(b.date));

          return (
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>Staff Requests</div>
              {allLocations.length > 1 && (
                <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
                  {["All", ...allLocations.map(l => l.name)].map(loc => (
                    <button key={loc} onClick={() => setLocationFilter(loc)} style={{ flex: 1, padding: "8px 4px", background: locationFilter === loc ? "#f97316" : "#141414", border: `1px solid ${locationFilter === loc ? "#f97316" : "#2a2a2a"}`, borderRadius: 8, color: locationFilter === loc ? "#000" : "#fff", fontSize: 12, fontWeight: locationFilter === loc ? 700 : 400, cursor: "pointer" }}>{loc}</button>
                  ))}
                </div>
              )}

              {pending.length === 0 && approvedTimeOff.length === 0 && (
                <div style={{ color: "#444", textAlign: "center", padding: 40 }}>No requests yet.</div>
              )}

              {/* Pending */}
              <div style={{ fontSize: 11, color: "#555", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>Pending</div>
              {pending.length === 0
                ? <div style={{ color: "#444", fontSize: 14, marginBottom: 20 }}>No pending requests.</div>
                : pending.map(r => <RequestCard key={r.id} r={r} showActions={r.status === "pending" || r.status === "pending_manager"} />)
              }

              {/* Approved time off — collapsible */}
              {approvedTimeOff.length > 0 && (
                <div style={{ marginTop: 24 }}>
                  <div
                    onClick={() => setShowApproved(v => !v)}
                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", marginBottom: showApproved ? 12 : 0 }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ fontSize: 11, color: "#555", textTransform: "uppercase", letterSpacing: "0.1em" }}>Approved</div>
                      <span style={{ background: "#14532d", color: "#22c55e", fontSize: 11, fontWeight: 700, borderRadius: 20, padding: "2px 10px" }}>{approvedTimeOff.length} approved</span>
                    </div>
                    <span style={{ fontSize: 13, color: "#555" }}>{showApproved ? "Hide ↑" : "Show ↓"}</span>
                  </div>
                  {showApproved && approvedTimeOff.map(r => (
                    <div key={r.id} style={{ ...shiftCard, flexDirection: "column", alignItems: "flex-start", gap: 10 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                        <div>
                          <div style={{ fontWeight: 600 }}>{formatName(r.requester_name, null, user.role)}</div>
                          <div style={{ color: "#888", fontSize: 13, marginTop: 2 }}>{fmtDateRange(r.date, r.end_date)}</div>
                          {r.reason && <div style={{ color: "#666", fontSize: 12, marginTop: 4 }}>Reason: {r.reason}</div>}
                        </div>
                        <span style={{ background: "#14532d", color: "#22c55e", fontSize: 11, fontWeight: 700, borderRadius: 20, padding: "2px 10px", alignSelf: "flex-start", flexShrink: 0 }}>Approved</span>
                      </div>
                      <button
                        onClick={() => setConfirmDialog({
                          message: `Revoke time off?\n${formatName(r.requester_name, null, user.role)}'s approved time off for ${fmtDateRange(r.date, r.end_date)} will be set back to pending.`,
                          confirmColor: "#ef4444",
                          onConfirm: async () => {
                            setProcessingId(r.id);
                            await supabase.from("requests").update({ status: "pending" }).eq("id", r.id);
                            setRequests(prev => prev.map(x => x.id === r.id ? { ...x, status: "pending", updated_at: new Date().toISOString() } : x));
                            setProcessingId(null);
                          },
                        })}
                        disabled={processingId === r.id}
                        style={{ width: "100%", background: "none", border: "1.5px solid #ef4444", borderRadius: 8, padding: "9px", color: processingId === r.id ? "#555" : "#ef4444", fontWeight: 700, fontSize: 13, cursor: processingId === r.id ? "default" : "pointer" }}
                      >
                        {processingId === r.id ? "Processing…" : "Revoke"}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* EMPLOYEE: My Shifts */}
        {user.role === "employee" && view === "schedule" && (() => {
          const weekDays = Array.from({ length: 7 }, (_, i) => {
            const d = new Date(empWeekAnchor + "T00:00:00");
            d.setDate(d.getDate() + i);
            return d.toISOString().split("T")[0];
          });
          const weekLabel = `${new Date(weekDays[0] + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${new Date(weekDays[6] + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
          const filteredMyShifts = myShiftsLocationFilter === "All" ? myShifts : myShifts.filter(s => (s.location || defaultLoc) === myShiftsLocationFilter);
          const myShiftsByDate = {};
          filteredMyShifts.forEach(s => {
            if (!myShiftsByDate[s.date]) myShiftsByDate[s.date] = [];
            myShiftsByDate[s.date].push(s);
          });
          const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
          const navBtn = { background: "none", border: "1px solid #2a2a2a", borderRadius: 8, padding: "7px 14px", color: "#aaa", fontSize: 16, cursor: "pointer" };

          const myEventsByDate = {};
          events.forEach(ev => {
            if (eventStaff.some(es => es.event_id === ev.id && es.employee_id === user.id))
              myEventsByDate[ev.date] = (myEventsByDate[ev.date] || 0) + 1;
          });
          const teamShiftsByDate = {};
          shifts.filter(s => {
            if (!employees.some(e => e.id === s.employee_id)) return false;
            if (myShiftsLocationFilter !== "All" && (s.location || defaultLoc) !== myShiftsLocationFilter) return false;
            return true;
          }).forEach(s => { if (!teamShiftsByDate[s.date]) teamShiftsByDate[s.date] = []; teamShiftsByDate[s.date].push(s); });
          const teamEventsByDate = {};
          events.forEach(ev => { teamEventsByDate[ev.date] = (teamEventsByDate[ev.date] || 0) + 1; });

          return (
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>Schedule</div>

              <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
                {["All", ...allLocations.map(l => l.name)].map(loc => (
                  <button key={loc} onClick={() => setMyShiftsLocationFilter(loc)} style={{ flex: 1, padding: "8px 4px", background: myShiftsLocationFilter === loc ? "#f97316" : "#141414", border: `1px solid ${myShiftsLocationFilter === loc ? "#f97316" : "#2a2a2a"}`, borderRadius: 8, color: myShiftsLocationFilter === loc ? "#000" : "#fff", fontSize: 12, fontWeight: myShiftsLocationFilter === loc ? 700 : 400, cursor: "pointer" }}>{loc}</button>
                ))}
              </div>

              <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
                {["mine", "team", "events"].map(mode => (
                  <button key={mode} onClick={() => setScheduleMode(mode)} style={{ flex: 1, padding: "8px 4px", background: scheduleMode === mode ? "rgba(255,255,255,0.15)" : "#141414", border: `1px solid ${scheduleMode === mode ? "rgba(255,255,255,0.3)" : "#2a2a2a"}`, borderRadius: 8, color: "#fff", fontSize: 12, fontWeight: scheduleMode === mode ? 600 : 400, cursor: "pointer" }}>{mode === "mine" ? "Me" : mode === "team" ? "Team" : "Events"}</button>
                ))}
              </div>

              {/* Week navigation */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <button style={navBtn} onClick={() => {
                  const d = new Date(empWeekAnchor + "T00:00:00"); d.setDate(d.getDate() - 7);
                  const nws = d.toISOString().split("T")[0];
                  setEmpWeekAnchor(nws);
                  if (scheduleMode !== "team") {
                    const days = Array.from({ length: 7 }, (_, i) => { const dd = new Date(nws + "T00:00:00"); dd.setDate(dd.getDate() + i); return dd.toISOString().split("T")[0]; });
                    const td = getToday(); setSelectedDay(days.includes(td) ? td : days[0]);
                  }
                }}>‹</button>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>{weekLabel}</div>
                <button style={navBtn} onClick={() => {
                  const d = new Date(empWeekAnchor + "T00:00:00"); d.setDate(d.getDate() + 7);
                  const nws = d.toISOString().split("T")[0];
                  setEmpWeekAnchor(nws);
                  if (scheduleMode !== "team") {
                    const days = Array.from({ length: 7 }, (_, i) => { const dd = new Date(nws + "T00:00:00"); dd.setDate(dd.getDate() + i); return dd.toISOString().split("T")[0]; });
                    const td = getToday(); setSelectedDay(days.includes(td) ? td : days[0]);
                  }
                }}>›</button>
              </div>

              {/* Week grid + inline panels */}
              {(() => {
                const myApprovedTimeOff = requests.filter(r =>
                  r.type === "timeoff" && r.status === "approved" && r.requester_id === user.id
                );
                return (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 3, marginBottom: 12 }}>
                      {weekDays.map((dateStr, i) => {
                        const dayShifts = myShiftsByDate[dateStr] || [];
                        const hasShift = dayShifts.length > 0;
                        const hasTimeOff = !hasShift && myApprovedTimeOff.some(r => dateStr >= r.date && dateStr <= (r.end_date || r.date));
                        const dayIsToday = isToday(dateStr);
                        const isSelected = dateStr === selectedDay;
                        const day = parseInt(dateStr.split("-")[2]);
                        return (
                          <div key={dateStr}
                            onClick={() => setSelectedDay(dateStr)}
                            style={{
                              background: dayIsToday ? "rgba(249,115,22,0.12)" : isSelected ? "rgba(255,255,255,0.07)" : "#111",
                              border: `1px solid ${dayIsToday ? "rgba(249,115,22,0.5)" : isSelected ? "rgba(255,255,255,0.35)" : "#1e1e1e"}`,
                              borderRadius: 8, minHeight: 80, padding: "6px 4px 8px",
                              cursor: "pointer",
                              opacity: 1,
                              display: "flex", flexDirection: "column", alignItems: "center",
                            }}>
                            <div style={{ textAlign: "center", marginBottom: 4, width: "100%" }}>
                              <div style={{ fontSize: 9, color: dayIsToday ? "#f97316" : "#fff", textTransform: "uppercase" }}>{DOW[new Date(dateStr + "T00:00:00").getDay()]}</div>
                              <div style={{ display: "flex", justifyContent: "center" }}>
                                <div style={dayIsToday ? todayBadge : { fontSize: 13, color: "#fff" }}>{day}</div>
                              </div>
                            </div>
                            {scheduleMode !== "events" && <div style={{ marginTop: "auto", width: 6, height: 6, borderRadius: "50%", background: hasShift ? "#22c55e" : hasTimeOff ? "#22c55e" : "transparent" }} />}
                          </div>
                        );
                      })}
                    </div>

                    {/* Mine inline day panel */}
                    {scheduleMode === "mine" && (() => {
                      const panelDate = selectedDay || getToday();
                      const panelShifts = myShiftsByDate[panelDate] || [];
                      const panelTimeOff = myApprovedTimeOff.some(r => panelDate >= r.date && panelDate <= (r.end_date || r.date));
                      const panelLabel = new Date(panelDate + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }) + (isToday(panelDate) ? " (Today)" : "");
                      return (
                        <div style={{ background: "#111", borderRadius: 12, padding: "16px 16px 20px", marginBottom: 16, border: "1px solid #222" }}>
                          <div style={{ fontSize: 15, fontWeight: 700, color: "#fff", marginBottom: 14 }}>{panelLabel}</div>
                          <div style={{ fontSize: 11, color: "#fff", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 600, marginBottom: 10 }}>Your Shift</div>
                          {panelShifts.length > 0 ? panelShifts.map(s => {
                            const isConfirming = coverConfirmShiftId === s.id;
                            const isRequested = requestedCoverIds.has(s.id);
                            const shiftPast = new Date() >= new Date(`${s.date}T${s.start_time}`);
                            return (
                              <div key={s.id} style={{ background: "#111", borderRadius: 10, padding: "12px 14px", marginBottom: 8 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                  <div>
                                    <div style={{ fontSize: 17, fontWeight: 700, color: "#fff" }}>{fmt(s.start_time)} – {fmt(s.end_time)}</div>
                                    <div style={{ fontSize: 13, color: "#fff", marginTop: 4 }}>{s.location || defaultLoc}</div>
                                    {s.note && <div style={{ fontSize: 12, color: "#f97316", marginTop: 4 }}>{s.note}</div>}
                                  </div>
                                  {!shiftPast && (isRequested ? (
                                    <div style={{ fontSize: 12, color: "#f97316", marginLeft: 12, flexShrink: 0, marginTop: 2 }}>Requested</div>
                                  ) : !isConfirming && (
                                    <button onClick={() => setCoverConfirmShiftId(s.id)} style={{ background: "#f97316", border: "none", borderRadius: 7, padding: "6px 10px", color: "#000", fontSize: 11, fontWeight: 700, cursor: "pointer", flexShrink: 0, marginLeft: 12 }}>Request Cover</button>
                                  ))}
                                </div>
                                {isConfirming && !shiftPast && (
                                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #2a2a2a" }}>
                                    <div style={{ fontSize: 13, color: "#fff", marginBottom: 10 }}>Are you sure you want to request a cover?</div>
                                    {coverConfirmError && <div style={{ fontSize: 12, color: "#ef4444", marginBottom: 8 }}>{coverConfirmError}</div>}
                                    <div style={{ display: "flex", gap: 8 }}>
                                      <button onClick={async () => {
                                        setCoverConfirmError("");
                                        const ok = await submitCoverForShift(s);
                                        if (ok) { setRequestedCoverIds(prev => new Set([...prev, s.id])); setCoverConfirmShiftId(null); }
                                        else { setCoverConfirmError("Failed to submit. Please try again."); }
                                      }} style={{ flex: 1, background: "#22c55e", border: "none", borderRadius: 7, padding: "8px", color: "#000", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Yes</button>
                                      <button onClick={() => { setCoverConfirmShiftId(null); setCoverConfirmError(""); }} style={{ flex: 1, background: "#ef4444", border: "none", borderRadius: 7, padding: "8px", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>No</button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          }) : panelTimeOff ? (
                            <div style={{ fontSize: 15, color: "#22c55e" }}>Time Off</div>
                          ) : (
                            <div style={{ fontSize: 15, color: "#fff" }}>You're off</div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Team inline day panel */}
                    {scheduleMode === "team" && (() => {
                      const panelDate = selectedDay || getToday();
                      const panelLabel = new Date(panelDate + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }) + (isToday(panelDate) ? " (Today)" : "");
                      const dayShiftsAll = shifts.filter(s => s.date === panelDate && employees.some(e => e.id === s.employee_id));
                      const myShiftForDay = dayShiftsAll.find(s => s.employee_id === user.id);
                      const myLoc = myShiftForDay ? (myShiftForDay.location || defaultLoc) : null;
                      const allLocs = [...new Set(dayShiftsAll.map(s => s.location || defaultLoc))];
                      const orderedLocs = myLoc ? [myLoc, ...allLocs.filter(l => l !== myLoc)] : allLocs;
                      return (
                        <div style={{ background: "#111", borderRadius: 12, padding: "16px 16px 20px", marginBottom: 16, border: "1px solid #222" }}>
                          <div style={{ fontSize: 15, fontWeight: 700, color: "#fff", marginBottom: 14 }}>{panelLabel}</div>
                          {dayShiftsAll.length === 0 ? (
                            <div style={{ fontSize: 15, color: "#fff" }}>No shifts scheduled</div>
                          ) : orderedLocs.map(loc => {
                            const locShifts = dayShiftsAll
                              .filter(s => (s.location || defaultLoc) === loc)
                              .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
                            return (
                              <div key={loc} style={{ marginBottom: 16 }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", marginBottom: 8 }}>{loc}</div>
                                {locShifts.map(s => {
                                  const emp = allProfiles.find(p => p.id === s.employee_id);
                                  const isMe = s.employee_id === user.id;
                                  return (
                                    <div key={s.id} style={{ background: "#111", borderRadius: 10, padding: "10px 14px", marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                      <div style={{ fontSize: 14, color: "#fff" }}>
                                        {emp ? formatName(emp.first_name || emp.name, emp.last_name, user.role) : "Unknown"}
                                        {isMe && <span style={{ color: "#f97316", marginLeft: 6 }}>You</span>}
                                      </div>
                                      <div style={{ fontSize: 13, color: "#fff", flexShrink: 0, marginLeft: 8 }}>{fmt(s.start_time)} – {fmt(s.end_time)}</div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}

                    {/* Events inline day panel */}
                    {scheduleMode === "events" && (() => {
                      const panelDate = selectedDay || getToday();
                      const panelLabel = new Date(panelDate + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }) + (isToday(panelDate) ? " (Today)" : "");
                      const dayAllEvents = events
                        .filter(ev => ev.date === panelDate)
                        .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
                      const myShiftForDay = shifts.find(s => s.date === panelDate && s.employee_id === user.id);
                      const myLoc = myShiftForDay ? (myShiftForDay.location || defaultLoc) : null;
                      const allLocs = [...new Set(dayAllEvents.map(ev => ev.location || defaultLoc))];
                      const orderedLocs = myLoc ? [myLoc, ...allLocs.filter(l => l !== myLoc)] : allLocs;
                      const fmtStaffName = (p) => {
                        if (p.first_name && p.last_name) return `${p.first_name} ${p.last_name[0]}.`;
                        const parts = (p.name || "").trim().split(/\s+/);
                        return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : parts[0];
                      };
                      return (
                        <div style={{ background: "#111", borderRadius: 12, padding: "16px 16px 20px", marginBottom: 16, border: "1px solid #222" }}>
                          <div style={{ fontSize: 15, fontWeight: 700, color: "#fff", marginBottom: 14 }}>{panelLabel}</div>
                          {dayAllEvents.length === 0 ? (
                            <div style={{ fontSize: 15, color: "#fff" }}>No events scheduled</div>
                          ) : orderedLocs.map(loc => {
                            const locEvents = dayAllEvents.filter(ev => (ev.location || defaultLoc) === loc);
                            if (locEvents.length === 0) return null;
                            return (
                              <div key={loc} style={{ marginBottom: 16 }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", marginBottom: 8 }}>{loc}</div>
                                {locEvents.map(ev => {
                                  const isAssigned = eventStaff.some(es => es.event_id === ev.id && es.employee_id === user.id);
                                  const assignedStaff = eventStaff
                                    .filter(es => es.event_id === ev.id)
                                    .map(es => allProfiles.find(p => p.id === es.employee_id))
                                    .filter(Boolean);
                                  return (
                                    <div key={ev.id} style={{ background: "#1a1a1a", borderRadius: 10, padding: "12px 14px", marginBottom: 8, border: `1px solid ${isAssigned ? "#f97316" : "transparent"}` }}>
                                      <div style={{ fontSize: 15, fontWeight: 600, color: "#fff", marginBottom: 3 }}>{ev.title}</div>
                                      <div style={{ fontSize: 13, color: "#fff", marginBottom: assignedStaff.length > 0 ? 8 : 0 }}>
                                        {ev.start_time ? `${fmt(ev.start_time)}${ev.end_time ? ` – ${fmt(ev.end_time)}` : ""}` : ""}
                                      </div>
                                      {assignedStaff.length > 0 && (
                                        <div style={{ fontSize: 12 }}>
                                          {assignedStaff.map((p, idx) => (
                                            <span key={p.id}>
                                              {idx > 0 && <span style={{ color: "#fff" }}> · </span>}
                                              <span style={{ color: p.id === user.id ? "#f97316" : "#fff" }}>{fmtStaffName(p)}</span>
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </>
                );
              })()}
            </div>
          );
        })()}

        {/* EMPLOYEE: Team Calendar */}
        {user.role === "employee" && view === "team" && (() => {
          const { year, month } = calDate;
          const firstDow = new Date(year, month, 1).getDay();
          const daysInMonth = new Date(year, month + 1, 0).getDate();
          const monthLabel = new Date(year, month, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });

          const cells = [];
          for (let i = 0; i < firstDow; i++) cells.push(null);
          for (let d = 1; d <= daysInMonth; d++) {
            cells.push(`${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
          }

          const filteredTeamShifts = empTeamLocationFilter === "All" ? sortedShifts : sortedShifts.filter(s => (s.location || defaultLoc) === empTeamLocationFilter);
          const shiftsByDate = {};
          filteredTeamShifts.forEach(s => {
            if (!shiftsByDate[s.date]) shiftsByDate[s.date] = [];
            shiftsByDate[s.date].push(s);
          });

          const selectedShifts = selectedDay
            ? [...(shiftsByDate[selectedDay] || [])].sort((a, b) => a.start_time.localeCompare(b.start_time))
            : [];

          const navBtn = { background: "none", border: "1px solid #2a2a2a", borderRadius: 8, padding: "7px 14px", color: "#aaa", fontSize: 16, cursor: "pointer" };

          return (
            <div>
              {/* Location filter */}
              <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
                {["All", ...allLocations.map(l => l.name)].map(loc => (
                  <button key={loc} onClick={() => { setEmpTeamLocationFilter(loc); setSelectedDay(null); }} style={{ flex: 1, padding: "8px 4px", background: empTeamLocationFilter === loc ? "#f97316" : "#141414", border: `1px solid ${empTeamLocationFilter === loc ? "#f97316" : "#2a2a2a"}`, borderRadius: 8, color: empTeamLocationFilter === loc ? "#000" : "#fff", fontSize: 12, fontWeight: empTeamLocationFilter === loc ? 700 : 400, cursor: "pointer" }}>{loc}</button>
                ))}
              </div>

              {/* Month navigation */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <button style={navBtn} onClick={() => { const d = new Date(year, month - 1, 1); setCalDate({ year: d.getFullYear(), month: d.getMonth() }); setSelectedDay(null); }}>‹</button>
                <div style={{ fontWeight: 700, fontSize: 16 }}>{monthLabel}</div>
                <button style={navBtn} onClick={() => { const d = new Date(year, month + 1, 1); setCalDate({ year: d.getFullYear(), month: d.getMonth() }); setSelectedDay(null); }}>›</button>
              </div>

              {/* Day-of-week headers */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 3, marginBottom: 3 }}>
                {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map(d => (
                  <div key={d} style={{ textAlign: "center", fontSize: 11, color: "#fff", paddingBottom: 4 }}>{d}</div>
                ))}
              </div>

              {/* Calendar grid */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 3 }}>
                {cells.map((dateStr, i) => {
                  if (!dateStr) return <div key={`blank-${i}`} />;
                  const dayShifts = shiftsByDate[dateStr] || [];
                  const hasShifts = dayShifts.length > 0;
                  const dayIsToday = isToday(dateStr);
                  const dayIsTomorrow = isTomorrow(dateStr);
                  const isSelected = dateStr === selectedDay;
                  const isMine = dayShifts.some(s => s.employee_id === user.id);
                  const day = parseInt(dateStr.split("-")[2]);

                  return (
                    <div
                      key={dateStr}
                      onClick={() => hasShifts && setSelectedDay(isSelected ? null : dateStr)}
                      style={{
                        background: dayIsToday ? "#111" : isSelected ? "#f97316" : dayIsTomorrow ? "rgba(249,115,22,0.05)" : "#111",
                        border: `1px solid ${dayIsToday ? "rgba(249,115,22,0.4)" : isSelected ? "#f97316" : dayIsTomorrow ? "rgba(249,115,22,0.3)" : "#1e1e1e"}`,
                        borderRadius: 8,
                        minHeight: 50,
                        padding: "6px 3px 4px",
                        cursor: hasShifts ? "pointer" : "default",
                        textAlign: "center",
                        opacity: 1,
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "center" }}>
                        <div style={dayIsToday ? todayBadge : dayIsTomorrow ? tomorrowBadge : { fontSize: 13, color: isSelected ? "#000" : "#fff" }}>{day}</div>
                      </div>
                      {hasShifts && !isSelected && (
                        <div style={{ marginTop: 4, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                          {isMine && <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#f97316" }} />}
                          <div style={{ fontSize: 9, color: "#fff", lineHeight: 1 }}>{dayShifts.length}</div>
                        </div>
                      )}
                      {isSelected && hasShifts && (
                        <div style={{ fontSize: 9, color: "rgba(0,0,0,0.6)", marginTop: 2 }}>{dayShifts.length}</div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 12, marginBottom: selectedDay ? 20 : 0 }}>
                <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#f97316" }} />
                <span style={{ fontSize: 11, color: "#fff" }}>You're scheduled · number = total shifts that day</span>
              </div>

              {/* Selected day detail */}
              {selectedDay && (
                <div>
                  <div style={{ fontSize: 12, letterSpacing: "0.15em", color: "#f97316", textTransform: "uppercase", marginBottom: 12 }}>
                    {fmtDate(selectedDay)}
                  </div>
                  {selectedShifts.length === 0 ? (
                    <div style={{ color: "#ffffff", fontSize: 14 }}>No shifts.</div>
                  ) : (() => {
                    const ShiftRow = s => (
                      <div key={s.id} style={{ ...shiftCard, borderLeft: s.employee_id === user.id ? "3px solid #f97316" : "3px solid #1e1e1e" }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ fontWeight: 600, fontSize: 15 }}>{formatName(s.employee_name, null, user.role)}</div>
                            {s.employee_id === user.id && <span style={{ fontSize: 10, background: "#f97316", color: "#000", borderRadius: 4, padding: "2px 6px", fontWeight: 700 }}>YOU</span>}
                          </div>
                          <div style={{ color: "#fff", fontSize: 13, marginTop: 4 }}>{fmt(s.start_time)} – {fmt(s.end_time)}</div>
                          {s.note && <div style={{ color: "#f97316", fontSize: 12, marginTop: 3 }}>{s.note}</div>}
                        </div>
                      </div>
                    );
                    if (empTeamLocationFilter === "All" && allLocations.length > 1) {
                      const locGroups = allLocations.map(l => ({ name: l.name, shifts: selectedShifts.filter(s => (s.location || allLocations[0]?.name) === l.name) })).filter(g => g.shifts.length > 0);
                      if (locGroups.length > 0) return (
                        <>
                          {locGroups.map((g, i) => (
                            <div key={g.name}>
                              <div style={{ fontSize: 10, color: "#fff", textDecoration: "underline", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 8, marginTop: i > 0 ? 12 : 0 }}>{g.name}</div>
                              {g.shifts.map(ShiftRow)}
                            </div>
                          ))}
                        </>
                      );
                    }
                    return selectedShifts.map(ShiftRow);
                  })()}
                </div>
              )}
            </div>
          );
        })()}

        {/* EMPLOYEE: Events */}
        {user.role === "employee" && view === "events" && (() => {
          const KNOWN_TYPES = ["Class", "Birthday Party", "Field Day", "Camp"];
          const EVENT_FILTERS = ["All", "Classes", "Birthday Parties", "Field Days", "Camps", "Other"];
          const filterMatch = (ev) => {
            if (eventTypeFilter === "All") return true;
            if (eventTypeFilter === "Classes") return ev.type === "Class";
            if (eventTypeFilter === "Birthday Parties") return ev.type === "Birthday Party";
            if (eventTypeFilter === "Field Days") return ev.type === "Field Day";
            if (eventTypeFilter === "Camps") return ev.type === "Camp";
            if (eventTypeFilter === "Other") return !KNOWN_TYPES.includes(ev.type);
            return true;
          };
          const nowDateEmp = getToday();
          const in30Days = (() => {
            const d = new Date(nowDateEmp + "T00:00:00");
            d.setDate(d.getDate() + 30);
            return d.toISOString().split("T")[0];
          })();
          const upcomingEvents = events
            .filter(ev => ev.date >= nowDateEmp && ev.date <= in30Days && filterMatch(ev))
            .sort((a, b) => a.date.localeCompare(b.date) || (a.start_time || "").localeCompare(b.start_time || ""));
          const byDate = {};
          upcomingEvents.forEach(ev => { if (!byDate[ev.date]) byDate[ev.date] = []; byDate[ev.date].push(ev); });
          const dates = Object.keys(byDate).sort();

          return (
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 14 }}>Events</div>
              <div style={{ display: "flex", gap: 6, overflowX: "auto", marginBottom: 16, paddingBottom: 2 }}>
                {EVENT_FILTERS.map(f => (
                  <button key={f} onClick={() => setEventTypeFilter(f)} style={{ flexShrink: 0, padding: "7px 12px", background: eventTypeFilter === f ? "#f97316" : "#141414", border: `1px solid ${eventTypeFilter === f ? "#f97316" : "#2a2a2a"}`, borderRadius: 8, color: eventTypeFilter === f ? "#000" : "#fff", fontSize: 12, fontWeight: eventTypeFilter === f ? 700 : 400, cursor: "pointer" }}>{f}</button>
                ))}
              </div>
              {dates.length === 0 && (
                <div style={{ color: "#ffffff", textAlign: "center", padding: 40 }}>{eventTypeFilter === "All" ? "No upcoming events in the next 30 days." : `No upcoming ${eventTypeFilter.toLowerCase()} in the next 30 days.`}</div>
              )}
              {dates.map(date => (
                <div key={date} style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11, color: "#f97316", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 8 }}>{fmtDate(date)}</div>
                  {byDate[date].map(ev => {
                    const isAssigned = eventStaff.some(es => es.event_id === ev.id && es.employee_id === user.id);
                    return (
                      <div key={ev.id} style={{ background: isAssigned ? "#1a0f00" : "#111", border: `1px solid ${isAssigned ? "#f97316" : "#1e1e1e"}`, borderLeft: `3px solid ${isAssigned ? "#f97316" : "#2a2a2a"}`, borderRadius: 10, padding: "12px 14px", marginBottom: 8 }}>
                        <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4, flexWrap: "wrap" }}>
                          <div style={{ fontSize: 10, background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 4, padding: "2px 6px", color: "#aaaaaa" }}>{ev.type}</div>
                          {ev.is_informational && <div style={{ fontSize: 10, color: "#aaaaaa" }}>Info</div>}
                          {isAssigned && <div style={{ fontSize: 10, background: "#f97316", color: "#000", borderRadius: 4, padding: "2px 6px", fontWeight: 700 }}>You're on</div>}
                        </div>
                        <div style={{ fontWeight: 600, fontSize: 15, color: "#fff" }}>{ev.title}</div>
                        {ev.start_time && <div style={{ color: "#fff", fontSize: 13, marginTop: 3 }}>{fmt(ev.start_time)} – {fmt(ev.end_time)}</div>}
                        <div style={{ fontSize: 12, color: "#fff", marginTop: 2 }}>{ev.location}</div>
                        {ev.notes && <div style={{ fontSize: 12, color: "#fff", marginTop: 4, lineHeight: 1.5 }}>{ev.notes}</div>}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          );
        })()}

        {/* EMPLOYEE: My Requests */}
        {user.role === "employee" && view === "requests" && (() => {
          const openCoversFiltered = openCovers.filter(r => r.requester_id !== user.id && new Date() < new Date(`${r.shift_date}T${r.shift_start}`));
          const incomingSwaps = requests.filter(r => r.type === "swap" && r.target_id === user.id && r.status === "pending_target");
          // Normalize a time string like '9:00' or '09:00' to 'HH:MM' for reliable Date parsing
          const padTime = t => t ? t.split(":").map((p, i) => i === 0 ? p.padStart(2, "0") : p).join(":") : "00:00";
          const now = new Date();
          const isCoverExpired = r => {
            if (r.type !== "cover" || !r.shift_date || !r.shift_end) return false;
            const expiry = new Date(`${r.shift_date}T${padTime(r.shift_end)}`);
            console.log(`[Cover expiry] id=${r.id} expiry=${expiry.toLocaleString()} now=${now.toLocaleString()} expired=${now >= expiry}`);
            return now >= expiry;
          };
          const isTimeOffExpired = r => {
            if (r.type !== "timeoff") return false;
            const dateToCheck = r.end_date || r.date;
            if (!dateToCheck) return false;
            return dateToCheck < getToday();
          };
          const myPending = requests.filter(r =>
            r.requester_id === user.id &&
            (r.status === "pending" || r.status === "pending_target" || r.status === "pending_manager") &&
            !isCoverExpired(r) &&
            !isTimeOffExpired(r)
          );
          // Expired covers from requests state (visible via RLS)
          const myPendingCoversExpired = requests.filter(r =>
            r.requester_id === user.id && r.type === "cover" && r.status === "pending" && isCoverExpired(r)
          );
          // Own cover requests may not appear in `requests` due to RLS — pull from openCovers and dedup
          const myOwnCovers = openCovers.filter(r => r.requester_id === user.id && r.status === "pending");
          const myPendingIds = new Set(myPending.map(r => r.id));
          const myPendingExpiredIds = new Set(myPendingCoversExpired.map(r => r.id));
          const myOwnCoversPending = myOwnCovers.filter(r => {
            const expiry = new Date(`${r.shift_date}T${padTime(r.shift_end)}`);
            console.log(`[Cover pending check] id=${r.id} expiry=${expiry.toLocaleString()} now=${now.toLocaleString()} expired=${now >= expiry}`);
            return !myPendingIds.has(r.id) && !myPendingExpiredIds.has(r.id) && now < expiry;
          });
          const myOwnCoversExpired = myOwnCovers.filter(r => {
            const expiry = new Date(`${r.shift_date}T${padTime(r.shift_end)}`);
            console.log(`[Cover expired check] id=${r.id} expiry=${expiry.toLocaleString()} now=${now.toLocaleString()} expired=${now >= expiry}`);
            return !myPendingIds.has(r.id) && !myPendingExpiredIds.has(r.id) && now >= expiry;
          });
          // Expired swaps: requester-only, either shift's start time has passed
          const isSwapExpired = r => {
            if (r.type !== "swap" || r.requester_id !== user.id) return false;
            if (!r.shift_date || !r.shift_start) return false;
            const myShiftStart = new Date(`${r.shift_date}T${padTime(r.shift_start)}`);
            if (now >= myShiftStart) return true;
            const theirShift = shifts.find(s => s.id === r.cover_shift_id);
            if (theirShift && now >= new Date(`${theirShift.date}T${padTime(theirShift.start_time)}`)) return true;
            return false;
          };
          const expiredSwaps = myPending.filter(r => isSwapExpired(r));
          const expiredSwapIds = new Set(expiredSwaps.map(r => r.id));

          const pending = [...incomingSwaps, ...myPending.filter(r => !expiredSwapIds.has(r.id)), ...myOwnCoversPending];

          const cutoff = Date.now() - 24 * 60 * 60 * 1000;
          const completed = [
            ...requests
              .filter(r =>
                (r.status === "approved" || r.status === "denied" || r.status === "declined" || r.status === "claimed" || r.status === "revoked") &&
                (r.requester_id === user.id || r.target_id === user.id) &&
                new Date(r.updated_at || r.created_at).getTime() >= cutoff
              ),
            ...myOwnCoversExpired.map(r => ({ ...r, _notFulfilled: true })),
            ...myPendingCoversExpired.map(r => ({ ...r, _notFulfilled: true })),
            ...expiredSwaps.map(r => ({ ...r, _swapExpired: true })),
          ].sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));

          const statusColor = s => (s === "approved" || s === "claimed") ? "#22c55e" : (s === "denied" || s === "declined") ? "#ef4444" : (s === "revoked" || s === "cancelled") ? "#aaaaaa" : "#f97316";
          const myStatusText = r => {
            if (r._notFulfilled) return "No Coverage Found";
            if (r._swapExpired) return "Expired";
            if (r._cancelled || (r.status === "revoked" && r.requester_id === user.id)) return "Cancelled";
            if (r.type === "cover") return { pending: "Open · Awaiting claims", claimed: "Claimed", pending_manager: "Awaiting approval" }[r.status] || r.status;
            if (r.type !== "swap") return { pending: "Pending", approved: "Approved", denied: "Denied", revoked: "Revoked" }[r.status] || r.status;
            return { pending_target: `Waiting for ${formatName(r.target_name, null, "employee")}`, pending_manager: "Awaiting manager approval", approved: "Approved", denied: "Denied", declined: `Declined by ${formatName(r.target_name, null, "employee")}` }[r.status] || r.status;
          };

          return (
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 20 }}>Requests</div>

              <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
                <button onClick={() => { setShowSwap(s => !s); setShowTimeOff(false); }} style={{ flex: 1, background: showSwap ? "#f97316" : "#1a1a1a", border: `1px solid ${showSwap ? "#f97316" : "#2a2a2a"}`, borderRadius: 8, padding: "11px", color: showSwap ? "#000" : "#fff", fontWeight: showSwap ? 700 : 400, fontSize: 13, cursor: "pointer" }}>Request Swap</button>
                <button onClick={() => { setShowTimeOff(s => !s); setShowSwap(false); }} style={{ flex: 1, background: showTimeOff ? "#f97316" : "#1a1a1a", border: `1px solid ${showTimeOff ? "#f97316" : "#2a2a2a"}`, borderRadius: 8, padding: "11px", color: showTimeOff ? "#000" : "#fff", fontWeight: showTimeOff ? 700 : 400, fontSize: 13, cursor: "pointer" }}>Time Off</button>
              </div>

              {showSwap && (() => {
                const swapTimeOff = requests.filter(r => r.type === "timeoff" && r.status === "approved" && r.requester_id === user.id);
                const isOnTimeOff = date => swapTimeOff.some(r => date >= r.date && date <= (r.end_date || r.date));
                const otherShifts = shifts
                  .filter(s => s.employee_id !== user.id && s.date >= getToday() && employees.some(e => e.id === s.employee_id) && !isOnTimeOff(s.date))
                  .sort((a, b) => a.date.localeCompare(b.date) || a.start_time.localeCompare(b.start_time));
                const byEmp = {};
                otherShifts.forEach(s => {
                  if (!byEmp[s.employee_id]) byEmp[s.employee_id] = { name: s.employee_name, list: [] };
                  byEmp[s.employee_id].list.push(s);
                });
                const empGroups = Object.entries(byEmp);
                const selTarget = swapRequest.targetShiftId ? shifts.find(s => s.id === swapRequest.targetShiftId) : null;
                const selMine = swapRequest.myShiftId ? myShifts.find(s => s.id === swapRequest.myShiftId) : null;
                const canSubmit = !!(selTarget && selMine);
                const rowStyle = (sel) => ({
                  padding: "10px 12px", borderRadius: 8, marginBottom: 6, cursor: "pointer",
                  background: sel ? "#f9731618" : "#0a0a0a",
                  border: `1px solid ${sel ? "#f97316" : "#2a2a2a"}`,
                  fontSize: 13, color: sel ? "#f97316" : "#ccc",
                });
                return (
                  <div style={{ background: "#141414", border: "1px solid #222", borderRadius: 12, padding: 20, marginBottom: 20 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: "#f97316" }}>Request Shift Swap</div>
                    {empGroups.length === 0 ? (
                      <div style={{ color: "#ffffff", fontSize: 13, textAlign: "center", padding: "16px 0" }}>No other employees have upcoming shifts.</div>
                    ) : (
                      <>
                        <div style={{ fontSize: 11, color: "#fff", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>
                          {selTarget ? "You get:" : "Tap a shift you want"}
                        </div>
                        {selTarget ? (
                          <div onClick={() => setSwapRequest(p => ({ ...p, targetShiftId: "", myShiftId: "" }))} style={{ ...rowStyle(true), display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 13 }}>{formatName(selTarget.employee_name, null, user.role)}</div>
                              <div style={{ fontSize: 12, marginTop: 2, color: "#f9731699" }}>{fmtDate(selTarget.date)} · {fmt(selTarget.start_time)}–{fmt(selTarget.end_time)} · {selTarget.location || defaultLoc}</div>
                            </div>
                            <div style={{ fontSize: 11, color: "#fff", flexShrink: 0 }}>tap to change</div>
                          </div>
                        ) : (
                          empGroups.map(([empId, empData]) => (
                            <div key={empId} style={{ marginBottom: 12 }}>
                              <div style={{ fontSize: 12, color: "#ffffff", fontWeight: 600, marginBottom: 6 }}>{formatName(empData.name, null, user.role)}</div>
                              {empData.list.map(s => (
                                <div key={s.id} onClick={() => setSwapRequest(p => ({ ...p, targetShiftId: s.id, myShiftId: "" }))} style={rowStyle(false)}>
                                  {fmtDate(s.date)} · {fmt(s.start_time)}–{fmt(s.end_time)} · {s.location || defaultLoc}
                                </div>
                              ))}
                            </div>
                          ))
                        )}
                        {selTarget && (
                          <>
                            <div style={{ fontSize: 11, color: "#fff", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10, marginTop: 16 }}>
                              {selMine ? "You give up:" : "Tap the shift you're trading away"}
                            </div>
                            {selMine ? (
                              <div onClick={() => setSwapRequest(p => ({ ...p, myShiftId: "" }))} style={{ ...rowStyle(true), display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <div style={{ fontSize: 13 }}>{fmtDate(selMine.date)} · {fmt(selMine.start_time)}–{fmt(selMine.end_time)} · {selMine.location || defaultLoc}</div>
                                <div style={{ fontSize: 11, color: "#fff", flexShrink: 0 }}>tap to change</div>
                              </div>
                            ) : myShifts.filter(s => !isOnTimeOff(s.date)).length === 0 ? (
                              <div style={{ color: "#ffffff", fontSize: 13 }}>You have no upcoming shifts to trade.</div>
                            ) : (
                              myShifts.filter(s => !isOnTimeOff(s.date)).map(s => (
                                <div key={s.id} onClick={() => setSwapRequest(p => ({ ...p, myShiftId: s.id }))} style={rowStyle(false)}>
                                  {fmtDate(s.date)} · {fmt(s.start_time)}–{fmt(s.end_time)} · {s.location || defaultLoc}
                                </div>
                              ))
                            )}
                          </>
                        )}
                        {canSubmit && (
                          <div style={{ marginTop: 16, padding: 14, background: "#0f0f0f", border: "1px solid #2a2a2a", borderRadius: 10 }}>
                            <div style={{ fontSize: 11, color: "#aaaaaa", marginBottom: 10, letterSpacing: "0.05em" }}>⇄ TRADE SUMMARY</div>
                            <div style={{ fontSize: 13, color: "#ccc", marginBottom: 6 }}>
                              <span style={{ color: "#cccccc" }}>You give up: </span>{fmtDate(selMine.date)} · {fmt(selMine.start_time)}–{fmt(selMine.end_time)} · {selMine.location || defaultLoc}
                            </div>
                            <div style={{ fontSize: 13, color: "#ccc" }}>
                              <span style={{ color: "#cccccc" }}>You get: </span>{fmtDate(selTarget.date)} · {fmt(selTarget.start_time)}–{fmt(selTarget.end_time)} · {selTarget.location || defaultLoc} · {formatName(selTarget.employee_name, null, user.role)}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {canSubmit && (
                      <input placeholder="Reason (optional)" value={swapRequest.reason} onChange={e => setSwapRequest(p => ({ ...p, reason: e.target.value }))} style={{ ...inputStyle, marginTop: 12 }} />
                    )}
                    {confirmSwap ? (
                      <div style={{ marginTop: 12, background: "#0f0f0f", border: "1px solid #2a2a2a", borderRadius: 10, padding: 14 }}>
                        <div style={{ fontSize: 13, color: "#fff", marginBottom: 12, textAlign: "center" }}>Submit this swap?</div>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button onClick={() => { setConfirmSwap(false); submitSwap(); }} style={{ flex: 1, background: "#22c55e", border: "none", borderRadius: 8, padding: "11px", color: "#000", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Yes</button>
                          <button onClick={() => setConfirmSwap(false)} style={{ flex: 1, background: "#ef4444", border: "none", borderRadius: 8, padding: "11px", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>No</button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                        <button onClick={() => { if (canSubmit && !swapSubmitting) setConfirmSwap(true); }} disabled={!canSubmit || swapSubmitting} style={{ flex: 1, background: canSubmit && !swapSubmitting ? "#f97316" : "#2a2a2a", border: "none", borderRadius: 8, padding: "11px", color: canSubmit && !swapSubmitting ? "#000" : "#aaaaaa", fontWeight: 700, fontSize: 13, cursor: canSubmit && !swapSubmitting ? "pointer" : "default" }}>{swapSubmitting ? "Submitting…" : "Submit Trade"}</button>
                        <button onClick={() => { setShowSwap(false); setSwapRequest({ targetShiftId: "", myShiftId: "", reason: "" }); setConfirmSwap(false); }} style={{ flex: 1, background: "#1a1a1a", border: "none", borderRadius: 8, padding: "11px", color: "#fff", fontSize: 13, cursor: "pointer" }}>Cancel</button>
                      </div>
                    )}
                  </div>
                );
              })()}

              {showTimeOff && (
                <div style={{ background: "#141414", border: "1px solid #222", borderRadius: 12, padding: 20, marginBottom: 20 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14, color: "#f97316" }}>Request Time Off</div>
                  {(() => {
                    const existingTimeOff = requests.filter(r =>
                      r.type === "timeoff" && (r.status === "pending" || r.status === "approved") && r.requester_id === user.id
                    );
                    const conflictsWithExisting = date => existingTimeOff.some(r => date >= r.date && date <= (r.end_date || r.date));
                    const endDateMin = (() => {
                      if (!timeOffRequest.startDate) return "";
                      const d = new Date(timeOffRequest.startDate + "T00:00:00");
                      d.setDate(d.getDate() + 1);
                      return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
                    })();
                    return (
                      <>
                        <div style={{ display: "flex", gap: 8 }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 11, color: "#fff", marginBottom: 4 }}>Start Date</div>
                            <input type="date" value={timeOffRequest.startDate} min={getToday()} onChange={e => {
                              const val = e.target.value;
                              if (val && conflictsWithExisting(val)) {
                                setTimeOffDateError("This date overlaps with an existing time off request.");
                                setTimeOffRequest(p => ({ ...p, startDate: "", endDate: "" }));
                              } else {
                                setTimeOffDateError("");
                                setTimeOffRequest(p => ({ ...p, startDate: val, endDate: "" }));
                              }
                            }} style={{ ...inputStyle, marginBottom: 0 }} />
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 11, color: "#fff", marginBottom: 4 }}>End Date</div>
                            <input type="date" value={timeOffRequest.endDate} disabled={!timeOffRequest.startDate} min={endDateMin} onChange={e => {
                              const val = e.target.value;
                              if (val && conflictsWithExisting(val)) {
                                setTimeOffDateError("This date overlaps with an existing time off request.");
                                setTimeOffRequest(p => ({ ...p, endDate: "" }));
                              } else {
                                setTimeOffDateError("");
                                setTimeOffRequest(p => ({ ...p, endDate: val }));
                              }
                            }} style={{ ...inputStyle, marginBottom: 0, opacity: timeOffRequest.startDate ? 1 : 0.4 }} />
                          </div>
                        </div>
                        {timeOffDateError && <div style={{ fontSize: 12, color: "#ef4444", marginTop: 6, marginBottom: 2 }}>{timeOffDateError}</div>}
                      </>
                    );
                  })()}
                  <div style={{ height: 10 }} />
                  <input placeholder="Reason" value={timeOffRequest.reason} onChange={e => { setTimeOffRequest(p => ({ ...p, reason: e.target.value })); setTimeOffReasonError(false); }} style={{ ...inputStyle, borderColor: timeOffReasonError ? "#ef4444" : undefined }} />
                  {timeOffReasonError && <div style={{ fontSize: 12, color: "#ef4444", marginTop: -8, marginBottom: 8 }}>Please add a reason.</div>}
                  {confirmTimeOff ? (
                    <div style={{ background: "#0f0f0f", border: "1px solid #2a2a2a", borderRadius: 10, padding: 14 }}>
                      <div style={{ fontSize: 13, color: "#fff", marginBottom: 12, textAlign: "center" }}>Submit time off request?</div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button onClick={() => { setConfirmTimeOff(false); submitTimeOff(); }} style={{ flex: 1, background: "#22c55e", border: "none", borderRadius: 8, padding: "11px", color: "#000", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Yes</button>
                        <button onClick={() => setConfirmTimeOff(false)} style={{ flex: 1, background: "#ef4444", border: "none", borderRadius: 8, padding: "11px", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>No</button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => { if (timeOffSubmitting) return; if (!timeOffRequest.reason.trim()) { setTimeOffReasonError(true); return; } setConfirmTimeOff(true); }} disabled={timeOffSubmitting} style={{ flex: 1, background: timeOffSubmitting ? "#2a2a2a" : "#f97316", border: "none", borderRadius: 8, padding: "11px", color: timeOffSubmitting ? "#aaaaaa" : "#000", fontWeight: 700, fontSize: 13, cursor: timeOffSubmitting ? "default" : "pointer" }}>{timeOffSubmitting ? "Submitting…" : "Submit"}</button>
                      <button onClick={() => { setShowTimeOff(false); setTimeOffReasonError(false); setConfirmTimeOff(false); }} style={{ flex: 1, background: "#1a1a1a", border: "none", borderRadius: 8, padding: "11px", color: "#fff", fontSize: 13, cursor: "pointer" }}>Cancel</button>
                    </div>
                  )}
                </div>
              )}

              {openCoversFiltered.length > 0 && (
                <>
                  <div style={{ fontSize: 11, color: "#f97316", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10, fontWeight: 700 }}>Open Covers</div>
                  {openCoversFiltered.map(r => (
                    <div key={r.id} style={{ ...shiftCard, flexDirection: "column", alignItems: "flex-start", gap: 12, borderColor: "#2a1a0a" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                        <div>
                          <div style={{ fontWeight: 600 }}>{formatName(r.requester_name, null, user.role)} needs cover</div>
                          <div style={{ color: "#cccccc", fontSize: 13, marginTop: 4 }}>
                            {fmtDate(r.shift_date)} · {fmt(r.shift_start)}–{fmt(r.shift_end)} · {r.shift_location || defaultLoc}
                          </div>
                          {r.reason && <div style={{ color: "#cccccc", fontSize: 12, marginTop: 4 }}>Reason: {r.reason}</div>}
                        </div>
                      </div>
                      <button
                        onClick={() => setConfirmDialog({ message: `Cover ${formatName(r.requester_name, null, user.role)}'s shift on ${fmtDate(r.shift_date)}?`, confirmColor: "#22c55e", onConfirm: () => claimCover(r) })}
                        disabled={processingId === r.id}
                        style={{ width: "100%", background: processingId === r.id ? "#1a1a1a" : "#22c55e", border: "none", borderRadius: 7, padding: "9px", color: processingId === r.id ? "#f97316" : "#000", fontWeight: 700, fontSize: 13, cursor: processingId === r.id ? "default" : "pointer", fontStyle: processingId === r.id ? "italic" : "normal" }}
                      >{processingId === r.id ? "Processing…" : "I'll Cover It"}</button>
                    </div>
                  ))}
                </>
              )}

              {openCoversFiltered.length === 0 && pending.length === 0 && completed.length === 0 && (
                <div style={{ color: "#ffffff", textAlign: "center", padding: 40 }}>No requests yet.</div>
              )}

              <div style={{ fontSize: 11, color: "#fff", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10, marginTop: openCoversFiltered.length > 0 ? 24 : 0 }}>Pending</div>
              {pending.length === 0
                ? <div style={{ color: "#ffffff", fontSize: 14, marginBottom: 20 }}>No pending requests.</div>
                : pending.map(r => {
                  const isIncoming = r.type === "swap" && r.target_id === user.id && r.status === "pending_target";
                  const isMyCover = r.type === "cover" && r.requester_id === user.id;
                  const isConfirmingCancel = cancelConfirmId === r.id;
                  const canCancel = !isIncoming;
                  return (
                    <div key={r.id} style={{ ...shiftCard, flexDirection: "column", alignItems: "flex-start", gap: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                        <div>
                          {isMyCover
                            ? <><div style={{ fontWeight: 600 }}>📋 Cover Needed</div>
                                <div style={{ color: "#cccccc", fontSize: 13, marginTop: 4 }}>{fmtDate(r.shift_date)} · {fmt(r.shift_start)}–{fmt(r.shift_end)} · {r.shift_location || defaultLoc}</div></>
                            : isIncoming
                            ? (() => {
                                const myGiveUp = shifts.find(s => s.id === r.cover_shift_id);
                                return <>
                                  <div style={{ fontWeight: 600 }}>{formatName(r.requester_name, null, user.role)} wants to swap</div>
                                  <div style={{ color: "#cccccc", fontSize: 13, marginTop: 4 }}>You get: {fmtDate(r.shift_date)} · {fmt(r.shift_start)}–{fmt(r.shift_end)} · {r.shift_location || defaultLoc}</div>
                                  {myGiveUp && <div style={{ color: "#cccccc", fontSize: 13, marginTop: 2 }}>You give up: {fmtDate(myGiveUp.date)} · {fmt(myGiveUp.start_time)}–{fmt(myGiveUp.end_time)} · {myGiveUp.location || defaultLoc}</div>}
                                </>;
                              })()
                            : <><div style={{ fontWeight: 600 }}>{r.type === "swap" ? "⇄ Shift Swap" : "📅 Time Off"}</div>
                                {r.type === "swap" && (() => {
                                  const theirShift = shifts.find(s => s.id === r.cover_shift_id);
                                  return <>
                                    <div style={{ color: "#cccccc", fontSize: 13, marginTop: 4 }}>With {formatName(r.target_name, null, user.role)}</div>
                                    <div style={{ color: "#cccccc", fontSize: 13, marginTop: 2 }}>You give up: {fmtDate(r.shift_date)} · {fmt(r.shift_start)}–{fmt(r.shift_end)} · {r.shift_location || defaultLoc}</div>
                                    {theirShift && <div style={{ color: "#cccccc", fontSize: 13, marginTop: 2 }}>You get: {fmtDate(theirShift.date)} · {fmt(theirShift.start_time)}–{fmt(theirShift.end_time)} · {theirShift.location || defaultLoc}</div>}
                                  </>;
                                })()}
                                {r.type === "timeoff" && <div style={{ color: "#cccccc", fontSize: 13, marginTop: 4 }}>{fmtDateRange(r.date, r.end_date)}</div>}</>
                          }
                          {r.reason && <div style={{ color: "#cccccc", fontSize: 12, marginTop: 4 }}>Reason: {r.reason}</div>}
                        </div>
                        {!isIncoming && <div style={{ fontSize: 11, color: statusColor(r.status), fontWeight: 600, textTransform: "uppercase", textAlign: "right", maxWidth: 100 }}>{myStatusText(r)}</div>}
                      </div>
                      {isIncoming && (
                        processingId === r.id
                          ? <div style={{ fontSize: 13, color: "#f97316", textAlign: "center", padding: "9px 0", fontStyle: "italic" }}>Processing…</div>
                          : <div style={{ display: "flex", gap: 8, width: "100%" }}>
                              <button onClick={() => setConfirmDialog({ message: "Are you sure you want to accept this swap request?", confirmColor: "#22c55e", onConfirm: () => handleTargetResponse(r.id, true) })} style={{ flex: 1, background: "#22c55e", border: "none", borderRadius: 7, padding: "9px", color: "#000", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Accept</button>
                              <button onClick={() => setConfirmDialog({ message: "Are you sure you want to decline this swap request?", onConfirm: () => handleTargetResponse(r.id, false) })} style={{ flex: 1, background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: 7, padding: "9px", color: "#ef4444", fontSize: 13, cursor: "pointer" }}>Decline</button>
                            </div>
                      )}
                      {canCancel && (
                        isConfirmingCancel ? (
                          <div style={{ width: "100%", background: "#0f0f0f", border: "1px solid #2a2a2a", borderRadius: 8, padding: "10px 12px" }}>
                            <div style={{ fontSize: 12, color: "#aaa", marginBottom: 8, textAlign: "center" }}>Cancel this request?</div>
                            <div style={{ display: "flex", gap: 8 }}>
                              <button onClick={() => cancelRequest(r)} style={{ flex: 1, background: "#22c55e", border: "none", borderRadius: 7, padding: "8px", color: "#000", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>Yes</button>
                              <button onClick={() => setCancelConfirmId(null)} style={{ flex: 1, background: "#ef4444", border: "none", borderRadius: 7, padding: "8px", color: "#fff", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>No</button>
                            </div>
                          </div>
                        ) : (
                          <div style={{ width: "100%", display: "flex", justifyContent: "flex-end" }}>
                            <button onClick={() => setCancelConfirmId(r.id)} style={{ background: "none", border: "none", color: "#ef4444", fontSize: 11, cursor: "pointer", padding: "0 2px" }}>Cancel Request</button>
                          </div>
                        )
                      )}
                    </div>
                  );
                })
              }

              {completed.length > 0 && (
                <>
                  <div style={{ fontSize: 11, color: "#fff", textTransform: "uppercase", letterSpacing: "0.1em", marginTop: 24, marginBottom: 10 }}>Completed</div>
                  {completed.map(r => (
                    <div key={r.id} style={shiftCard}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600 }}>{r.type === "cover" ? "📋 Cover Request" : r.type === "swap" ? "⇄ Shift Swap" : "📅 Time Off"}</div>
                        {r.type === "cover" && <div style={{ color: "#cccccc", fontSize: 13, marginTop: 4 }}>{fmtDate(r.shift_date)} · {fmt(r.shift_start)}–{fmt(r.shift_end)}{r.target_name ? ` · Covered by ${formatName(r.target_name, null, "employee")}` : ""}</div>}
                        {r.type === "swap" && (() => {
                          const isMe = r.requester_id === user.id;
                          const other = isMe ? r.target_name : r.requester_name;
                          const theirShift = shifts.find(s => s.id === r.cover_shift_id);
                          return <>
                            <div style={{ color: "#cccccc", fontSize: 13, marginTop: 4 }}>With {formatName(other, null, user.role)}</div>
                            <div style={{ color: "#cccccc", fontSize: 13, marginTop: 2 }}>{isMe ? "Gave up" : "Got"}: {fmtDate(r.shift_date)} · {fmt(r.shift_start)}–{fmt(r.shift_end)}</div>
                            {theirShift && <div style={{ color: "#cccccc", fontSize: 13, marginTop: 2 }}>{isMe ? "Got" : "Gave up"}: {fmtDate(theirShift.date)} · {fmt(theirShift.start_time)}–{fmt(theirShift.end_time)}</div>}
                          </>;
                        })()}
                        {r.type === "timeoff" && <div style={{ color: "#cccccc", fontSize: 13, marginTop: 4 }}>{fmtDateRange(r.date, r.end_date)}</div>}
                        {r.reason && <div style={{ color: "#cccccc", fontSize: 12, marginTop: 4 }}>Reason: {r.reason}</div>}
                      </div>
                      <div style={{ fontSize: 11, color: r._notFulfilled ? "#ef4444" : r._swapExpired ? "#ffffff" : (r._cancelled || (r.status === "revoked" && r.requester_id === user.id)) ? "#ef4444" : statusColor(r.status), fontWeight: 600, textTransform: "uppercase", textAlign: "right", maxWidth: 100 }}>{myStatusText(r)}</div>
                    </div>
                  ))}
                </>
              )}
            </div>
          );
        })()}

      </div>


      {/* ── Employee action sheets ── */}
      {user.role !== "manager" && empSheet.type && ReactDOM.createPortal(
        <div
          onMouseDown={(e) => { if (e.target === e.currentTarget) closeEmpSheet(); }}
          onTouchStart={(e) => { if (e.target === e.currentTarget) closeEmpSheet(); }}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 600 }}
        >
          <div
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            style={{
              position: "fixed", bottom: 0, left: 0, right: 0,
              background: "white",
              borderRadius: "20px 20px 0 0",
              maxHeight: "60vh", display: "flex", flexDirection: "column",
              transform: empSheet.visible ? "translateY(0)" : "translateY(100%)",
              transition: "transform 300ms ease", zIndex: 601,
            }}
          >
            {/* Drag handle */}
            <div style={{ width: 36, height: 4, background: "#E0E0E0", borderRadius: 2, margin: "10px auto 0", flexShrink: 0 }} />

            {/* Sheet header */}
            {empSheet.type !== "availability" && (
              <div style={{ padding: "10px 20px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #EFEFEF", flexShrink: 0 }}>
                <span style={{ fontSize: 17, fontWeight: 700, color: "#F55C00", fontFamily: empSheet.type === "requests" ? "'Oswald', sans-serif" : "inherit" }}>
                  {empSheet.type === "timeoff" ? "Time Off" : empSheet.type === "cover" ? "Need Coverage" : empSheet.type === "requests" ? "Requests" : "Late Arrival"}
                </span>
                <button type="button" onClick={closeEmpSheet} style={{ background: "none", border: "none", fontSize: 15, color: "#888", cursor: "pointer", fontFamily: "inherit", fontWeight: 500 }}>Cancel</button>
              </div>
            )}
            {empSheet.type === "availability" && (
              <div style={{ padding: "14px 20px 12px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", borderBottom: "1px solid #EFEFEF", flexShrink: 0 }}>
                <div>
                  <div style={{ fontSize: 17, fontWeight: 700, color: "#F55C00" }}>Availability</div>
                  <div style={{ fontSize: 12, color: "#555555", marginTop: 2 }}>Let us know when you're free to work</div>
                </div>
                <button type="button" onClick={closeEmpSheet} style={{ background: "none", border: "none", fontSize: 15, color: "#888", cursor: "pointer", fontFamily: "inherit", fontWeight: 500, paddingTop: 2 }}>Cancel</button>
              </div>
            )}

            {/* Sheet body */}
            <div style={{ overflowY: "auto", flex: 1, padding: "20px 20px 48px", WebkitOverflowScrolling: "touch" }}>

              {/* ── AVAILABILITY ── */}
              {empSheet.type === "availability" && (() => {
                const DAY_ORDER = [0,1,2,3,4,5,6]; // MON=0...SUN=6
                const DAY_LABELS = ["MON","TUE","WED","THU","FRI","SAT","SUN"];
                const TIME_OPTIONS = (() => {
                  const opts = [];
                  for (let h = 0; h < 24; h++) {
                    for (let m = 0; m < 60; m += 15) {
                      const hh = String(h).padStart(2,"0");
                      const mm = String(m).padStart(2,"0");
                      const val = `${hh}:${mm}:00`;
                      const ampm = h < 12 ? "AM" : "PM";
                      const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
                      const label = `${displayH}:${mm} ${ampm}`;
                      opts.push({ val, label });
                    }
                  }
                  return opts;
                })();
                return (
                  <div>
                    {DAY_ORDER.map((dow) => {
                      const row = empAvailRows.find(r => r.day_of_week === dow) || { day_of_week: dow, is_available: false, start_time: "09:00:00", end_time: "22:00:00" };
                      const on = row.is_available;
                      return (
                        <div key={dow} style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: 14, marginBottom: 14, borderBottom: "0.5px solid #EFEFEF" }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: "#111111", width: 32, flexShrink: 0, letterSpacing: 0.5 }}>{DAY_LABELS[dow]}</span>
                          {/* Toggle */}
                          <div
                            onClick={() => setEmpAvailRows(prev => prev.map(r => r.day_of_week === dow ? { ...r, is_available: !r.is_available } : r))}
                            style={{ width: 40, height: 24, borderRadius: 12, background: on ? "#F55C00" : "#e0e0e0", position: "relative", cursor: "pointer", transition: "background 0.2s", flexShrink: 0 }}
                          >
                            <div style={{ position: "absolute", top: 2, left: on ? 18 : 2, width: 20, height: 20, borderRadius: "50%", background: "white", boxShadow: "0 1px 3px rgba(0,0,0,0.4)", transition: "left 0.2s" }} />
                          </div>
                          {/* Time selects */}
                          <select
                            disabled={!on}
                            value={row.start_time}
                            onChange={e => setEmpAvailRows(prev => prev.map(r => r.day_of_week === dow ? { ...r, start_time: e.target.value } : r))}
                            style={{ width: 120, flexShrink: 0, background: "#f5f5f5", border: "0.5px solid #ddd", color: "#111111", borderRadius: 8, padding: "6px 10px", fontSize: 13, fontFamily: "inherit", outline: "none", appearance: "none", cursor: on ? "pointer" : "default", opacity: on ? 1 : 0.3 }}
                          >
                            {TIME_OPTIONS.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
                          </select>
                          <span style={{ color: "#888", fontSize: 11, flexShrink: 0 }}>–</span>
                          <select
                            disabled={!on}
                            value={row.end_time}
                            onChange={e => setEmpAvailRows(prev => prev.map(r => r.day_of_week === dow ? { ...r, end_time: e.target.value } : r))}
                            style={{ width: 120, flexShrink: 0, background: "#f5f5f5", border: "0.5px solid #ddd", color: "#111111", borderRadius: 8, padding: "6px 10px", fontSize: 13, fontFamily: "inherit", outline: "none", appearance: "none", cursor: on ? "pointer" : "default", opacity: on ? 1 : 0.3 }}
                          >
                            {TIME_OPTIONS.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
                          </select>
                        </div>
                      );
                    })}
                    <button
                      type="button"
                      onClick={submitEmpAvailability}
                      disabled={empAvailSaving}
                      style={{ width: "100%", background: "#F55C00", color: "white", border: "none", borderRadius: 14, padding: "15px", fontSize: 14, fontWeight: 700, cursor: empAvailSaving ? "default" : "pointer", fontFamily: "inherit", marginTop: 8, opacity: empAvailSaving ? 0.7 : 1 }}
                    >
                      {empAvailSaving ? "Saving…" : "SAVE AVAILABILITY"}
                    </button>
                  </div>
                );
              })()}

              {/* ── TIME OFF ── */}
              {empSheet.type === "timeoff" && (() => {
                const existingTO = requests.filter(r =>
                  r.type === "timeoff" && (r.status === "pending" || r.status === "approved") && r.requester_id === user.id
                );
                const conflicts = date => existingTO.some(r => date >= r.date && date <= (r.end_date || r.date));
                const endMin = (() => {
                  if (!empTimeOff.startDate) return "";
                  const d = new Date(empTimeOff.startDate + "T00:00:00");
                  d.setDate(d.getDate() + 1);
                  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
                })();
                return (
                  <>
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Start Date</div>
                      <input
                        type="date"
                        value={empTimeOff.startDate}
                        min={getToday()}
                        onChange={e => {
                          const val = e.target.value;
                          if (val && conflicts(val)) {
                            setEmpTimeOff(s => ({ ...s, startDate: val, endDate: "", error: "This date overlaps with an existing time off request." }));
                          } else {
                            setEmpTimeOff(s => ({ ...s, startDate: val, endDate: "", error: "" }));
                          }
                        }}
                        style={{ width: "100%", border: "1px solid #E0E0E0", borderRadius: 10, padding: "11px 12px", fontSize: 15, fontFamily: "inherit", outline: "none", boxSizing: "border-box", color: "#111" }}
                      />
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>End Date <span style={{ fontWeight: 400, color: "#aaa", textTransform: "none", letterSpacing: 0 }}>(optional)</span></div>
                      <input
                        type="date"
                        value={empTimeOff.endDate}
                        min={endMin}
                        disabled={!empTimeOff.startDate}
                        onChange={e => {
                          const val = e.target.value;
                          if (val && conflicts(val)) {
                            setEmpTimeOff(s => ({ ...s, error: "This date overlaps with an existing time off request." }));
                          } else {
                            setEmpTimeOff(s => ({ ...s, endDate: val, error: "" }));
                          }
                        }}
                        style={{ width: "100%", border: "1px solid #E0E0E0", borderRadius: 10, padding: "11px 12px", fontSize: 15, fontFamily: "inherit", outline: "none", boxSizing: "border-box", color: "#111", opacity: empTimeOff.startDate ? 1 : 0.5 }}
                      />
                      <div style={{ fontSize: 12, color: "#555", marginTop: 4 }}>Leave blank for a single day request</div>
                    </div>
                    <div style={{ marginBottom: 20 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Reason <span style={{ color: "#E53935" }}>*</span></div>
                      <textarea
                        value={empTimeOff.reason}
                        onChange={e => setEmpTimeOff(s => ({ ...s, reason: e.target.value, error: s.error === "Start date and reason are required." ? "" : s.error }))}
                        placeholder="Why are you requesting time off?"
                        rows={3}
                        style={{ width: "100%", border: "1px solid #E0E0E0", borderRadius: 10, padding: "11px 12px", fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box", resize: "none", color: "#111" }}
                      />
                    </div>
                    {empTimeOff.error && <div style={{ color: "#E53935", fontSize: 13, marginBottom: 12, fontWeight: 500 }}>{empTimeOff.error}</div>}
                    <button
                      type="button"
                      onClick={submitEmpTimeOff}
                      disabled={empTimeOff.submitting}
                      style={{ width: "100%", background: empTimeOff.submitting ? "#888" : "#F55C00", color: "#ffffff", border: "none", borderRadius: 14, padding: "16px", fontSize: 15, fontWeight: 700, cursor: empTimeOff.submitting ? "default" : "pointer", fontFamily: "inherit" }}
                    >{empTimeOff.submitting ? "Submitting…" : "Submit Request"}</button>
                  </>
                );
              })()}

              {/* ── COVER REQUEST ── */}
              {empSheet.type === "cover" && (() => {
                const now = new Date();
                const todayStr = getToday();
                const currentHHMM = `${String(now.getHours()).padStart(2,"0")}:${String(now.getMinutes()).padStart(2,"0")}`;

                const myUpcoming = shifts
                  .filter(s => {
                    if (s.employee_id !== user.id) return false;
                    if (s.date < todayStr) return false;
                    if (s.date === todayStr && s.start_time <= currentHHMM) return false;
                    return true;
                  })
                  .sort((a, b) => a.date.localeCompare(b.date) || a.start_time.localeCompare(b.start_time));
                const selectedShift = myUpcoming.find(s => s.id === empCover.shiftId) || null;
                return (
                  <>
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Select a shift</div>
                      {myUpcoming.length === 0 ? (
                        <div style={{ fontSize: 13, color: "#aaa", padding: "16px 0" }}>No upcoming shifts found.</div>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                          {myUpcoming.map(s => {
                            const isSelected = s.id === empCover.shiftId;
                            const hasPendingCover =
                              requests.some(r => r.type === "cover" && r.requester_id === user.id && r.cover_shift_id === s.id && r.status !== "declined" && r.status !== "revoked" && r.status !== "denied" && !r._cancelled) ||
                              openCovers.some(r => r.requester_id === user.id && r.cover_shift_id === s.id && r.status === "pending");
                            return (
                              <button
                                key={s.id}
                                type="button"
                                onClick={hasPendingCover ? () => {} : () => setEmpCover(prev => ({ ...prev, shiftId: s.id, error: "" }))}
                                style={{ textAlign: "left", padding: "12px 14px", border: isSelected ? "2px solid #111" : "1px solid #E0E0E0", borderRadius: 12, background: isSelected ? "#f9f9f9" : "#fff", cursor: hasPendingCover ? "default" : "pointer", fontFamily: "inherit", WebkitTapHighlightColor: "transparent", opacity: hasPendingCover ? 0.5 : 1 }}
                              >
                                <div style={{ fontSize: 14, fontWeight: 700, color: "#111" }}>{fmtDate(s.date)}</div>
                                <div style={{ fontSize: 13, color: "#555", marginTop: 3 }}>{fmt(s.start_time)} – {fmt(s.end_time)} · {s.location || defaultLoc}</div>
                                {hasPendingCover && <div style={{ fontSize: 10, fontWeight: 700, color: "#F55C00", fontFamily: "'Oswald', sans-serif", textTransform: "uppercase", marginTop: 4 }}>Cover Requested</div>}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <div style={{ marginBottom: 20 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Reason <span style={{ color: "#F55C00" }}>*</span></div>
                      <textarea
                        value={empCover.reason}
                        onChange={e => setEmpCover(s => ({ ...s, reason: e.target.value }))}
                        placeholder="Let your manager know why you need coverage"
                        rows={2}
                        style={{ width: "100%", border: "1px solid #E0E0E0", borderRadius: 10, padding: "11px 12px", fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box", resize: "none", color: "#111" }}
                      />
                    </div>
                    {empCover.error && <div style={{ color: "#E53935", fontSize: 13, marginBottom: 12, fontWeight: 500 }}>{empCover.error}</div>}
                    <button
                      type="button"
                      onClick={submitEmpCover}
                      disabled={empCover.submitting || !empCover.shiftId}
                      style={{ width: "100%", background: empCover.submitting ? "#888" : "#F55C00", color: "#ffffff", border: "none", borderRadius: 14, padding: "16px", fontSize: 15, fontWeight: 700, cursor: (!empCover.shiftId || empCover.submitting) ? "default" : "pointer", fontFamily: "inherit" }}
                    >{empCover.submitting ? "Submitting…" : "Submit Request"}</button>
                  </>
                );
              })()}

              {/* ── LATE ARRIVAL ── */}
              {/* ── REQUESTS ── */}
              {empSheet.type === "requests" && (() => {
                const today = getToday();
                const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
                const reqTypeLabel = r => {
                  if (r.type === "timeoff") return "Time Off";
                  if (r.type === "cover") return "Cover Request";
                  if (r.type === "late_arrival") return "Late Arrival";
                  return r.type;
                };
                const fmt = (t) => { if (!t) return ""; const [h, m] = t.split(":"); const hr = parseInt(h); return `${hr % 12 || 12}:${m} ${hr < 12 ? "AM" : "PM"}`; };
                const fmtShortDate = (d) => { if (!d) return ""; const dt = new Date(d + "T00:00:00"); return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" }); };
                const fmtDateRange = (start, end) => { if (!start) return ""; if (!end || end === start) return fmtShortDate(start); return `${fmtShortDate(start)} – ${fmtShortDate(end)}`; };
                const reqDateLabel = r => {
                  if (r.type === "timeoff") return fmtDateRange(r.date, r.end_date);
                  if (r.type === "cover") return r.shift_date ? fmtShortDate(r.shift_date) : "";
                  if (r.type === "late_arrival") return r.shift_date ? `Today · ETA ${fmt(r.shift_start)}` : "Today";
                  return "";
                };
                const myBase = requests.filter(r => r.requester_id === user.id && r.type !== "swap");
                const pendingStatuses = new Set(["pending", "pending_manager", "pending_target"]);
                const pendingFromRequests = myBase.filter(r => {
                  if (!pendingStatuses.has(r.status)) return false;
                  if (r.type === "timeoff") return (r.end_date || r.date) >= today;
                  if (r.type === "cover") return r.shift_date >= today;
                  return true;
                });
                const pendingFromCovers = openCovers.filter(r => r.requester_id === user.id && r.status === "pending" && r.shift_date >= today);
                const seenIds = new Set(pendingFromRequests.map(r => r.id));
                const pendingAll = [...pendingFromRequests, ...pendingFromCovers.filter(r => !seenIds.has(r.id))];
                const resolvedStatuses = new Set(["approved", "claimed", "denied", "declined"]);
                const resolvedAll = myBase
                  .filter(r => resolvedStatuses.has(r.status) && r.updated_at >= sevenDaysAgo)
                  .sort((a, b) => {
                    const rank = s => (s === "approved" || s === "claimed") ? 0 : 1;
                    return rank(a.status) - rank(b.status) || new Date(b.updated_at) - new Date(a.updated_at);
                  });
                const sectionLabel = (text, mt) => (
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, marginTop: mt || 0 }}>{text}</div>
                );
                const badge = (label, bg, color) => (
                  <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 700, color, background: bg, borderRadius: 20, padding: "3px 10px", whiteSpace: "nowrap" }}>{label}</span>
                );
                const renderRow = (r, idx, arr, badgeEl) => (
                  <div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: idx < arr.length - 1 ? "0.5px solid #EFEFEF" : "none" }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "#111", fontFamily: "'Oswald', sans-serif" }}>{reqTypeLabel(r)}</div>
                      {reqDateLabel(r) ? <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>{reqDateLabel(r)}</div> : null}
                    </div>
                    {badgeEl}
                  </div>
                );
                return (
                  <div>
                    {sectionLabel("Pending")}
                    {pendingAll.length === 0
                      ? <div style={{ fontSize: 13, color: "#888", paddingBottom: 4 }}>Nothing pending</div>
                      : pendingAll.map((r, i, arr) => renderRow(r, i, arr,
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                          {badge("Pending", "#F5F5F5", "#888")}
                          <button
                            type="button"
                            onClick={() => setCancelConfirmRequest(r)}
                            style={{
                              background: "none",
                              border: "none",
                              color: "#E53935",
                              fontSize: 11,
                              fontWeight: 700,
                              cursor: "pointer",
                              fontFamily: "'Oswald', sans-serif",
                              textTransform: "uppercase",
                              padding: 0
                            }}
                          >Cancel</button>
                        </div>
                      ))
                    }
                    {sectionLabel("Recently Resolved", 20)}
                    {resolvedAll.length === 0
                      ? <div style={{ fontSize: 13, color: "#888" }}>Nothing in the last 7 days</div>
                      : resolvedAll.map((r, i, arr) => {
                          const approved = r.status === "approved" || r.status === "claimed";
                          return renderRow(r, i, arr, approved
                            ? badge("Approved ✓", "#E8F5E9", "#2E7D32")
                            : badge("Denied", "#FFEBEE", "#E53935"));
                        })
                    }
                  </div>
                );
              })()}

              {empSheet.type === "late" && (
                <>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Estimated arrival time <span style={{ color: "#E53935" }}>*</span></div>
                    <input
                      type="time"
                      value={empLate.arrivalTime}
                      onChange={e => setEmpLate(s => ({ ...s, arrivalTime: e.target.value, error: "" }))}
                      style={{ width: "100%", border: "1px solid #E0E0E0", borderRadius: 10, padding: "11px 12px", fontSize: 15, fontFamily: "inherit", outline: "none", boxSizing: "border-box", color: "#111" }}
                    />
                  </div>
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Note <span style={{ fontWeight: 400, color: "#aaa", textTransform: "none", letterSpacing: 0 }}>(optional)</span></div>
                    <textarea
                      value={empLate.note}
                      onChange={e => setEmpLate(s => ({ ...s, note: e.target.value }))}
                      placeholder="Any additional context for your manager?"
                      rows={2}
                      style={{ width: "100%", border: "1px solid #E0E0E0", borderRadius: 10, padding: "11px 12px", fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box", resize: "none", color: "#111" }}
                    />
                  </div>
                  {empLate.error && <div style={{ color: "#E53935", fontSize: 13, marginBottom: 12, fontWeight: 500 }}>{empLate.error}</div>}
                  <button
                    type="button"
                    onClick={submitEmpLate}
                    disabled={empLate.submitting}
                    style={{ width: "100%", background: empLate.submitting ? "#888" : "#111", color: "#F55C00", border: "none", borderRadius: 14, padding: "16px", fontSize: 15, fontWeight: 700, cursor: empLate.submitting ? "default" : "pointer", fontFamily: "inherit" }}
                  >{empLate.submitting ? "Submitting…" : "Notify Manager"}</button>
                </>
              )}

            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Employee hamburger drawer */}
      {user.role !== "manager" && empDrawerOpen && ReactDOM.createPortal(
        <>
          <div
            onClick={() => { setEmpDrawerVisible(false); setTimeout(() => setEmpDrawerOpen(false), 280); }}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 499, opacity: empDrawerVisible ? 1 : 0, transition: "opacity 0.28s cubic-bezier(0.4,0,0.2,1)" }}
          />
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ position: "fixed", top: 0, right: 0, height: "100%", width: "75%", background: "white", zIndex: 500, transform: empDrawerVisible ? "translateX(0)" : "translateX(100%)", transition: "transform 0.28s cubic-bezier(0.4,0,0.2,1)", display: "flex", flexDirection: "column", boxShadow: "-4px 0 24px rgba(0,0,0,0.12)" }}
          >
            <div style={{ padding: "60px 24px 24px", borderBottom: "1px solid #EFEFEF" }}>
              <div style={{ fontWeight: 700, fontSize: 18, color: "#111" }}>
                {user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.name || ""}
              </div>
              <div style={{ fontSize: 13, color: "#F55C00", marginTop: 4, fontWeight: 500 }}>
                {user.role === "manager" ? "Manager" : "Employee"}
              </div>
            </div>
            <div style={{ flex: 1, overflowY: "auto" }}>
              {/* Phone number */}
              <div style={{ padding: "16px 24px", borderBottom: "1px solid #EFEFEF" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#aaa", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Phone</div>
                {!editPhoneState.editing ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 15, fontWeight: 500, color: user.phone ? "#111" : "#bbb", fontFamily: "inherit" }}>
                      {user.phone && user.phone.startsWith("+1") && user.phone.length === 12
                        ? `(${user.phone.slice(2, 5)}) ${user.phone.slice(5, 8)}-${user.phone.slice(8)}`
                        : user.phone || "No number"}
                    </span>
                    <button type="button" onClick={() => setEditPhoneState({ editing: true, value: user.phone ? user.phone.replace(/^\+1/, "") : "", error: "", saving: false })}
                      style={{ background: "none", border: "none", color: "#F55C00", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", padding: 0 }}>
                      {user.phone ? "Edit" : "Add"}
                    </button>
                  </div>
                ) : (
                  <div>
                    <input
                      autoFocus
                      type="tel"
                      inputMode="numeric"
                      placeholder="(XXX) XXX-XXXX"
                      value={formatPhoneDisplay(editPhoneState.value)}
                      onChange={e => { const d = phoneDigitsOnly(e.target.value); setEditPhoneState(p => ({ ...p, value: d, error: "" })); }}
                      onKeyDown={e => {
                        if (e.key === "Escape") setEditPhoneState({ editing: false, value: "", error: "", saving: false });
                        if (e.key === "Enter") {
                          const digits = editPhoneState.value.replace(/\D/g, "");
                          if (digits.length !== 10) { setEditPhoneState(p => ({ ...p, error: "Enter a valid 10-digit phone number" })); return; }
                          setEditPhoneState(p => ({ ...p, saving: true }));
                          const e164 = `+1${digits}`;
                          supabase.from("profiles").update({ phone: e164 }).eq("id", user.id).then(({ error: upErr }) => {
                            if (upErr) { setEditPhoneState(p => ({ ...p, saving: false, error: "Failed to save" })); return; }
                            setUser(prev => ({ ...prev, phone: e164 }));
                            setEditPhoneState({ editing: false, value: "", error: "", saving: false });
                          });
                        }
                      }}
                      style={{ width: "100%", background: "#f9f9f9", border: `1px solid ${editPhoneState.error ? "#E53935" : "#E0E0E0"}`, borderRadius: 8, padding: "10px 12px", fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box", color: "#111", marginBottom: editPhoneState.error ? 4 : 8 }}
                    />
                    {editPhoneState.error && <div style={{ color: "#E53935", fontSize: 12, marginBottom: 8 }}>{editPhoneState.error}</div>}
                    <div style={{ display: "flex", gap: 8 }}>
                      <button type="button" onClick={() => setEditPhoneState({ editing: false, value: "", error: "", saving: false })}
                        style={{ flex: 1, background: "#F5F5F5", border: "none", borderRadius: 8, padding: 8, color: "#111", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
                      <button type="button" disabled={editPhoneState.saving} onClick={() => {
                        const digits = editPhoneState.value.replace(/\D/g, "");
                        if (digits.length !== 10) { setEditPhoneState(p => ({ ...p, error: "Enter a valid 10-digit phone number" })); return; }
                        setEditPhoneState(p => ({ ...p, saving: true }));
                        const e164 = `+1${digits}`;
                        supabase.from("profiles").update({ phone: e164 }).eq("id", user.id).then(({ error: upErr }) => {
                          if (upErr) { setEditPhoneState(p => ({ ...p, saving: false, error: "Failed to save" })); return; }
                          setUser(prev => ({ ...prev, phone: e164 }));
                          setEditPhoneState({ editing: false, value: "", error: "", saving: false });
                        });
                      }}
                        style={{ flex: 1, background: "#F55C00", border: "none", borderRadius: 8, padding: 8, color: "#fff", fontWeight: 600, fontSize: 13, cursor: editPhoneState.saving ? "default" : "pointer", opacity: editPhoneState.saving ? 0.7 : 1, fontFamily: "inherit" }}>
                        {editPhoneState.saving ? "Saving…" : "Save"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              {["Notifications", "Settings", "Help"].map(label => (
                <div key={label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 24px", borderBottom: "1px solid #EFEFEF" }}>
                  <span style={{ fontSize: 15, fontWeight: 500, color: "#ccc", fontFamily: "inherit" }}>{label}</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: "#ccc", letterSpacing: "0.05em" }}>SOON</span>
                </div>
              ))}
            </div>
            <div style={{ padding: "24px", borderTop: "1px solid #EFEFEF" }}>
              <button
                onClick={logout}
                style={{ background: "none", border: "none", color: "#E53935", fontSize: 15, fontWeight: 600, cursor: "pointer", padding: 0, fontFamily: "inherit", WebkitTapHighlightColor: "transparent" }}
              >Log out</button>
            </div>
          </div>
        </>,
        document.body
      )}

      {cancelConfirmRequest && ReactDOM.createPortal(
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 600, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 24, width: 300 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#111", fontFamily: "'Oswald', sans-serif" }}>Cancel Request?</div>
            <div style={{ fontSize: 13, color: "#888", marginTop: 8 }}>
              Are you sure you want to cancel your {(() => {
                const r = cancelConfirmRequest;
                if (r.type === "timeoff") return "Time Off";
                if (r.type === "cover") return "Cover Request";
                return r.type;
              })()} request?
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 20 }}>
              <button
                type="button"
                onClick={() => setCancelConfirmRequest(null)}
                style={{ flex: 1, background: "#F5F5F5", border: "none", borderRadius: 10, padding: 10, color: "#111", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}
              >Keep it</button>
              <button
                type="button"
                onClick={() => { cancelRequest(cancelConfirmRequest); setCancelConfirmRequest(null); }}
                style={{ flex: 1, background: "#E53935", border: "none", borderRadius: 10, padding: 10, color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}
              >Cancel Request</button>
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
}
